import axios from 'axios';
import type {
  ILoginResponse,
  IAuthTokens,
  IUser,
  IPaginatedResult,
  IPaginationQuery,
  IDocumentTemplate,
  ICreateDocumentTemplatePayload,
  IUpdateDocumentTemplatePayload,
  NotificationUrgency,
  PurchaseInvoiceVatRate,
} from '@mirasoles/shared';
import type { UserRole } from '@mirasoles/shared';
import type {
  DishStatus,
  DailyPlanningStatus,
  PlanningPeriodStatus,
  ClientClaimStatus,
  ServiceConsumptionStatus,
  DemandProjectionStatus,
  ReconciliationPeriodStatus,
  StockCountStatus,
  StockCountLineStatus,
  BpmSurveyStatus,
  SupplierStatus,
  BpmFindingStatus,
  KitchenOrderStatus,
  KitchenOrderLineStatus,
  PurchaseConsolidationStatus,
  PurchaseOrderStatus,
  ReceptionStatus,
  PickingRouteStatus,
  KitchenReceptionStatus,
  PurchaseInvoiceStatus,
  PurchaseInvoiceLineMatchStatus,
  PaymentProposalStatus,
  PaymentOrderStatus,
  PreliquidacionStatus,
  ClientWithholdingConfigStatus,
  PettyCashBoxStatus,
  PettyCashMovementStatus,
  PettyCashCountStatus,
} from '@mirasoles/shared';
export type {
  DishStatus,
  DailyPlanningStatus,
  PlanningPeriodStatus,
  ClientClaimStatus,
  ServiceConsumptionStatus,
  DemandProjectionStatus,
  ReconciliationPeriodStatus,
  StockCountStatus,
  StockCountLineStatus,
  BpmSurveyStatus,
  SupplierStatus,
  BpmFindingStatus,
  KitchenOrderStatus,
  KitchenOrderLineStatus,
  PurchaseConsolidationStatus,
  PurchaseOrderStatus,
  ReceptionStatus,
  PickingRouteStatus,
  KitchenReceptionStatus,
  PurchaseInvoiceStatus,
  PurchaseInvoiceLineMatchStatus,
  PaymentProposalStatus,
  PaymentOrderStatus,
  PreliquidacionStatus,
  ClientWithholdingConfigStatus,
  PettyCashBoxStatus,
  PettyCashMovementStatus,
  PettyCashCountStatus,
};
export { PURCHASE_INVOICE_VAT_RATES } from '@mirasoles/shared';
export type { PurchaseInvoiceVatRate } from '@mirasoles/shared';

const http = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    // #314: un 401 del propio login es "credenciales inválidas", no una sesión vencida: no refrescar ni sacar de la URL de la empresa.
    if (error.response?.status === 401 && !original._retry && !String(original.url ?? '').endsWith('/auth/login')) {
      original._retry = true;
      const refresh = localStorage.getItem('refreshToken');
      if (refresh) {
        try {
          const { data } = await axios.post<{ data: IAuthTokens }>('/api/auth/refresh', {
            refreshToken: refresh,
          });
          localStorage.setItem('accessToken', data.data.accessToken);
          localStorage.setItem('refreshToken', data.data.refreshToken);
          original.headers.Authorization = `Bearer ${data.data.accessToken}`;
          return http(original);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  },
);

const unwrap = <T>(promise: Promise<{ data: { data: T } }>) =>
  promise.then((r) => r.data.data);

/**
 * Distingue "no hay conexión real" (la request nunca llegó al servidor —
 * `error.response` es undefined) de un error de servidor genuino (401, 500,
 * etc., que sí respondió). Confiar solo en `navigator.onLine`/el evento
 * 'offline' del browser es frágil: en algunos entornos (DevTools throttling
 * contra un servidor remoto, redes capturas de portal cautivo) el estado no
 * cambia pero la request igual falla por red — hay que detectarlo también en
 * el resultado real del request, no solo de antemano.
 */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response;
}

export function getErrorMessage(error: unknown, fallback = 'Ocurrió un error. Por favor intentá de nuevo.'): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data?.message;
    if (Array.isArray(body)) return body.join(', ');
    if (typeof body === 'string') return body;
    if (typeof body?.message === 'string') return body.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Extrae el body estructurado de un error (p. ej. `{ message, existingOrderId }` de un 409 ConflictException). */
export function getConflictPayload<T = Record<string, unknown>>(error: unknown): T | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const body = error.response?.data?.message;
  if (body && typeof body === 'object' && !Array.isArray(body)) return body as T;
  return undefined;
}

export const authApi = {
  login: (email: string, password: string, portalSlug?: string) =>
    unwrap<ILoginResponse>(http.post('/auth/login', { email, password, ...(portalSlug ? { portalSlug } : {}) })),

  refresh: (refreshToken: string) =>
    unwrap<IAuthTokens>(http.post('/auth/refresh', { refreshToken })),

  me: () => unwrap<IUser>(http.get('/auth/me')),

  forgotPassword: (email: string) =>
    unwrap<{ message: string }>(http.post('/auth/forgot-password', { email })),

  resetPassword: (token: string, newPassword: string) =>
    unwrap<{ message: string }>(http.post('/auth/reset-password', { token, newPassword })),
};

export interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  dbStatus: 'ok' | 'error';
  uptimeSeconds: number;
}

export const healthApi = {
  stats: () => unwrap<SystemStats>(http.get('/health/stats')),
};

export interface IMealsServedByService {
  serviceType: string;
  mealsServed: number;
  averageCostPerMeal: string | null;
}

export interface IMealsServedSummary {
  date: string;
  totalMealsServed: number;
  averageCostPerMeal: string | null;
  byService: IMealsServedByService[];
}

export type ManagementPaymentStatus = 'all' | 'pending' | 'paid';

/** #41: Tablero Gerencial en tiempo real. */
export interface IManagementDashboard {
  generatedAt: string;
  period: { from: string; to: string };
  costPerPortion: IMealsServedSummary;
  marginBySite: { siteId: string; siteName: string; income: number; rawMaterialCost: number; margin: number; marginPct: number | null }[];
  criticalInputs: { articleId: string; code: string; description: string; consumptionCost: number; currentUnitValue: number | null; previousUnitValue: number | null; variationPct: number | null }[];
  openAlerts: { overdueClientInvoices: { count: number; amount: number }; overdueSupplierInvoices: { count: number; amount: number } };
  financial: {
    status: ManagementPaymentStatus;
    totals: { spend: number; pending: number; paid: number };
    weeks: { weekStart: string; invoices: number; spend: number; pending: number; paid: number }[];
  };
}

export const dashboardApi = {
  getMealsServedSummary: (date?: string) =>
    unwrap<IMealsServedSummary>(http.get('/dashboard/meals-served-summary', { params: date ? { date } : {} })),
  getManagement: (params: { from?: string; to?: string; status?: ManagementPaymentStatus } = {}) =>
    unwrap<IManagementDashboard>(http.get('/dashboard/management', { params })),
};

export interface IBalancesSummary {
  receivable: number;
  payable: number;
}

type ReportRow = Record<string, string | number | null>;
export interface IReportRange {
  from: string;
  to: string;
}
export interface IOperationalReport extends IReportRange {
  leftovers: ReportRow[];
  waste: ReportRow[];
  orders: ReportRow[];
  bpm: ReportRow[];
  purchaseOrders: ReportRow[];
}
export interface IExpensesReport extends IReportRange {
  total: number;
  bySupplier: ReportRow[];
  byCategory: ReportRow[];
  byStatus: ReportRow[];
  balances: ReportRow[];
}
export interface ICostsReport extends IReportRange {
  bySite: ReportRow[];
  byCategory: ReportRow[];
}
export interface IFiscalReport extends IReportRange {
  purchaseBook: ReportRow[];
  /** El libro muestra como máximo los primeros 2000 comprobantes del período; los totales siguen siendo los de todo el período. */
  purchaseBookTruncated: boolean;
  purchaseTotals: { net: number; vat: number; total: number };
  salesSummary: ReportRow[];
  withholdings: ReportRow[];
  vouchers: ReportRow[];
}

// §26: reportes gerenciales (Administración o superior).
export const reportsApi = {
  balancesSummary: () => unwrap<IBalancesSummary>(http.get('/reports/balances-summary')),
  operational: (params: Partial<IReportRange>) => unwrap<IOperationalReport>(http.get('/reports/operational', { params })),
  expenses: (params: Partial<IReportRange>) => unwrap<IExpensesReport>(http.get('/reports/expenses', { params })),
  costs: (params: Partial<IReportRange>) => unwrap<ICostsReport>(http.get('/reports/costs', { params })),
  fiscal: (params: Partial<IReportRange>) => unwrap<IFiscalReport>(http.get('/reports/fiscal', { params })),
};

export interface INotification {
  id: string;
  userId: string;
  title: string;
  message: string;
  url: string | null;
  urgency: NotificationUrgency;
  read: boolean;
  createdAt: string;
}

export interface INotificationsResult {
  data: INotification[];
  total: number;
  unreadCount: number;
}

export interface INotificationsQuery {
  page?: number;
  limit?: number;
  read?: boolean;
  urgency?: NotificationUrgency[];
  userId?: string;
}

export const notificationsApi = {
  list: ({ urgency, ...params }: INotificationsQuery = {}) =>
    unwrap<INotificationsResult>(
      http.get('/notifications', {
        params: {
          ...params,
          ...(urgency?.length ? { urgency: urgency.join(',') } : {}),
        },
      }),
    ),

  markRead: (id: string) =>
    unwrap<INotification>(http.patch(`/notifications/${id}/read`)),

  markAllRead: () =>
    unwrap<{ updated: number }>(http.patch('/notifications/read-all')),

  remove: (id: string) => http.delete(`/notifications/${id}`),
};

export interface IClientContact {
  name: string;
  email?: string;
  phone?: string;
}

export interface IClient {
  id: string;
  businessName: string;
  tradeName: string | null;
  taxId: string;
  taxCondition: string;
  fiscalAddress: string;
  contacts: IClientContact[];
  paymentTerms: string;
  billingInfo: string | null;
  /** Key de storage (bucket privado), no una URL usable directo — ver `clientsApi.getLogo` (#128). */
  logoUrl: string | null;
  isActive: boolean;
  /** ISSUE-144: activable/desactivable por cliente, no es un comportamiento global. */
  dinerChoiceEnabled: boolean;
  dinerChoiceDeadlineDaysBefore: number;
  /** ISSUE-209: slug legible (nombre del cliente) usado en la URL pública del portal del comensal (`/:portalSlug`), no el `id` real. */
  portalSlug: string;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateClientPayload {
  businessName: string;
  tradeName?: string;
  taxId: string;
  taxCondition: string;
  fiscalAddress: string;
  contacts: IClientContact[];
  paymentTerms: string;
  billingInfo?: string;
  isActive?: boolean;
  dinerChoiceEnabled?: boolean;
  dinerChoiceDeadlineDaysBefore?: number;
}

export const clientsApi = {
  list: (query: IPaginationQuery = {}) =>
    unwrap<IPaginatedResult<IClient>>(http.get('/clients', { params: query })),

  get: (id: string) => unwrap<IClient>(http.get(`/clients/${id}`)),

  create: (payload: ICreateClientPayload) =>
    unwrap<IClient>(http.post('/clients', payload)),

  update: (id: string, payload: Partial<ICreateClientPayload> & { portalSlug?: string }) =>
    unwrap<IClient>(http.patch(`/clients/${id}`, payload)),

  remove: (id: string) => http.delete(`/clients/${id}`),

  uploadLogo: (id: string, file: Blob, filename: string) => {
    const formData = new FormData();
    formData.append('file', file, filename);
    return unwrap<IClient>(
      http.post(`/clients/${id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  /** `IClient.logoUrl` es la key de storage (bucket privado) — el logo lo sirve el backend por esta ruta. 404 si no tiene logo cargado. */
  getLogo: (id: string) => http.get(`/clients/${id}/logo`, { responseType: 'blob' }).then((r) => r.data as Blob),
};

// ISSUE-144 (cambio de plan): el comensal es un usuario del sistema (rol
// COMENSAL) — nómina gestionable desde Administración (`comensalesApi`) o
// desde el propio portal RR.HH. (`clientPortalComensalesApi`, sin clientId
// en la URL, resuelto por el JWT).
export interface IComensalPinChange {
  id: string;
  changedAt: string;
  dinerName: string;
  externalCode: string;
  /** `diner`: lo cambió el propio comensal; `regenerated`: lo regeneró RR.HH./Administración (`changedBy`). */
  source: 'diner' | 'regenerated';
  changedBy: string | null;
}

export interface IComensal {
  id: string;
  clientId: string;
  firstName: string;
  lastName: string;
  externalCode: string;
  /** Sólo viene presente en la respuesta de `create`/`regeneratePin` — nunca en `list`/`update` (ver backend). */
  accessPin?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateComensalPayload {
  fullName: string;
  externalCode: string;
  isActive?: boolean;
}

export const comensalesApi = {
  list: (clientId: string) => unwrap<IComensal[]>(http.get(`/clients/${clientId}/comensales`)),

  create: (clientId: string, payload: ICreateComensalPayload) =>
    unwrap<IComensal>(http.post(`/clients/${clientId}/comensales`, payload)),

  update: (clientId: string, id: string, payload: Partial<ICreateComensalPayload>) =>
    unwrap<IComensal>(http.patch(`/clients/${clientId}/comensales/${id}`, payload)),

  remove: (clientId: string, id: string) => http.delete(`/clients/${clientId}/comensales/${id}`),

  /** Devuelve el PIN nuevo en claro — mostralo una sola vez, no se puede volver a consultar. */
  regeneratePin: (clientId: string, id: string) => unwrap<IComensal>(http.post(`/clients/${clientId}/comensales/${id}/regenerate-pin`)),
};

/** Mismo CRUD, pero para RR.HH. gestionando su propia nómina desde `/portal` — sin clientId en la URL. */
export const clientPortalComensalesApi = {
  list: () => unwrap<IComensal[]>(http.get('/client-portal/comensales')),

  create: (payload: ICreateComensalPayload) => unwrap<IComensal>(http.post('/client-portal/comensales', payload)),

  update: (id: string, payload: Partial<ICreateComensalPayload>) =>
    unwrap<IComensal>(http.patch(`/client-portal/comensales/${id}`, payload)),

  remove: (id: string) => http.delete(`/client-portal/comensales/${id}`),

  regeneratePin: (id: string) => unwrap<IComensal>(http.post(`/client-portal/comensales/${id}/regenerate-pin`)),

  /** Issue #214: historial de cambios de PIN (fecha, comensal, quién lo cambió). */
  listPinChanges: () => unwrap<IComensalPinChange[]>(http.get('/client-portal/comensales/pin-changes')),
};

export interface ICategory {
  id: string;
  name: string;
  isActive: boolean;
  /** ISSUE-179: false = rubro de insumos no alimentarios (pedido de supervisor). */
  isFood: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateCategoryPayload {
  name: string;
  isActive?: boolean;
  isFood?: boolean;
}

export const categoriesApi = {
  list: (query: IPaginationQuery = {}) =>
    unwrap<IPaginatedResult<ICategory>>(http.get('/categories', { params: query })),

  get: (id: string) => unwrap<ICategory>(http.get(`/categories/${id}`)),

  create: (payload: ICreateCategoryPayload) =>
    unwrap<ICategory>(http.post('/categories', payload)),

  update: (id: string, payload: Partial<ICreateCategoryPayload>) =>
    unwrap<ICategory>(http.patch(`/categories/${id}`, payload)),

  remove: (id: string) => http.delete(`/categories/${id}`),

  reorder: (ids: string[]) => unwrap<ICategory[]>(http.patch('/categories/reorder', { ids })),
};

export interface ISubcategory {
  id: string;
  categoryId: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateSubcategoryPayload {
  categoryId: string;
  name: string;
  isActive?: boolean;
}

export interface ISubcategoriesQuery extends IPaginationQuery {
  categoryId?: string;
}

export const subcategoriesApi = {
  list: (query: ISubcategoriesQuery = {}) =>
    unwrap<IPaginatedResult<ISubcategory>>(http.get('/subcategories', { params: query })),

  // `categoryId` opcional: valida server-side que la subcategoría pertenece a esa categoría (404 si
  // no) — la ruta del ABM trae ambos ids en la URL (`/categories/:categoryId/subcategories/:id`) y
  // sin este chequeo se podía editar una subcategoría de otra categoría con el breadcrumb equivocado.
  get: (id: string, categoryId?: string) =>
    unwrap<ISubcategory>(http.get(`/subcategories/${id}`, { params: { categoryId } })),

  create: (payload: ICreateSubcategoryPayload) =>
    unwrap<ISubcategory>(http.post('/subcategories', payload)),

  update: (id: string, payload: Partial<Pick<ICreateSubcategoryPayload, 'name' | 'isActive'>>, categoryId?: string) =>
    unwrap<ISubcategory>(http.patch(`/subcategories/${id}`, payload, { params: { categoryId } })),

  remove: (id: string, categoryId?: string) => http.delete(`/subcategories/${id}`, { params: { categoryId } }),

  reorder: (categoryId: string, ids: string[]) =>
    unwrap<ISubcategory[]>(http.patch('/subcategories/reorder', { categoryId, ids })),
};

export interface INutritionalValue {
  id: string;
  name: string;
  unit: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateNutritionalValuePayload {
  name: string;
  unit?: string;
  isActive?: boolean;
}

export const nutritionalValuesApi = {
  list: (query: IPaginationQuery = {}) =>
    unwrap<IPaginatedResult<INutritionalValue>>(http.get('/nutritional-values', { params: query })),

  get: (id: string) => unwrap<INutritionalValue>(http.get(`/nutritional-values/${id}`)),

  create: (payload: ICreateNutritionalValuePayload) =>
    unwrap<INutritionalValue>(http.post('/nutritional-values', payload)),

  update: (id: string, payload: Partial<ICreateNutritionalValuePayload>) =>
    unwrap<INutritionalValue>(http.patch(`/nutritional-values/${id}`, payload)),

  remove: (id: string) => http.delete(`/nutritional-values/${id}`),
};

export interface IUnitOfMeasure {
  id: string;
  name: string;
  code: string;
  /** #306: null = envase (bolsa, cajón, pack…). */
  dimension: 'mass' | 'volume' | 'count' | null;
  factorToCanonical: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateUnitOfMeasurePayload {
  name: string;
  code: string;
  dimension?: 'mass' | 'volume' | 'count' | null;
  factorToCanonical?: string | null;
  isActive?: boolean;
}

export const unitsOfMeasureApi = {
  list: (query: IPaginationQuery = {}) =>
    unwrap<IPaginatedResult<IUnitOfMeasure>>(http.get('/units-of-measure', { params: query })),

  get: (id: string) => unwrap<IUnitOfMeasure>(http.get(`/units-of-measure/${id}`)),

  create: (payload: ICreateUnitOfMeasurePayload) =>
    unwrap<IUnitOfMeasure>(http.post('/units-of-measure', payload)),

  update: (id: string, payload: Partial<ICreateUnitOfMeasurePayload>) =>
    unwrap<IUnitOfMeasure>(http.patch(`/units-of-measure/${id}`, payload)),

  remove: (id: string) => http.delete(`/units-of-measure/${id}`),
};

export interface IPaymentTerm {
  id: string;
  name: string;
  /** Plazo en días. Opcional e informativo (base para #115), no se usa en cálculos. */
  days: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ICreatePaymentTermPayload {
  name: string;
  days?: number | null;
  isActive?: boolean;
}

export const paymentTermsApi = {
  list: (query: IPaginationQuery & { active?: boolean } = {}) =>
    unwrap<IPaginatedResult<IPaymentTerm>>(http.get('/payment-terms', { params: query })),

  get: (id: string) => unwrap<IPaymentTerm>(http.get(`/payment-terms/${id}`)),

  create: (payload: ICreatePaymentTermPayload) =>
    unwrap<IPaymentTerm>(http.post('/payment-terms', payload)),

  update: (id: string, payload: Partial<ICreatePaymentTermPayload>) =>
    unwrap<IPaymentTerm>(http.patch(`/payment-terms/${id}`, payload)),

  remove: (id: string) => http.delete(`/payment-terms/${id}`),
};

export interface IWarehouse {
  id: string;
  name: string;
  address: string | null;
  isActive: boolean;
  /** ISSUE-222: depósito central (a lo sumo uno). */
  isCentral: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateWarehousePayload {
  name: string;
  address?: string;
  isActive?: boolean;
  isCentral?: boolean;
}

export const warehousesApi = {
  list: (query: IPaginationQuery & { isActive?: boolean } = {}) =>
    unwrap<IPaginatedResult<IWarehouse>>(http.get('/warehouses', { params: query })),

  get: (id: string) => unwrap<IWarehouse>(http.get(`/warehouses/${id}`)),

  create: (payload: ICreateWarehousePayload) =>
    unwrap<IWarehouse>(http.post('/warehouses', payload)),

  update: (id: string, payload: Partial<ICreateWarehousePayload>) =>
    unwrap<IWarehouse>(http.patch(`/warehouses/${id}`, payload)),

  remove: (id: string) => http.delete(`/warehouses/${id}`),
};

export interface IArticle {
  id: string;
  internalCode: string;
  description: string;
  shortDescription: string | null;
  categoryId: string;
  subcategoryId: string | null;
  baseUnitId: string;
  orderUnitId: string;
  /** #306: cuánto trae una unidad de pedido (ej. "20.0000" kg por cajón); null si la unidad de pedido es la base. */
  orderUnitContent: string | null;
  orderUnitContentUnitId: string | null;
  isPerishable: boolean;
  shelfLifeDays: number | null;
  requiresTempControl: boolean;
  usualSupplierId: string | null;
  minStock: string | null;
  maxStock: string | null;
  /** Calculado por el backend (promedio de las últimas 3 compras) — no se envía en create/update, ver `lastPurchasePrice`. */
  averagePrice: string | null;
  lastPurchasePrice: string | null;
  /** #221: precio de referencia interno (nunca sale al proveedor). */
  referencePrice: string | null;
  /** Ventana de las últimas 3 compras (más reciente al final), solo lectura. */
  recentPurchasePrices: string[] | null;
  isCrossCutting: boolean;
  imageUrl: string | null;
  isActive: boolean;
  notes: string | null;
  /** ISSUE-198: presentación de compra estándar del artículo (ej. "Cajón"), independiente del proveedor. Null = sin presentación definida. */
  purchasePresentationName: string | null;
  /** Contenido de la presentación en la unidad base del artículo (`baseUnitId`), ej. "15.0000" para un cajón de 15kg. */
  purchasePresentationContent: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateArticlePayload {
  description: string;
  shortDescription?: string;
  categoryId: string;
  subcategoryId?: string;
  baseUnitId: string;
  orderUnitId: string;
  orderUnitContent?: number;
  orderUnitContentUnitId?: string;
  isPerishable?: boolean;
  shelfLifeDays?: number;
  requiresTempControl?: boolean;
  usualSupplierId?: string;
  minStock?: number;
  maxStock?: number;
  /** Precio de esta compra. Dispara el recálculo automático de averagePrice en el backend. */
  lastPurchasePrice?: number;
  /** #221: precio de referencia interno; `null` lo borra. */
  referencePrice?: number | null;
  isCrossCutting?: boolean;
  imageUrl?: string;
  notes?: string;
  isActive?: boolean;
}

export interface IArticlesQuery extends IPaginationQuery {
  categoryId?: string;
  subcategoryId?: string;
  isActive?: boolean;
  /** ISSUE-179: false = sólo artículos de rubros no alimentarios. */
  isFood?: boolean;
}

export interface IArticleLookups {
  categories: { id: string; name: string }[];
  subcategories: { id: string; categoryId: string; name: string }[];
  unitsOfMeasure: { id: string; name: string; code: string; dimension: 'mass' | 'volume' | 'count' | null; factorToCanonical: string | null }[];
  suppliers: { id: string; businessName: string }[];
}

export const articlesApi = {
  list: (query: IArticlesQuery = {}) =>
    unwrap<IPaginatedResult<IArticle>>(http.get('/articles', { params: query })),

  get: (id: string) => unwrap<IArticle>(http.get(`/articles/${id}`)),

  create: (payload: ICreateArticlePayload) =>
    unwrap<IArticle>(http.post('/articles', payload)),

  update: (id: string, payload: Partial<ICreateArticlePayload>) =>
    unwrap<IArticle>(http.patch(`/articles/${id}`, payload)),

  remove: (id: string) => http.delete(`/articles/${id}`),

  lookups: () => unwrap<IArticleLookups>(http.get('/articles/lookups')),

  /** #74: relaciones artículo-proveedor de este artículo, con su unidad de venta/bulto si está definida. */
  getArticleSuppliers: (articleId: string) =>
    unwrap<IArticleSupplier[]>(http.get(`/articles/${articleId}/suppliers`)),

  /** @deprecated ISSUE-198: la presentación de compra ya no depende del proveedor, usar `setPurchasePresentation`. */
  setSalesUnitSize: (articleId: string, supplierId: string, salesUnitSize: number | null) =>
    unwrap<IArticleSupplier>(
      http.patch(`/articles/${articleId}/suppliers/${supplierId}/sales-unit-size`, { salesUnitSize }),
    ),

  /** #232: define (o quita con null) el proveedor principal del artículo. */
  setPrimarySupplier: (articleId: string, supplierId: string | null) =>
    unwrap<IArticleSupplier[]>(http.patch(`/articles/${articleId}/primary-supplier`, { supplierId })),

  /** ISSUE-198: define/borra la presentación de compra estándar del artículo (ej. "Cajón" de 15kg), independiente del proveedor. */
  setPurchasePresentation: (articleId: string, name: string | null, content: number | null) =>
    unwrap<IArticle>(http.patch(`/articles/${articleId}/purchase-presentation`, { name, content })),
};

export interface IArticleSupplier {
  id: string;
  articleId: string;
  supplierId: string;
  salesUnitSize: string | null;
  /** #232: proveedor principal del artículo. */
  isPrimary: boolean;
  createdAt: string;
}

export interface IArticleImportRowError {
  field?: string;
  message: string;
}

export interface IArticleImportRow {
  row: number;
  operation: 'create' | 'update' | null;
  internalCode: string | null;
  articleId: string | null;
  matchedBy: 'code' | 'name' | null;
  payload: Record<string, unknown> | null;
  errors: IArticleImportRowError[];
}

export interface IArticleImportPreview {
  batchId: string;
  fileName: string;
  expiresAt: string;
  summary: { create: number; update: number; errors: number };
  rows: IArticleImportRow[];
}

export interface IArticleImportConfirmRowResult {
  row: number;
  operation: 'create' | 'update' | null;
  internalCode: string | null;
  status: 'applied' | 'failed' | 'skipped';
  error?: string;
}

export interface IArticleImportConfirmResult {
  batchId: string;
  applied: number;
  failed: number;
  rows: IArticleImportConfirmRowResult[];
}

export const articleImportsApi = {
  preview: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return unwrap<IArticleImportPreview>(
      http.post('/article-imports/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  confirm: (batchId: string) =>
    unwrap<IArticleImportConfirmResult>(http.post(`/article-imports/${batchId}/confirm`)),

  downloadTemplate: () =>
    http.get('/article-imports/template', { responseType: 'blob' }).then((r) => r.data as Blob),

  /** #112: exporta a .xlsx los artículos que matchean el filtro dado (search/categoryId), sin paginar. */
  exportArticles: (query: { search?: string; categoryId?: string } = {}) =>
    http
      .get('/article-imports/export', { params: query, responseType: 'blob' })
      .then((r) => r.data as Blob),
};

export interface ISite {
  id: string;
  clientId: string;
  siteName: string;
  costCenter: string;
  geoAddress: string;
  deliveryZone: string | null;
  servicesProvided: string[];
  estimatedDiners: number;
  targetCostPerDiner: string | null;
  orderScheduleConfig: Record<string, unknown>;
  deliveryDays: string[];
  warehouseId: string;
  registrationMode: 'app' | 'client' | 'qr';
  clientResponsibleUserId: string | null;
  foodSafetyNotes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Nombre del cliente dueño del site, solo para el placeholder de iniciales del selector de tarjetas (#128) — no es el resto de `IClient` (esa lectura está gateada a Administración). */
  clientName: string;
  /** Si es true, el logo se pide con `sitesApi.getLogo(id)`; si es false, mostrar el placeholder de iniciales directamente. */
  hasLogo: boolean;
}

export interface ICreateSitePayload {
  clientId: string;
  siteName: string;
  geoAddress: string;
  deliveryZone?: string;
  servicesProvided: string[];
  estimatedDiners: number;
  targetCostPerDiner?: number;
  orderScheduleConfig: Record<string, unknown>;
  deliveryDays: string[];
  warehouseId: string;
  registrationMode: 'app' | 'client' | 'qr';
  clientResponsibleUserId?: string;
  foodSafetyNotes?: string;
}

export interface IUserSiteAssignment {
  id: string;
  userId: string;
  siteId: string;
  assignedFrom: string;
  assignedTo: string | null;
  createdAt: string;
}

export const sitesApi = {
  list: (query: IPaginationQuery & { isActive?: boolean; includeInactive?: boolean; clientId?: string } = {}) =>
    unwrap<IPaginatedResult<ISite>>(http.get('/sites', { params: query })),

  /**
   * #305: el backend topea `limit` en 100 (pedir 200/500 se recortaba en silencio), así que los
   * selectores y reportes con más de 100 sites perdían los sobrantes. Recorre todas las páginas y
   * devuelve el resultado con la misma forma que `list`.
   */
  listAll: async (query: { isActive?: boolean; includeInactive?: boolean; clientId?: string; search?: string } = {}) => {
    const pageSize = 100;
    const first = await unwrap<IPaginatedResult<ISite>>(http.get('/sites', { params: { ...query, page: 1, limit: pageSize } }));
    const rest = await Promise.all(
      Array.from({ length: Math.max(0, first.totalPages - 1) }, (_, i) =>
        unwrap<IPaginatedResult<ISite>>(http.get('/sites', { params: { ...query, page: i + 2, limit: pageSize } })),
      ),
    );
    return { ...first, data: [first, ...rest].flatMap((r) => r.data), limit: first.total, page: 1, totalPages: 1 };
  },

  get: (id: string) => unwrap<ISite>(http.get(`/sites/${id}`)),

  create: (payload: ICreateSitePayload) => unwrap<ISite>(http.post('/sites', payload)),

  update: (id: string, payload: Partial<ICreateSitePayload>) =>
    unwrap<ISite>(http.patch(`/sites/${id}`, payload)),

  deactivate: (id: string) => unwrap<ISite>(http.patch(`/sites/${id}/deactivate`)),
  activate: (id: string) => unwrap<ISite>(http.patch(`/sites/${id}/activate`)),

  listAssignments: (siteId: string) =>
    unwrap<IUserSiteAssignment[]>(http.get(`/sites/${siteId}/assignments`)),

  listAssignmentsForUser: (userId: string) =>
    unwrap<IUserSiteAssignment[]>(http.get(`/sites/by-user/${userId}`)),

  assignUser: (siteId: string, userId: string) =>
    unwrap<IUserSiteAssignment>(http.post(`/sites/${siteId}/assignments`, { userId })),

  closeAssignment: (assignmentId: string) =>
    unwrap<IUserSiteAssignment>(http.patch(`/sites/assignments/${assignmentId}/close`)),

  rotateAssignment: (assignmentId: string, toSiteId: string) =>
    unwrap<IUserSiteAssignment>(http.patch(`/sites/assignments/${assignmentId}/rotate`, { toSiteId })),

  listProhibitedIngredients: (siteId: string) =>
    unwrap<IProhibitedIngredient[]>(http.get(`/sites/${siteId}/prohibited-ingredients`)),

  addProhibitedIngredient: (siteId: string, articleId: string) =>
    unwrap<IProhibitedIngredient>(http.post(`/sites/${siteId}/prohibited-ingredients`, { articleId })),

  removeProhibitedIngredient: (siteId: string, articleId: string) =>
    http.delete(`/sites/${siteId}/prohibited-ingredients/${articleId}`),

  /** Logo del cliente dueño del site (proxy, #128) — 404 si el cliente no tiene logo cargado. */
  getLogo: (siteId: string) => http.get(`/sites/${siteId}/logo`, { responseType: 'blob' }).then((r) => r.data as Blob),
};

export interface IProhibitedIngredient {
  id: string;
  articleId: string;
  internalCode: string;
  description: string;
}

export interface ICommercialAgreement {
  id: string;
  siteId: string;
  serviceType: string;
  mealValue: string;
  targetCostPerDiner: string | null;
  adjustmentType: 'currency' | 'index';
  currency?: 'ARS' | 'USD';
  validFrom: string;
  validTo: string;
  billingPeriod: string;
  dinerRules: Record<string, unknown>;
  minBillableAmount: number | null;
  extras: Record<string, unknown> | null;
  contractUrl: string | null;
  status: 'active' | 'superseded';
  version: number;
  previousVersionId: string | null;
  supersededAt: string | null;
  alertSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateCommercialAgreementPayload {
  siteId: string;
  serviceType: string;
  mealValue: number;
  targetCostPerDiner?: number;
  adjustmentType: 'currency' | 'index';
  currency?: 'ARS' | 'USD';
  validFrom: string;
  validTo: string;
  billingPeriod: string;
  dinerRules: Record<string, unknown>;
  minBillableAmount?: number;
  extras?: Record<string, unknown>;
  contractUrl?: string;
}

export type ICreateAgreementVersionPayload = Omit<ICreateCommercialAgreementPayload, 'siteId' | 'serviceType'>;

export const commercialAgreementsApi = {
  list: (query: IPaginationQuery & { siteId?: string } = {}) =>
    unwrap<IPaginatedResult<ICommercialAgreement>>(http.get('/commercial-agreements', { params: query })),

  get: (id: string) => unwrap<ICommercialAgreement>(http.get(`/commercial-agreements/${id}`)),

  listVersions: (id: string) =>
    unwrap<ICommercialAgreement[]>(http.get(`/commercial-agreements/${id}/versions`)),

  create: (payload: ICreateCommercialAgreementPayload) =>
    unwrap<ICommercialAgreement>(http.post('/commercial-agreements', payload)),

  createVersion: (id: string, payload: ICreateAgreementVersionPayload) =>
    unwrap<ICommercialAgreement>(http.post(`/commercial-agreements/${id}/versions`, payload)),
};


export interface IDishIngredient {
  id: string;
  dishId: string;
  articleId: string;
  quantity: string;
  unitOfMeasureId: string;
  correctionFactor: string;
}

export interface IDishSite {
  siteId: string;
  siteName: string;
}

/** Entrada del historial de versiones, con autor y descripción legible del cambio (#141). */
export interface IDishVersionHistoryEntry {
  id: string;
  version: number;
  status: DishStatus;
  previousVersionId: string | null;
  createdAt: string;
  authorName: string | null;
  changeSummary: string;
}

/**
 * Valor nutricional del catálogo (`/nutritional-values`) vinculado a un plato, con el monto
 * propio de ese plato (`amount`, ej. 600 para "Energía") — `name`/`unit` vienen del catálogo,
 * `amount` es dato del vínculo plato-valor, no del catálogo.
 */
export interface IDishNutritionalValue {
  id: string;
  name: string;
  unit: string | null;
  amount: string | null;
}

export interface IDish {
  id: string;
  name: string;
  tags: string[];
  serviceCategories: string[];
  /** Cantidad de comensales de referencia para las cantidades cargadas en los ingredientes (#141). */
  basePortions: number;
  preparation: string | null;
  description: string | null;
  prepTimeMinutes: number | null;
  /** Valores nutricionales del catálogo de Configuración (`/nutritional-values`) vinculados a este plato, con el monto propio de este plato. */
  nutritionalValues: IDishNutritionalValue[];
  allergens: string[] | null;
  suitableDiets: string[] | null;
  photoUrl: string | null;
  version: number;
  status: DishStatus;
  previousVersionId: string | null;
  createdAt: string;
  updatedAt: string;
  ingredients: IDishIngredient[];
  /** Sites habilitados (ISSUE-152) — vacío significa "aplica a todos los sites". */
  sites: IDishSite[];
}

export interface IDishIngredientInput {
  articleId: string;
  quantity: number;
  unitOfMeasureId: string;
  correctionFactor?: number;
}

export interface ICreateDishPayload {
  name: string;
  tags: string[];
  serviceCategories: string[];
  basePortions: number;
  preparation?: string;
  description?: string;
  prepTimeMinutes?: number;
  /** Opcional (#141): un plato puede existir solo con descripción, sin artículos. */
  ingredients?: IDishIngredientInput[];
  allergens?: string[];
  suitableDiets?: string[];
  /** Sites habilitados (ISSUE-152) — si se omite o se envía vacío, el plato aplica a todos los sites. */
  siteIds?: string[];
  /** Valores nutricionales del catálogo (`/nutritional-values`), cada uno con su propio monto para este plato — si se omite no se toca la selección existente. */
  nutritionalValues?: IDishNutritionalValueInput[];
}

export interface IDishNutritionalValueInput {
  nutritionalValueId: string;
  amount?: number;
}

export const dishesApi = {
  list: (query: IPaginationQuery & { status?: DishStatus | DishStatus[]; tag?: string; siteId?: string; clientId?: string; articleId?: string } = {}) =>
    unwrap<IPaginatedResult<IDish>>(http.get('/dishes', { params: query })),

  listTags: (status?: DishStatus) =>
    unwrap<string[]>(http.get('/dishes/tags', { params: status ? { status } : {} })),

  get: (id: string) => unwrap<IDish>(http.get(`/dishes/${id}`)),

  listVersions: (id: string) => unwrap<IDishVersionHistoryEntry[]>(http.get(`/dishes/${id}/versions`)),

  create: (payload: ICreateDishPayload) => unwrap<IDish>(http.post('/dishes', payload)),

  update: (id: string, payload: Partial<ICreateDishPayload>) =>
    unwrap<IDish>(http.patch(`/dishes/${id}`, payload)),

  approve: (id: string) => unwrap<IDish>(http.post(`/dishes/${id}/approve`)),

  deactivate: (id: string) => unwrap<IDish>(http.post(`/dishes/${id}/deactivate`)),

  reactivate: (id: string) => unwrap<IDish>(http.post(`/dishes/${id}/reactivate`)),

  uploadPhoto: (id: string, file: Blob, filename: string) => {
    const formData = new FormData();
    formData.append('file', file, filename);
    return unwrap<IDish>(
      http.post(`/dishes/${id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  /**
   * `IDish.photoUrl` es la key de storage (bucket privado) — la foto la sirve el backend por esta
   * ruta (bytes de la imagen, no una URL de storage). 404 si el plato no tiene foto cargada.
   */
  getPhoto: (id: string) => http.get(`/dishes/${id}/photo`, { responseType: 'blob' }).then((r) => r.data as Blob),

  getVariants: (id: string) => unwrap<IDish[]>(http.get(`/dishes/${id}/variants`)),

  addVariant: (id: string, variantDishId: string) =>
    unwrap<void>(http.post(`/dishes/${id}/variants`, { variantDishId })),

  removeVariant: (id: string, variantDishId: string) =>
    unwrap<void>(http.delete(`/dishes/${id}/variants/${variantDishId}`)),
};


export interface IDailyPlanning {
  id: string;
  siteId: string;
  date: string;
  service: string;
  dishIds: string[];
  estimatedDiners: number;
  confirmedDiners: number | null;
  hasNoService: boolean;
  status: DailyPlanningStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateDailyPlanningPayload {
  siteId: string;
  date: string;
  service: string;
  dishIds?: string[];
  estimatedDiners?: number;
  hasNoService?: boolean;
}

export interface ICreateDailyPlanningFromPlanningPeriodPayload {
  siteId: string;
  date: string;
  service: string;
  estimatedDiners?: number;
}

export interface IUpdateDailyPlanningPayload extends Partial<ICreateDailyPlanningPayload> {
  confirmedDiners?: number;
}

export interface ICopyDailyPlanningsPayload {
  siteId: string;
  fromDate: string;
  toDate: string;
  toFromDate: string;
}

export interface IPlanningDemandLine {
  articleId: string;
  articleDescription: string;
  unitOfMeasureId: string;
  unitOfMeasureCode: string;
  unitOfMeasureName: string;
  quantity: string;
}

export interface IPlanningDemandSummary {
  siteId: string;
  fromDate: string;
  toDate: string;
  lines: IPlanningDemandLine[];
}

export interface IDishLastUsage {
  siteId: string;
  date: string;
  dishId: string;
  lastUsedDate: string | null;
  daysSinceLastUse: number | null;
}

export interface IDailyPlanningCostAlert {
  theoreticalCostPerDiner: string;
  targetCostPerDiner: string | null;
  exceedsTarget: boolean;
  deltaPct: string | null;
  incomplete: boolean;
}

export interface IProhibitedIngredientAlertItem {
  articleId: string;
  internalCode: string;
  description: string;
  dishName: string;
}

export interface IProhibitedIngredientsAlert {
  hasProhibitedIngredients: boolean;
  articles: IProhibitedIngredientAlertItem[];
}

/**
 * ISSUE-199: resultado de `dailyPlanningsApi.preview` — nunca datos de otro site/fecha/servicio.
 * `dailyPlanningId` es `null` cuando la combinación todavía no tiene un `daily_planning`
 * materializado (el preview es de sólo lectura, no lo crea) — se materializa recién al generar.
 */
export type IDailyPlanningPreview =
  | { available: true; dailyPlanningId: string | null; estimatedDiners: number; dishes: { dishId: string; dishName: string }[] }
  | { available: false; reason: string };

export const dailyPlanningsApi = {
  listForSite: (siteId: string, fromDate: string, toDate: string) =>
    unwrap<IDailyPlanning[]>(http.get('/daily-plannings', { params: { siteId, fromDate, toDate } })),

  getDishLastUsage: (siteId: string, date: string, dishIds: string[]) =>
    unwrap<IDishLastUsage[]>(http.get('/daily-plannings/dish-last-usage', { params: { siteId, date, dishIds: dishIds.join(',') } })),

  getCostAlert: (planningId: string) =>
    unwrap<IDailyPlanningCostAlert>(http.get(`/daily-plannings/${planningId}/cost-alert`)),

  getProhibitedIngredientsAlert: (planningId: string) =>
    unwrap<IProhibitedIngredientsAlert>(http.get(`/daily-plannings/${planningId}/prohibited-ingredients-alert`)),

  previewProhibitedIngredients: (siteId: string, dishIds: string[]) =>
    unwrap<IProhibitedIngredientsAlert>(http.get('/daily-plannings/prohibited-ingredients-preview', { params: { siteId, dishIds: dishIds.join(',') } })),

  getDemand: (siteId: string, fromDate: string, toDate: string) =>
    unwrap<IPlanningDemandSummary>(http.get('/daily-plannings/demand', { params: { siteId, fromDate, toDate } })),

  /** ISSUE-199: previsualización de sólo lectura antes de iniciar un pedido de cocina. */
  preview: (params: { siteId: string; date: string; service: string }) =>
    unwrap<IDailyPlanningPreview>(http.get('/daily-plannings/preview', { params })),

  /** ISSUE-209: días (+servicio) con planificación cargada para un site, desde hoy en adelante — alimenta la lista de tarjetas de Pedido de cocina. */
  listPlannedDates: (siteId: string) =>
    unwrap<{ date: string; service: string }[]>(http.get('/daily-plannings/planned-dates', { params: { siteId } })),

  create: (payload: ICreateDailyPlanningPayload) => unwrap<IDailyPlanning>(http.post('/daily-plannings', payload)),

  /** Genera (o reutiliza) el menú a partir de los platos que la Planificación quincenal del cliente dueño del site tiene cargados para esa fecha, y da de alta el día — ver discusión con Manuel (Mattermost, 2026-09). */
  createFromPlanningPeriod: (payload: ICreateDailyPlanningFromPlanningPeriodPayload) =>
    unwrap<IDailyPlanning>(http.post('/daily-plannings/from-planning-period', payload)),

  update: (id: string, payload: IUpdateDailyPlanningPayload) =>
    unwrap<IDailyPlanning>(http.patch(`/daily-plannings/${id}`, payload)),

  confirm: (id: string, confirmedDiners?: number) =>
    unwrap<IDailyPlanning>(http.post(`/daily-plannings/${id}/confirm`, { confirmedDiners })),

  markNoService: (id: string) => unwrap<IDailyPlanning>(http.post(`/daily-plannings/${id}/mark-no-service`)),

  copyPeriod: (payload: ICopyDailyPlanningsPayload) =>
    unwrap<IDailyPlanning[]>(http.post('/daily-plannings/copy', payload)),
};


export interface IPlanningPeriod {
  id: string;
  clientId: string;
  fromDate: string;
  toDate: string;
  status: PlanningPeriodStatus;
  sentAt: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
  choiceClosedAt: string | null;
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IDishOptionIngredientLine {
  articleId: string;
  description: string;
  quantity: string;
  unitOfMeasureCode: string;
}

/** Ficha de una opción de plato de la grilla — nunca trae costo ni precio, para ningún rol (ver backend). */
export interface IDishOptionCard {
  optionId: string;
  position: number;
  dishId: string;
  name: string;
  description: string | null;
  dishStatus: DishStatus;
  ingredientsPerDiner: IDishOptionIngredientLine[];
  nutritionalValues: IDishNutritionalValue[];
  daysSinceLastUsedForClient: number | null;
  lastUsedDate: string | null;
}

export interface IPlanningPeriodDay {
  date: string;
  options: IDishOptionCard[];
  hasNoService: boolean;
}

export interface IPlanningPeriodBoard extends IPlanningPeriod {
  isComplete: boolean;
  incompleteDates: string[];
  days: IPlanningPeriodDay[];
}

export interface ICreatePlanningPeriodPayload {
  clientId: string;
  fromDate: string;
  toDate: string;
}

export interface IAddPlanningPeriodOptionPayload {
  date: string;
  dishId: string;
}

export interface IQuickCreateDishOptionPayload extends ICreateDishPayload {
  date: string;
}

export interface IDailyChoiceRow {
  id: string; name: string; dinerId: string | null; visitorName: string | null;
  date: string; siteId: string | null; service: string; optionPosition: number | null;
  choice: string; source: string; notes: string | null; lastModifiedAt: string | null;
}

export interface IManualChoicePayload {
  date: string; siteId: string; service: string; dinerId?: string; visitorName?: string;
  optionPosition?: number | null; notes?: string;
}

export const planningPeriodsApi = {
  listForClient: (clientId: string) => unwrap<IPlanningPeriod[]>(http.get('/planning-periods', { params: { clientId } })),

  getBoard: (id: string) => unwrap<IPlanningPeriodBoard>(http.get(`/planning-periods/${id}/board`)),

  create: (payload: ICreatePlanningPeriodPayload) => unwrap<IPlanningPeriod>(http.post('/planning-periods', payload)),

  addOption: (id: string, payload: IAddPlanningPeriodOptionPayload) =>
    unwrap<unknown>(http.post(`/planning-periods/${id}/options`, payload)),

  removeOption: (id: string, optionId: string) => unwrap<void>(http.delete(`/planning-periods/${id}/options/${optionId}`)),

  setDayNoService: (id: string, date: string, hasNoService: boolean) =>
    unwrap<IPlanningPeriod>(http.patch(`/planning-periods/${id}/days/${date}/no-service`, { hasNoService })),

  quickCreateOption: (id: string, payload: IQuickCreateDishOptionPayload) =>
    unwrap<unknown>(http.post(`/planning-periods/${id}/options/quick-create`, payload)),

  send: (id: string) => unwrap<IPlanningPeriod>(http.post(`/planning-periods/${id}/send`)),

  returnToDraft: (id: string) => unwrap<IPlanningPeriod>(http.post(`/planning-periods/${id}/return-to-draft`)),

  /** ISSUE-209: deshace el último avance del ciclo (un paso atrás), por si se avanzó por error. */
  goBack: (id: string) => unwrap<IPlanningPeriod>(http.post(`/planning-periods/${id}/go-back`)),

  approve: (id: string) => unwrap<IPlanningPeriod>(http.post(`/planning-periods/${id}/approve`)),

  publish: (id: string) => unwrap<IPlanningPeriod>(http.post(`/planning-periods/${id}/publish`)),

  closeChoice: (id: string) => unwrap<IPlanningPeriod>(http.post(`/planning-periods/${id}/close-choice`)),

  confirm: (id: string) => unwrap<IPlanningPeriod>(http.post(`/planning-periods/${id}/confirm`)),

  /** ISSUE-143: historial de observaciones del cliente (RR.HH.) sobre este período — vigentes y resueltas, con autor. */
  getObservations: (id: string) => unwrap<IPlanningPeriodObservation[]>(http.get(`/planning-periods/${id}/observations`)),

  /** ISSUE-144: avance de elección de comensales por día — para mostrar antes de "Cerrar elección". */
  getChoiceProgress: (id: string) => unwrap<IPlanningPeriodChoiceProgress[]>(http.get(`/planning-periods/${id}/choice-progress`)),

  listChoices: (id: string, params: { date: string; siteId?: string; service?: string }) =>
    unwrap<IDailyChoiceRow[]>(http.get(`/planning-periods/${id}/choices-list`, { params })),
  createManualChoice: (id: string, payload: IManualChoicePayload) => unwrap<IDailyChoiceRow>(http.post(`/planning-periods/${id}/choices`, payload)),
  updateManualChoice: (id: string, choiceId: string, payload: Pick<IManualChoicePayload, 'optionPosition' | 'notes'>) =>
    unwrap<IDailyChoiceRow>(http.patch(`/planning-periods/${id}/choices/${choiceId}`, payload)),
  voidChoice: (id: string, choiceId: string) => unwrap<void>(http.delete(`/planning-periods/${id}/choices/${choiceId}`)),
  exportChoices: (id: string, params: { date: string; siteId?: string; service?: string }) =>
    http.get(`/planning-periods/${id}/choices-list/export`, { params, responseType: 'blob' }).then((response) => response.data as Blob),
};

export interface IPlanningPeriodChoiceProgress {
  date: string;
  chosenCount: number;
  totalDiners: number;
}

export interface IPlanningPeriodObservation {
  date: string;
  version: number;
  comment: string;
  authorName: string;
  createdAt: string;
  resolved: boolean;
}

// ISSUE-143: portal de cliente (rol Cliente RR.HH.) — endpoints separados de
// `planningPeriodsApi` (mismo aislamiento que el backend: `client-portal`
// nunca reusa `/planning-periods`).
export interface IClientPortalOption {
  position: number;
  description: string | null;
  /** #189: valor nutricional por porción de la opción. */
  nutritionalValues: { name: string; unit: string | null; amount: string | null }[];
}

export interface IClientPortalObservation {
  comment: string;
  authorName: string;
  clientName: string;
  createdAt: string;
  resolved: boolean;
}

export interface IClientPortalDay {
  date: string;
  hasNoService: boolean;
  options: IClientPortalOption[];
  currentObservation: IClientPortalObservation | null;
}

export interface IClientPortalPeriod {
  id: string;
  fromDate: string;
  toDate: string;
  status: PlanningPeriodStatus;
  sentAt: string | null;
}

export interface IClientPortalPeriodDetail extends IClientPortalPeriod {
  days: IClientPortalDay[];
  observedDaysCount: number;
  observationHistory: IClientPortalObservation[];
}

export interface IClientPortalClientInfo {
  id: string;
  name: string;
  hasLogo: boolean;
  /** El link al portal del comensal sólo se muestra con la función activa. */
  dinerChoiceEnabled: boolean;
  /** ISSUE-209: `ClientEntity.portalSlug` — usado en la URL pública del portal del comensal en vez de `id`. */
  portalSlug: string | null;
}

// ISSUE-191: reportes, comprobantes, estado de cuenta y reclamos del portal del cliente.
export interface IClientPortalReportQuery {
  from?: string;
  to?: string;
  siteId?: string;
}

export interface IClientPortalServiceReportRow {
  id: string;
  date: string;
  siteId: string;
  siteName: string;
  serviceType: string;
  preparedPortions: number;
  servedPortions: number;
  status: 'pending' | 'validated' | 'disputed';
  /** #190: motivo de la disputa (del cliente o de Supervisión). */
  disputeReason: string | null;
}

/** #190: resultado de validar o rechazar un período de consumos. */
export interface IClientPortalConsumptionPeriodResult {
  affected: number;
  alreadyDisputed: number;
  skipped: number;
}

export interface IClientPortalServiceReport {
  from: string;
  to: string;
  totals: { preparedPortions: number; servedPortions: number; services: number };
  rows: IClientPortalServiceReportRow[];
  truncated: boolean;
}

export interface IClientPortalNutritionDish {
  dishId: string;
  name: string;
  timesServed: number;
  values: { name: string; unit: string | null; amount: string | null }[];
}

export interface IClientPortalNutritionReport {
  from: string;
  to: string;
  dishes: IClientPortalNutritionDish[];
}

export interface IClientPortalInvoice {
  id: string;
  voucherType: string;
  salesPoint: string;
  number: string;
  issueDate: string;
  dueDate: string | null;
  totalAmount: string;
  description: string | null;
}

export interface IClientPortalReceipt {
  id: string;
  receiptDate: string;
  amount: string;
  paymentMethod: PaymentMethod;
  notes: string | null;
}

export interface IClientPortalPage<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface IClientPortalDocumentsQuery {
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface IClientPortalAccountSummary {
  balance: number;
  overdue: { asOf: string; balance: number; overdueBalance: number; notDueBalance: number };
}

export interface IClientPortalAccountMovement {
  id: string;
  createdAt: string;
  entryType: 'invoice' | 'payment' | 'manual_adjustment';
  sourceDocumentType: string;
  amount: string;
  balanceAfter: string;
  dueDate: string | null;
}


export const CLIENT_CLAIM_STATUS_LABELS: Record<ClientClaimStatus, string> = {
  open: 'Abierto',
  in_progress: 'En gestión',
  resolved: 'Resuelto',
};

export interface IClientClaim {
  id: string;
  clientId: string;
  clientName: string | null;
  siteId: string | null;
  siteName: string | null;
  subject: string;
  description: string;
  status: ClientClaimStatus;
  response: string | null;
  respondedAt: string | null;
  createdAt: string;
}

export interface ICreateClientClaimPayload {
  subject: string;
  description: string;
  siteId?: string;
}

export const clientClaimsApi = {
  list: (status?: ClientClaimStatus) => unwrap<IClientClaim[]>(http.get('/client-claims', { params: { status } })),

  respond: (id: string, payload: { status: ClientClaimStatus; response?: string }) =>
    unwrap<IClientClaim>(http.patch(`/client-claims/${id}`, payload)),
};

export const clientPortalApi = {
  getClientInfo: () => unwrap<IClientPortalClientInfo>(http.get('/client-portal/client')),

  /** Mismo logo que usa el cliente en el sistema interno — se usa como foto del usuario del portal. 404 si no tiene logo cargado. */
  getClientLogo: () => http.get('/client-portal/client/logo', { responseType: 'blob' }).then((r) => r.data as Blob),

  getPeriods: () => unwrap<IClientPortalPeriod[]>(http.get('/client-portal/planning-periods')),

  getPeriodDetail: (id: string) => unwrap<IClientPortalPeriodDetail>(http.get(`/client-portal/planning-periods/${id}`)),

  observeDay: (id: string, date: string, comment: string) =>
    unwrap<IClientPortalPeriodDetail>(http.post(`/client-portal/planning-periods/${id}/observe-day`, { date, comment })),

  approvePeriod: (id: string) => unwrap<IClientPortalPeriodDetail>(http.post(`/client-portal/planning-periods/${id}/approve`)),

  downloadPdf: (id: string) => http.get(`/client-portal/planning-periods/${id}/pdf`, { responseType: 'blob' }),

  getServiceReport: (query: IClientPortalReportQuery) =>
    unwrap<IClientPortalServiceReport>(http.get('/client-portal/reports/service', { params: query })),

  validateConsumptions: (payload: { from: string; to: string; siteId?: string }) =>
    unwrap<IClientPortalConsumptionPeriodResult>(http.post('/client-portal/consumptions/validate', payload)),

  rejectConsumptions: (payload: { from: string; to: string; siteId?: string; reason: string }) =>
    unwrap<IClientPortalConsumptionPeriodResult>(http.post('/client-portal/consumptions/reject', payload)),

  observeConsumption: (id: string, reason: string) =>
    unwrap<{ id: string; status: string; disputeReason: string | null }>(http.post(`/client-portal/consumptions/${id}/observe`, { reason })),

  getNutritionReport: (query: IClientPortalReportQuery) =>
    unwrap<IClientPortalNutritionReport>(http.get('/client-portal/reports/nutrition', { params: query })),

  getInvoices: (query: IClientPortalDocumentsQuery = {}) =>
    unwrap<IClientPortalPage<IClientPortalInvoice>>(http.get('/client-portal/invoices', { params: query })),

  getReceipts: (query: IClientPortalDocumentsQuery = {}) =>
    unwrap<IClientPortalPage<IClientPortalReceipt>>(http.get('/client-portal/receipts', { params: query })),

  getAccountStatement: () => unwrap<IClientPortalAccountSummary>(http.get('/client-portal/account-statement')),

  getAccountMovements: (query: IClientPortalDocumentsQuery = {}) =>
    unwrap<IClientPortalPage<IClientPortalAccountMovement>>(http.get('/client-portal/account-movements', { params: query })),

  getSites: () => unwrap<{ id: string; name: string }[]>(http.get('/client-portal/sites')),

  getClaims: () => unwrap<IClientClaim[]>(http.get('/client-portal/claims')),

  createClaim: (payload: ICreateClientClaimPayload) => unwrap<IClientClaim>(http.post('/client-portal/claims', payload)),
};

// ISSUE-144 (cambio de plan): portal del comensal CON login real (JWT), vía
// `POST /auth/login-diner` (código externo + PIN, sin email/contraseña). Usa
// una instancia de axios y claves de localStorage APARTE (`diner-accessToken`/
// `diner-refreshToken`) — nunca las mismas que `http` — para que loguearse acá
// no pise la sesión de un usuario interno/RR.HH. en el mismo navegador.
const dinerHttp = axios.create({ baseURL: '/api', headers: { 'Content-Type': 'application/json' } });

dinerHttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('diner-accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

dinerHttp.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem('diner-refreshToken');
      if (refresh) {
        try {
          const { data } = await axios.post<{ data: IAuthTokens }>('/api/auth/refresh', { refreshToken: refresh });
          localStorage.setItem('diner-accessToken', data.data.accessToken);
          localStorage.setItem('diner-refreshToken', data.data.refreshToken);
          original.headers.Authorization = `Bearer ${data.data.accessToken}`;
          return dinerHttp(original);
        } catch {
          localStorage.removeItem('diner-accessToken');
          localStorage.removeItem('diner-refreshToken');
        }
      }
    }
    return Promise.reject(error);
  },
);

export interface IDinerPortalOption {
  position: number;
  description: string | null;
}

export interface IDinerPortalDay {
  date: string;
  hasNoService: boolean;
  options: IDinerPortalOption[];
  selectedOptionPosition: number | null;
  alreadyChose: boolean;
  choiceDeadline: string;
  isPastDeadline: boolean;
}

export interface IDinerPortalPeriod {
  id: string;
  fromDate: string;
  toDate: string;
  status: PlanningPeriodStatus;
  days: IDinerPortalDay[];
}

export const dinerPortalApi = {
  /** Guarda los tokens en `diner-accessToken`/`diner-refreshToken` — llamalo antes de usar el resto de este objeto. `slug` es `ClientEntity.portalSlug` (ISSUE-209), no el `id` real del cliente. */
  login: async (slug: string, externalCode: string, accessPin: string) => {
    const { data } = await dinerHttp.post<{ data: IAuthTokens & { user: { firstName: string; lastName: string } } }>(
      '/auth/login-diner',
      { slug, externalCode, accessPin },
    );
    localStorage.setItem('diner-accessToken', data.data.accessToken);
    localStorage.setItem('diner-refreshToken', data.data.refreshToken);
    return data.data.user;
  },

  /** Público (sin sesión): nombre del sitio al que corresponde el slug, para mostrarlo en el login. */
  getSite: (slug: string) =>
    unwrap<{ name: string; hasLogo: boolean }>(dinerHttp.get(`/diner-portal/public/site/${encodeURIComponent(slug)}`)),
  /** #314: URL pública del logo de la empresa (para el login de comensal y de RR.HH.). */
  siteLogoUrl: (slug: string) => `/api/diner-portal/public/site/${encodeURIComponent(slug)}/logo`,

  /** #314: empresa de la sesión del comensal (la resuelve el backend por el token, no la URL). */
  getOwnSite: () => unwrap<{ name: string; portalSlug: string | null }>(dinerHttp.get('/diner-portal/site')),

  logout: () => {
    localStorage.removeItem('diner-accessToken');
    localStorage.removeItem('diner-refreshToken');
  },

  isLoggedIn: () => !!localStorage.getItem('diner-accessToken'),

  /** Para cuando ya había una sesión guardada (F5) y hace falta el nombre para el header. */
  me: () => unwrap<{ firstName: string; lastName: string }>(dinerHttp.get('/auth/me')),

  /** Issue #214: el comensal cambia su PIN (inicial = últimos 4 dígitos del DNI) — exige el actual. */
  changePin: (currentPin: string, newPin: string) => dinerHttp.post('/diner-portal/change-pin', { currentPin, newPin }),

  getPeriods: () => unwrap<IDinerPortalPeriod[]>(dinerHttp.get('/diner-portal/planning-periods')),

  chooseDay: (date: string, optionPosition: number | null) =>
    unwrap<IDinerPortalPeriod>(dinerHttp.post('/diner-portal/choice', { date, optionPosition })),

  /** #241: encuesta de satisfacción semestral — si hay una ventana abierta hoy y si el comensal ya respondió. */
  getSurveyStatus: () => unwrap<IDinerSurveyStatus>(dinerHttp.get('/diner-portal/survey')),

  submitSurvey: (rating: number, comment?: string) =>
    unwrap<{ windowStart: string; rating: number }>(dinerHttp.post('/diner-portal/survey', { rating, comment: comment || undefined })),
};

export interface IDinerSurveyStatus {
  open: boolean;
  window: { start: string; end: string } | null;
  answered: boolean;
  rating: number | null;
  comment: string | null;
}

export interface IPeriodSatisfactionMetric {
  windowStart: string;
  windowEnd: string;
  responses: number;
  averageRating: number;
  acceptancePercent: number;
}

export interface IPeriodSatisfactionComment {
  windowStart: string;
  rating: number;
  comment: string;
  createdAt: string;
  siteName: string | null;
}

export interface IDishSatisfactionMetric {
  dishId: string;
  dishName: string;
  responses: number;
  averageRating: number;
  acceptancePercent: number;
}

export const dinerSatisfactionApi = {
  periodMetrics: (params: { fromDate?: string; toDate?: string; siteId?: string }) =>
    unwrap<IPeriodSatisfactionMetric[]>(http.get('/diner-satisfaction/period-metrics', { params })),

  periodComments: (params: { fromDate?: string; toDate?: string; siteId?: string }) =>
    unwrap<IPeriodSatisfactionComment[]>(http.get('/diner-satisfaction/period-comments', { params })),

  /** Histórico de la encuesta diaria (reemplazada por la semestral). */
  dishMetrics: (params: { fromDate?: string; toDate?: string; siteId?: string }) =>
    unwrap<IDishSatisfactionMetric[]>(http.get('/diner-satisfaction/dish-metrics', { params })),
};

export type LeftoverDestination = 'reuse' | 'waste' | 'other';

export interface ILeftoversDetail {
  destination: LeftoverDestination;
  notes?: string;
}

export interface IServiceConsumption {
  id: string;
  siteId: string;
  date: string;
  serviceType: string;
  servedDishIds: string[];
  preparedPortions: number;
  servedPortions: number;
  leftoversDetail: ILeftoversDetail | null;
  validatedByUserId: string | null;
  status: ServiceConsumptionStatus;
  disputeReason: string | null;
  createdAt: string;
  updatedAt: string;
}


export interface IDemandProjection {
  id: string;
  fromDate: string;
  toDate: string;
  status: DemandProjectionStatus;
  generatedByUserId: string | null;
  lastCalculatedAt: string;
  approvedByUserId: string | null;
  approvedAt: string | null;
  publishedByUserId: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateServiceConsumptionPayload {
  siteId: string;
  date: string;
  serviceType: string;
  preparedPortions: number;
  servedPortions: number;
  leftoversDetail?: ILeftoversDetail;
}

export interface IUpdateServiceConsumptionPayload {
  reason: string;
  preparedPortions?: number;
  servedPortions?: number;
  /** Omitido conserva el destino actual; null lo limpia. */
  leftoversDetail?: ILeftoversDetail | null;
}

export type SupervisorInboxReason = 'sin_declarar' | 'pendiente_validacion' | 'en_disputa' | 'inconsistencia_sin_servicio' | 'desvio_significativo' | 'diferencia_sobras';

export interface ISupervisorInboxRow {
  consumption: IServiceConsumption;
  planned: number | null;
  leftoverDiffPct: number;
  deviationPct: number | null;
  reasons: SupervisorInboxReason[];
  periodClosed: boolean;
}

export const serviceConsumptionsApi = {
  supervisorInbox: (params: { siteId?: string; fromDate: string; toDate: string; serviceType?: string }) =>
    unwrap<ISupervisorInboxRow[]>(http.get('/service-consumptions/supervisor-inbox', { params })),

  getLeftoverThresholdPct: () => unwrap<{ thresholdPct: number }>(http.get('/service-consumptions/leftover-threshold-pct')),

  listForSite: (siteId: string, fromDate: string, toDate: string) =>
    unwrap<IServiceConsumption[]>(http.get('/service-consumptions', { params: { siteId, fromDate, toDate } })),

  get: (id: string) => unwrap<IServiceConsumption>(http.get(`/service-consumptions/${id}`)),

  create: (payload: ICreateServiceConsumptionPayload) =>
    unwrap<IServiceConsumption>(http.post('/service-consumptions', payload)),

  update: (id: string, payload: IUpdateServiceConsumptionPayload) =>
    unwrap<IServiceConsumption>(http.patch(`/service-consumptions/${id}`, payload)),

  validate: (id: string) => unwrap<IServiceConsumption>(http.patch(`/service-consumptions/${id}/validate`)),

  dispute: (id: string, reason: string) =>
    unwrap<IServiceConsumption>(http.patch(`/service-consumptions/${id}/dispute`, { reason })),
};

/** #182: apertura del servicio del día (§2.3.1). */
export interface IServiceOpeningRecord {
  id: string;
  openedAt: string;
  openedByUserId: string;
  openedByName: string;
  siteName: string;
  siteAddress: string;
  dinersAtOpening: number;
}

export interface IServiceOpeningDay {
  dailyPlanningId: string;
  date: string;
  service: string;
  diners: number;
  dinersSource: 'confirmed' | 'estimated';
  hasNoService: boolean;
  dishCount: number;
  opening: IServiceOpeningRecord | null;
}

export interface IServiceOpeningLine {
  articleId: string;
  articleDescription: string;
  unitOfMeasureCode: string;
  quantity: string;
  perPortion: string | null;
}

export interface IServiceOpeningDetail {
  dailyPlanningId: string;
  siteId: string;
  siteName: string;
  siteAddress: string;
  date: string;
  service: string;
  diners: number;
  dinersSource: 'confirmed' | 'estimated';
  hasNoService: boolean;
  dishes: { dishId: string; dishName: string; hasIngredients: boolean; lines: IServiceOpeningLine[] }[];
  opening: IServiceOpeningRecord | null;
}

export interface IServiceOpeningTarget {
  siteId: string;
  date: string;
  service: string;
}

export const serviceOpeningsApi = {
  days: (siteId: string, fromDate: string, toDate: string) =>
    unwrap<IServiceOpeningDay[]>(http.get('/service-openings/days', { params: { siteId, fromDate, toDate } })),

  detail: (target: IServiceOpeningTarget) => unwrap<IServiceOpeningDetail>(http.get('/service-openings/detail', { params: target })),

  open: (target: IServiceOpeningTarget) => unwrap<IServiceOpeningRecord>(http.post('/service-openings', target)),
};

export type ReconciliationExceptionReason = 'sin_declarar' | 'pendiente_validacion' | 'en_disputa' | 'inconsistencia_sin_servicio' | 'desvio_significativo';

export interface IReconciliationPeriod {
  id: string;
  siteId: string;
  serviceType: string;
  periodStart: string;
  periodEnd: string;
  status: ReconciliationPeriodStatus;
  closedByUserId: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IReconciliationCrossRow {
  date: string;
  planned: number | null;
  prepared: number | null;
  served: number | null;
  toBill: number | null;
  consumptionId: string | null;
  consumptionStatus: ServiceConsumptionStatus | null;
  disputeReason: string | null;
  planningStatus: DailyPlanningStatus | null;
  hasNoService: boolean;
  isException: boolean;
  exceptionReasons: ReconciliationExceptionReason[];
}

export interface IIngredientReconciliationLine {
  articleId: string;
  articleDescription: string;
  unitOfMeasureId: string;
  unitOfMeasureCode: string;
  theoreticalQuantity: string;
  /** #306: `true` si algún ingrediente no se pudo convertir a la unidad base — el teórico es parcial y no hay desvío. */
  theoreticalIncomplete: boolean;
  consumedQuantity: string;
  deviationPct: string | null;
  exceedsThreshold: boolean;
}

export interface ICreateReconciliationPeriodPayload {
  siteId: string;
  serviceType: string;
  periodStart: string;
  periodEnd: string;
}

export const reconciliationPeriodsApi = {
  list: (query: { siteId?: string; serviceType?: string; status?: ReconciliationPeriodStatus } = {}) =>
    unwrap<IReconciliationPeriod[]>(http.get('/reconciliation-periods', { params: query })),

  get: (id: string) => unwrap<IReconciliationPeriod>(http.get(`/reconciliation-periods/${id}`)),

  crossTable: (id: string) => unwrap<IReconciliationCrossRow[]>(http.get(`/reconciliation-periods/${id}/cross-table`)),

  exceptions: (id: string) => unwrap<IReconciliationCrossRow[]>(http.get(`/reconciliation-periods/${id}/exceptions`)),

  ingredients: (id: string) => unwrap<IIngredientReconciliationLine[]>(http.get(`/reconciliation-periods/${id}/ingredients`)),

  create: (payload: ICreateReconciliationPeriodPayload) =>
    unwrap<IReconciliationPeriod>(http.post('/reconciliation-periods', payload)),

  close: (id: string) => unwrap<IReconciliationPeriod>(http.patch(`/reconciliation-periods/${id}/close`)),
};

export interface IDemandProjectionLine {
  articleId: string;
  articleDescription: string;
  unitOfMeasureId: string;
  unitOfMeasureCode: string;
  unitOfMeasureName: string;
  date: string;
  requiredQuantity: string;
  projectedWarehouseStock: string;
  balance: string;
}

export interface IDemandProjectionOverlap {
  id: string;
  fromDate: string;
  toDate: string;
  status: DemandProjectionStatus;
}

export interface IDemandProjectionWithLines extends IDemandProjection {
  lines: IDemandProjectionLine[];
  isStale: boolean;
  overlapsWith: IDemandProjectionOverlap[];
}

export interface ICreateDemandProjectionPayload {
  fromDate: string;
  toDate: string;
}

export const demandProjectionsApi = {
  list: (query: IPaginationQuery & { status?: DemandProjectionStatus } = {}) =>
    unwrap<IPaginatedResult<IDemandProjection>>(http.get('/demand-projections', { params: query })),

  get: (id: string) => unwrap<IDemandProjectionWithLines>(http.get(`/demand-projections/${id}`)),

  generateDraft: (payload: ICreateDemandProjectionPayload) =>
    unwrap<IDemandProjectionWithLines>(http.post('/demand-projections', payload)),

  recalculate: (id: string) => unwrap<IDemandProjectionWithLines>(http.post(`/demand-projections/${id}/recalculate`)),

  approve: (id: string) => unwrap<IDemandProjectionWithLines>(http.post(`/demand-projections/${id}/approve`)),

  publish: (id: string) => unwrap<IDemandProjectionWithLines>(http.post(`/demand-projections/${id}/publish`)),

  unapprove: (id: string) => unwrap<IDemandProjectionWithLines>(http.post(`/demand-projections/${id}/unapprove`)),

  unpublish: (id: string) => unwrap<IDemandProjectionWithLines>(http.post(`/demand-projections/${id}/unpublish`)),
};

export const usersApi = {
  list: (query: IPaginationQuery & { role?: UserRole } = {}) =>
    unwrap<IPaginatedResult<IUser>>(http.get('/users', { params: query })),

  get: (id: string) => unwrap<IUser>(http.get(`/users/${id}`)),

  // El rol Comensal no manda `password` (el backend genera email/PIN sintéticos) y
  // la respuesta trae `accessPin` en claro una única vez — ver UsersService.createComensal.
  create: (payload: Partial<IUser> & { password?: string }) =>
    unwrap<IUser & { accessPin?: string }>(http.post('/users', payload)),

  update: (id: string, payload: Partial<IUser>) =>
    unwrap<IUser>(http.patch(`/users/${id}`, payload)),

  changePassword: (id: string, password: string) =>
    http.patch(`/users/${id}/password`, { password }),

  // #247: blanqueo de clave por el administrador. `temporary` devuelve la clave una sola vez.
  resetPassword: (id: string, mode: ResetPasswordMode) =>
    unwrap<{ mode: ResetPasswordMode; temporaryPassword?: string }>(http.post(`/users/${id}/reset-password`, { mode })),

  remove: (id: string) => http.delete(`/users/${id}`),
};

export type ResetPasswordMode = 'temporary' | 'email';

export interface IDinerImportRow {
  row: number;
  firstName: string | null;
  lastName: string | null;
  externalCode: string | null;
  errors: { field?: string; message: string }[];
}

export interface IDinerImportPreview {
  batchId: string;
  fileName: string;
  expiresAt: string;
  summary: { valid: number; errors: number };
  rows: IDinerImportRow[];
}

export interface IDinerImportConfirmRowResult {
  row: number;
  externalCode: string | null;
  status: 'applied' | 'failed' | 'skipped';
  accessPin?: string;
  error?: string;
}

export interface IDinerImportConfirmResult {
  batchId: string;
  applied: number;
  failed: number;
  rows: IDinerImportConfirmRowResult[];
}

export const dinerImportsApi = {
  preview: (clientId: string, siteId: string, file: File) => {
    const formData = new FormData();
    formData.append('clientId', clientId);
    formData.append('siteId', siteId);
    formData.append('file', file);
    return unwrap<IDinerImportPreview>(
      http.post('/diner-imports/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  confirm: (batchId: string) =>
    unwrap<IDinerImportConfirmResult>(http.post(`/diner-imports/${batchId}/confirm`)),

  downloadTemplate: () =>
    http.get('/diner-imports/template', { responseType: 'blob' }).then((r) => r.data as Blob),
};

/** #215: importación masiva de comensales desde el portal de RR.HH. — el cliente sale del JWT, sólo se elige el site. */
export const clientPortalDinerImportsApi = {
  listSites: () => unwrap<{ id: string; name: string }[]>(http.get('/client-portal/sites')),

  preview: (siteId: string, file: File) => {
    const formData = new FormData();
    formData.append('siteId', siteId);
    formData.append('file', file);
    return unwrap<IDinerImportPreview>(
      http.post('/client-portal/comensales/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    );
  },

  confirm: (batchId: string) =>
    unwrap<IDinerImportConfirmResult>(http.post(`/client-portal/comensales/import/${batchId}/confirm`)),

  downloadTemplate: () =>
    http.get('/client-portal/comensales/import/template', { responseType: 'blob' }).then((r) => r.data as Blob),
};

export interface IWhatsappStatus {
  status: 'connected' | 'disconnected';
  phoneNumber?: string;
}

export interface IConnectWhatsappPayload {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

export type WhatsappTestTemplateStatus = 'APPROVED' | 'PENDING' | 'REJECTED' | 'NOT_FOUND';

export interface IWhatsappTestTemplate {
  name: string;
  status: WhatsappTestTemplateStatus;
}

export type BusinessParameterType = 'string' | 'number' | 'boolean' | 'json';

export interface IBusinessParameter {
  id: string;
  key: string;
  label: string;
  value: unknown;
  type: BusinessParameterType;
  category: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateBusinessParameterPayload {
  key: string;
  label: string;
  value: unknown;
  type: BusinessParameterType;
  category: string;
  description?: string;
}

export const businessParametersApi = {
  list: (category?: string) =>
    unwrap<IBusinessParameter[]>(http.get('/business-parameters', { params: category ? { category } : {} })),

  get: (key: string) => unwrap<IBusinessParameter>(http.get(`/business-parameters/${key}`)),

  create: (payload: ICreateBusinessParameterPayload) =>
    unwrap<IBusinessParameter>(http.post('/business-parameters', payload)),

  update: (key: string, payload: { value: unknown; label?: string; description?: string }) =>
    unwrap<IBusinessParameter>(http.patch(`/business-parameters/${key}`, payload)),

  remove: (key: string) => http.delete(`/business-parameters/${key}`),
};

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'approve'
  | 'cancel'
  | 'export'
  | 'login'
  | 'logout'
  | 'access_denied';

export interface IAuditFieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface IAuditLog {
  id: string;
  userId: string | null;
  /** Nombre completo del usuario al momento de leer el registro (no se guarda en el log — se resuelve contra la tabla de usuarios vigente). `null` si el usuario fue eliminado o la acción no tuvo usuario (ej. login fallido). */
  userName: string | null;
  action: AuditAction;
  module: string;
  entityName: string;
  entityId: string | null;
  changes: IAuditFieldChange[] | null;
  ipAddress: string | null;
  userAgent: string | null;
  reason: string | null;
  createdAt: string;
}

export interface IQueryAuditLogs {
  page?: number;
  limit?: number;
  module?: string;
  entityName?: string;
  userId?: string;
  /** Texto libre sobre nombre, apellido o email del usuario. */
  search?: string;
  action?: AuditAction;
  from?: string;
  to?: string;
}

export interface ISodAlert {
  id: string;
  userId: string;
  userName: string | null;
  module: string;
  entityName: string;
  entityId: string;
  firstAction: AuditAction;
  firstActionLogId: string;
  secondAction: AuditAction;
  secondActionLogId: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewedByName: string | null;
}

export interface IQuerySodAlerts {
  page?: number;
  limit?: number;
  module?: string;
  userId?: string;
  /** Texto libre sobre nombre, apellido o email del usuario. */
  search?: string;
  entityId?: string;
  reviewed?: boolean;
}

export const auditApi = {
  list: (query: IQueryAuditLogs = {}) =>
    unwrap<IPaginatedResult<IAuditLog>>(http.get('/audit-logs', { params: query })),

  getByEntity: (entityName: string, entityId: string, params: { page?: number; limit?: number } = {}) =>
    unwrap<IPaginatedResult<IAuditLog>>(http.get(`/audit-logs/entity/${entityName}/${entityId}`, { params })),

  listSodAlerts: (query: IQuerySodAlerts = {}) =>
    unwrap<IPaginatedResult<ISodAlert>>(http.get('/audit-logs/sod-alerts', { params: query })),

  reviewSodAlert: (id: string) =>
    unwrap<ISodAlert>(http.patch(`/audit-logs/sod-alerts/${id}/review`)),
};

export const documentTemplatesApi = {
  list: () => unwrap<IDocumentTemplate[]>(http.get('/document-templates')),

  get: (id: string) => unwrap<IDocumentTemplate>(http.get(`/document-templates/${id}`)),

  preview: (id: string) => unwrap<{ html: string }>(http.get(`/document-templates/${id}/preview`)),

  create: (payload: ICreateDocumentTemplatePayload) =>
    unwrap<IDocumentTemplate>(http.post('/document-templates', payload)),

  update: (id: string, payload: IUpdateDocumentTemplatePayload) =>
    unwrap<IDocumentTemplate>(http.patch(`/document-templates/${id}`, payload)),

  activate: (id: string) => unwrap<IDocumentTemplate>(http.patch(`/document-templates/${id}`, { activa: true })),

  remove: (id: string) => http.delete(`/document-templates/${id}`),
};

export const whatsappApi = {
  getStatus: () => unwrap<IWhatsappStatus>(http.get('/whatsapp/status')),

  connect: (payload: IConnectWhatsappPayload) =>
    unwrap<IWhatsappStatus>(http.post('/whatsapp/connect', payload)),

  getTestTemplateStatus: () =>
    unwrap<IWhatsappTestTemplate>(http.get('/whatsapp/test-template')),

  createTestTemplate: () =>
    unwrap<IWhatsappTestTemplate>(http.post('/whatsapp/test-template')),

  sendTestMessage: (to: string) =>
    unwrap<{ sent: boolean }>(http.post('/whatsapp/test-message', { to })),
};

export type BpmChecklistItemCriticality = 'critico' | 'mayor' | 'menor';

export interface IBpmChecklistItem {
  id: string;
  text: string;
  order: number;
  criticality: BpmChecklistItemCriticality;
  allowsNotApplicable: boolean;
  requiresPhotoOnFail: boolean;
}

export interface IBpmChecklistSection {
  id: string;
  name: string;
  order: number;
  items: IBpmChecklistItem[];
}

export interface IReplaceBpmChecklistTemplatePayload {
  /** Versión sobre la que se armó la edición; si ya no es la vigente el backend responde 409. */
  expectedVersion: number;
  sections: {
    id?: string;
    name: string;
    items: {
      id?: string;
      text: string;
      isCritical: boolean;
      allowsNotApplicable: boolean;
      requiresPhotoOnFail: boolean;
    }[];
  }[];
}

export interface IBpmChecklistTemplate {
  id: string;
  name: string;
  version: number;
  status: 'active' | 'inactive';
  sections: IBpmChecklistSection[];
  createdAt: string;
  updatedAt: string;
}

export type StockLocationType = 'warehouse' | 'site' | 'transit';
export type StockMovementType =
  | 'inbound'
  | 'transfer'
  | 'production_consumption'
  | 'manual_consumption'
  | 'waste'
  | 'return'
  | 'count_adjustment'
  | 'manual_adjustment'
  | 'leftover_reuse';
export type WasteReasonCode = 'expiration' | 'breakage' | 'theft' | 'other';

export interface IStockBalance {
  id: string;
  articleId: string;
  locationType: StockLocationType;
  locationId: string;
  quantity: string;
  averageCost: string;
  createdAt: string;
  updatedAt: string;
}

export type BpmAnswerValue = 'cumple' | 'no_cumple' | 'no_aplica';
export type BpmSurveyOrigin = 'interno' | 'auditoria_cliente' | 'organismo';

export interface IBpmSurveyAnswer {
  itemId: string;
  sectionId: string;
  value: BpmAnswerValue;
  observation?: string;
  photoKeys?: string[];
}

export interface IBpmSectionObservation {
  sectionId: string;
  observation: string;
}

export interface IBpmSectionScore {
  sectionId: string;
  score: number | null;
}

export interface IBpmSurvey {
  id: string;
  clientGeneratedId: string;
  siteId: string;
  templateId: string;
  templateVersion: number;
  startDate: string;
  closeDate: string | null;
  supervisorId: string;
  origin: BpmSurveyOrigin;
  answers: IBpmSurveyAnswer[];
  sectionObservations: IBpmSectionObservation[];
  generalPhotoKeys: string[];
  sectionScores: IBpmSectionScore[] | null;
  totalScore: string | null;
  status: BpmSurveyStatus;
  createdAt: string;
  updatedAt: string;
}

export interface IStockMovement {
  id: string;
  articleId: string;
  locationType: StockLocationType;
  locationId: string;
  movementType: StockMovementType;
  quantity: string;
  batch: string | null;
  expirationDate: string | null;
  unitValue: string;
  sourceDocumentId: string | null;
  movementDate: string;
}

export interface IStockLot {
  id: string;
  articleId: string;
  locationType: StockLocationType;
  locationId: string;
  batch: string;
  expirationDate: string;
  remainingQty: string;
  unitCost: string;
  receivedAt: string;
}

export interface IApplyMovementPayload {
  articleId: string;
  locationType: StockLocationType;
  locationId: string;
  movementType: StockMovementType;
  quantity: number;
  direction?: 'in' | 'out';
  unitCost?: number;
  batch?: string;
  expirationDate?: string;
  sourceDocumentId?: string;
}

export interface IRegisterWastePayload {
  articleId: string;
  locationType: StockLocationType;
  locationId: string;
  quantity: number;
  reasonCode: WasteReasonCode;
  batch?: string;
}

export interface ICreateStockCountPayload {
  locationType: StockLocationType;
  locationId: string;
  articleIds?: string[];
}

export interface IStockCount {
  id: string;
  locationType: StockLocationType;
  locationId: string;
  countDate: string;
  status: StockCountStatus;
  performedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IStockCountLineView {
  id: string;
  stockCountId: string;
  articleId: string;
  countedQty: string | null;
  status: StockCountLineStatus;
}

export interface ICreateManualStockAdjustmentPayload {
  articleId: string;
  locationType: StockLocationType;
  locationId: string;
  quantityDelta: number;
  motivo: string;
  batch?: string;
  expirationDate?: string;
}

export interface IManualStockAdjustment {
  id: string;
  userId: string | null;
  action: string;
  module: string;
  entityName: string;
  entityId: string | null;
  changes: { field: string; oldValue: unknown; newValue: unknown }[] | null;
  reason: string | null;
  createdAt: string;
}

export interface IFractionStockLotDestination {
  locationType: StockLocationType;
  locationId: string;
  quantity: number;
}

export interface ILotTraceabilityDestination {
  lotId: string;
  locationType: StockLocationType;
  locationId: string;
  quantity: string;
}

export interface ILotTraceability {
  rootLotId: string;
  purchaseOrderLineId: string | null;
  receptionId: string | null;
  destinations: ILotTraceabilityDestination[];
}

export const stockApi = {
  listBalances: (locationType?: StockLocationType, locationId?: string) =>
    unwrap<IStockBalance[]>(http.get('/stock/balances', { params: { locationType, locationId } })),

  getBalance: (articleId: string, locationType: StockLocationType, locationId: string) =>
    unwrap<IStockBalance | null>(http.get(`/stock/balances/${articleId}`, { params: { locationType, locationId } })),

  listMovements: (filters: { articleId?: string; locationType?: StockLocationType; locationId?: string; movementType?: StockMovementType } = {}) =>
    unwrap<IStockMovement[]>(http.get('/stock/movements', { params: filters })),

  listLots: (articleId: string, locationType: StockLocationType, locationId: string) =>
    unwrap<IStockLot[]>(http.get('/stock/lots', { params: { articleId, locationType, locationId } })),

  applyMovement: (payload: IApplyMovementPayload) => unwrap<IStockMovement>(http.post('/stock/movements', payload)),

  registerWaste: (payload: IRegisterWastePayload) => unwrap<IStockMovement>(http.post('/stock/waste', payload)),

  createCount: (payload: ICreateStockCountPayload) => unwrap<IStockCount>(http.post('/stock/counts', payload)),

  getCount: (id: string) => unwrap<{ count: IStockCount; lines: IStockCountLineView[] }>(http.get(`/stock/counts/${id}`)),

  updateCountLine: (countId: string, lineId: string, countedQty: number) =>
    unwrap<IStockCountLineView>(http.patch(`/stock/counts/${countId}/lines/${lineId}`, { countedQty })),

  confirmCount: (id: string) => unwrap<IStockCount>(http.post(`/stock/counts/${id}/confirm`)),

  createManualAdjustment: (payload: ICreateManualStockAdjustmentPayload) =>
    unwrap<IStockMovement>(http.post('/stock/manual-adjustments', payload)),

  listManualAdjustments: (articleId?: string) =>
    unwrap<IManualStockAdjustment[]>(http.get('/stock/manual-adjustments', { params: { articleId } })),

  fractionLot: (lotId: string, destinations: IFractionStockLotDestination[]) =>
    unwrap<IStockLot[]>(http.post(`/stock/lots/${lotId}/fraction`, { destinations })),

  getLotTraceability: (lotId: string) => unwrap<ILotTraceability>(http.get(`/stock/lots/${lotId}/traceability`)),
};

// --- Proveedores (#23) ---

export type SupplierAttachmentType = 'contract' | 'certification' | 'other';

export interface ISupplier {
  id: string;
  businessName: string;
  taxId: string;
  tradeName?: string | null;
  taxCondition: string;
  /** Ids del catálogo de categorías (ISSUE-153, el mismo que usan los artículos) — múltiple, puede ser vacío. */
  categories: string[];
  bankingInfo: Record<string, unknown>;
  commercialTerms: Record<string, unknown>;
  status: SupplierStatus;
  minOrderAmount: string | null;
  contactEmail: string | null;
  /** #265: proveedor genérico «varios» (compras fuera de circuito). */
  isGeneric: boolean;
  /** #266: permite pagar a este proveedor sin factura. */
  allowPaymentWithoutInvoice: boolean;
  /** #115: medio de pago por defecto (cheque, echeque, transferencia o efectivo); la condición por defecto (solo modalidad) es commercialTerms.paymentTerms. */
  defaultPaymentMethod: PaymentMethod | null;
  /** #310: plazo de vencimiento de la factura en días (independiente de la condición de pago, #311). */
  dueDays: number | null;
  /** #310: fecha desde la que se cuenta `dueDays`. */
  dueDaysBase: 'invoice_date' | 'receipt_date';
  createdAt: string;
  updatedAt: string;
}

export interface ICreateSupplierPayload {
  businessName: string;
  taxId: string;
  tradeName?: string | null;
  taxCondition: string;
  categories?: string[];
  bankingInfo: Record<string, unknown>;
  commercialTerms: Record<string, unknown>;
  status?: SupplierStatus;
  minOrderAmount?: number;
  contactEmail?: string;
  allowPaymentWithoutInvoice?: boolean;
  defaultPaymentMethod?: PaymentMethod | null;
  dueDays?: number | null;
  dueDaysBase?: 'invoice_date' | 'receipt_date';
}

export interface ISupplierAttachment {
  id: string;
  supplierId: string;
  fileName: string;
  fileUrl: string;
  attachmentType: SupplierAttachmentType;
  expirationDate: string | null;
  createdAt: string;
}

export interface ICreateSupplierAttachmentPayload {
  fileName: string;
  fileUrl: string;
  attachmentType?: SupplierAttachmentType;
  expirationDate?: string;
}

export interface ISupplierContactLog {
  id: string;
  supplierId: string;
  userId: string;
  userName: string;
  note: string;
  rating: number;
  createdAt: string;
}

export interface ISupplierContactLogSummary {
  items: ISupplierContactLog[];
  averageRating: number | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ICreateSupplierContactLogPayload {
  note: string;
  rating: number;
}

export const suppliersApi = {
  list: (query: IPaginationQuery & { status?: string; categoryId?: string } = {}) =>
    unwrap<IPaginatedResult<ISupplier>>(http.get('/suppliers', { params: query })),

  get: (id: string) => unwrap<ISupplier>(http.get(`/suppliers/${id}`)),

  create: (payload: ICreateSupplierPayload) => unwrap<ISupplier>(http.post('/suppliers', payload)),

  update: (id: string, payload: Partial<ICreateSupplierPayload>) =>
    unwrap<ISupplier>(http.patch(`/suppliers/${id}`, payload)),

  deactivate: (id: string) => unwrap<ISupplier>(http.patch(`/suppliers/${id}/deactivate`)),

  listAttachments: (id: string) => unwrap<ISupplierAttachment[]>(http.get(`/suppliers/${id}/attachments`)),

  addAttachment: (id: string, payload: ICreateSupplierAttachmentPayload) =>
    unwrap<ISupplierAttachment>(http.post(`/suppliers/${id}/attachments`, payload)),

  removeAttachment: (id: string, attachmentId: string) =>
    http.delete(`/suppliers/${id}/attachments/${attachmentId}`),

  getContactLogs: (id: string, query: IPaginationQuery = {}) =>
    unwrap<ISupplierContactLogSummary>(http.get(`/suppliers/${id}/contact-logs`, { params: query })),

  addContactLog: (id: string, payload: ICreateSupplierContactLogPayload) =>
    unwrap<ISupplierContactLog>(http.post(`/suppliers/${id}/contact-logs`, payload)),
};

// --- Retenciones asociadas al proveedor (#114) ---

export interface ISupplierWithholdingRule {
  id: string;
  supplierId: string;
  withholdingRuleId: string;
  exempt: boolean;
  exemptionCertificateNumber: string | null;
  exemptionValidUntil: string | null;
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  rule: IWithholdingRule;
}

export interface ICreateSupplierWithholdingRulePayload {
  withholdingRuleId: string;
  exempt?: boolean;
  exemptionCertificateNumber?: string;
  exemptionValidUntil?: string;
  notes?: string;
}

export interface IUpdateSupplierWithholdingRulePayload {
  exempt?: boolean;
  exemptionCertificateNumber?: string;
  exemptionValidUntil?: string;
  notes?: string;
}

export const supplierWithholdingRulesApi = {
  list: (supplierId: string) => unwrap<ISupplierWithholdingRule[]>(http.get(`/suppliers/${supplierId}/withholding-rules`)),

  associate: (supplierId: string, payload: ICreateSupplierWithholdingRulePayload) =>
    unwrap<ISupplierWithholdingRule>(http.post(`/suppliers/${supplierId}/withholding-rules`, payload)),

  update: (id: string, payload: IUpdateSupplierWithholdingRulePayload) =>
    unwrap<ISupplierWithholdingRule>(http.patch(`/suppliers/withholding-rules/${id}`, payload)),

  remove: (id: string) => http.delete(`/suppliers/withholding-rules/${id}`),
};

// --- Percepciones de proveedor (#312) ---

export interface ISupplierPerception {
  id: string;
  supplierId: string;
  taxType: PerceptionTaxType;
  label: string | null;
  rate: string;
  validUntil: string | null;
  active: boolean;
}

export interface ISupplierPerceptionPayload {
  taxType: PerceptionTaxType;
  label?: string;
  rate: number;
  validUntil?: string;
  active?: boolean;
}

export const supplierPerceptionsApi = {
  list: (supplierId: string) => unwrap<ISupplierPerception[]>(http.get(`/suppliers/${supplierId}/perceptions`)),

  create: (supplierId: string, payload: ISupplierPerceptionPayload) =>
    unwrap<ISupplierPerception>(http.post(`/suppliers/${supplierId}/perceptions`, payload)),

  update: (id: string, payload: Partial<ISupplierPerceptionPayload>) =>
    unwrap<ISupplierPerception>(http.patch(`/suppliers/perceptions/${id}`, payload)),

  remove: (id: string) => http.delete(`/suppliers/perceptions/${id}`),
};

// --- Precios de proveedor (#24, #25) ---

export interface ISupplierPrice {
  id: string;
  supplierId: string;
  articleId: string;
  price: string;
  validFrom: string;
  createdAt: string;
}

export interface ISiteMarginRow {
  siteId: string;
  siteName: string;
  serviceType: string;
  mealValue: string;
  previousMargin: string;
  newMargin: string;
  previousMarginPct: string;
  newMarginPct: string;
}

export interface IDishMarginImpact {
  averagePreviousMargin: string;
  averageNewMargin: string;
  averagePreviousMarginPct: string;
  averageNewMarginPct: string;
  bySite: ISiteMarginRow[];
}

export interface IDishCostImpactRow {
  dishId: string;
  dishName: string;
  previousCost: string;
  newCost: string;
  deltaPct: string;
  marginImpact: IDishMarginImpact | null;
}

export interface ICreateSupplierPriceResult {
  price: ISupplierPrice;
  increasePct: string | null;
  alertTriggered: boolean;
  costImpact: IDishCostImpactRow[];
}

export interface ISupplierComparisonRow {
  supplierId: string;
  supplierName: string;
  price: string;
  validFrom: string;
}

export interface ISimulatePriceResult {
  articleId: string;
  supplierId: string;
  currentPrice: string | null;
  hypotheticalPrice: string;
  costImpact: IDishCostImpactRow[];
}

export interface ISupplierPriceImportRow {
  row: number;
  supplierId: string | null;
  articleId: string | null;
  price: string | null;
  validFrom: string | null;
  errors: { field?: string; message: string }[];
}

export interface ISupplierPriceImportPreview {
  batchId: string;
  fileName: string;
  expiresAt: string;
  summary: { valid: number; errors: number };
  rows: ISupplierPriceImportRow[];
}

export interface ISupplierPriceImportConfirmResult {
  batchId: string;
  applied: number;
  failed: number;
  rows: { row: number; status: 'applied' | 'failed' | 'skipped'; error?: string }[];
}

export const supplierPricesApi = {
  create: (payload: { supplierId: string; articleId: string; price: number; validFrom?: string }) =>
    unwrap<ICreateSupplierPriceResult>(http.post('/supplier-prices', payload)),

  comparison: (articleId: string) =>
    unwrap<ISupplierComparisonRow[]>(http.get('/supplier-prices/comparison', { params: { articleId } })),

  /** #281: Compras quita un artículo de la oferta vigente de un proveedor. */
  remove: (supplierId: string, articleId: string) =>
    unwrap<{ removed: boolean }>(http.delete(`/supplier-prices/${supplierId}/${articleId}`)),

  simulate: (payload: { articleId: string; supplierId: string; hypotheticalPrice: number }) =>
    unwrap<ISimulatePriceResult>(http.post('/supplier-prices/simulator', payload)),

  previewImport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return unwrap<ISupplierPriceImportPreview>(
      http.post('/supplier-prices/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  confirmImport: (batchId: string, mode: 'add' | 'reset' = 'add') =>
    unwrap<ISupplierPriceImportConfirmResult>(http.post(`/supplier-prices/import/${batchId}/confirm`, { mode })),

  downloadTemplate: () =>
    http.get('/supplier-prices/import/template', { responseType: 'blob' }).then((r) => r.data as Blob),

  exportCurrentPrices: () =>
    http.get('/supplier-prices/export-current', { responseType: 'blob' }).then((r) => r.data as Blob),
};

export interface ISyncBpmSurveyPayload {
  clientGeneratedId: string;
  siteId: string;
  templateId: string;
  templateVersion: number;
  startDate: string;
  origin: BpmSurveyOrigin;
  answers: IBpmSurveyAnswer[];
  sectionObservations: IBpmSectionObservation[];
  generalPhotoKeys: string[];
  status: BpmSurveyStatus;
}

export const bpmChecklistTemplatesApi = {
  getActive: () => unwrap<IBpmChecklistTemplate>(http.get('/bpm-checklist-templates/active')),

  get: (id: string) => unwrap<IBpmChecklistTemplate>(http.get(`/bpm-checklist-templates/${id}`)),

  // Tabla maestra — solo rol Administración (rol exacto, no por nivel)
  getMaster: () => unwrap<IBpmChecklistTemplate>(http.get('/bpm-checklist-templates/master')),

  replaceMaster: (payload: IReplaceBpmChecklistTemplatePayload) =>
    unwrap<IBpmChecklistTemplate>(http.put('/bpm-checklist-templates/master', payload)),
};

/** #246: filtros del listado de relevamientos; los mismos viajan a la exportación a Excel. */
export interface IBpmSurveyExportParams {
  from?: string;
  to?: string;
  siteId?: string;
  templateId?: string;
  status?: BpmSurveyStatus;
  /** Un único relevamiento (exportación desde el detalle). */
  surveyId?: string;
}

export const bpmSurveysApi = {
  listMine: (query: IPaginationQuery & { status?: BpmSurveyStatus; siteId?: string; from?: string; to?: string } = {}) =>
    unwrap<IPaginatedResult<IBpmSurvey>>(http.get('/bpm-surveys/mine', { params: query })),

  get: (id: string) => unwrap<IBpmSurvey>(http.get(`/bpm-surveys/${id}`)),

  /** #246: .xlsx con una fila por ítem respondido (+ hoja Resumen). Ante un 400 el cuerpo llega como Blob: se lee el mensaje. */
  exportExcel: async (params: IBpmSurveyExportParams = {}): Promise<Blob> => {
    try {
      const response = await http.get('/bpm-surveys/export', { params, responseType: 'blob' });
      return response.data as Blob;
    } catch (error) {
      const data = (error as { response?: { data?: unknown } }).response?.data;
      if (data instanceof Blob) {
        let message = 'No se pudo generar el archivo';
        try {
          const body = JSON.parse(await data.text()) as { message?: string | string[] };
          message = Array.isArray(body.message) ? body.message.join(', ') : (body.message ?? message);
        } catch {
          /* el cuerpo no era JSON: queda el mensaje genérico */
        }
        throw new Error(message);
      }
      throw error;
    }
  },

  sync: (payload: ISyncBpmSurveyPayload) => unwrap<IBpmSurvey>(http.put('/bpm-surveys/sync', payload)),

  uploadPhoto: (surveyId: string, file: Blob, filename: string) => {
    const formData = new FormData();
    formData.append('file', file, filename);
    return unwrap<{ key: string }>(
      http.post(`/bpm-surveys/${surveyId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },
};

export type BpmFindingSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface IBpmFinding {
  id: string;
  bpmSurveyId: string;
  lastSurveyId: string | null;
  siteId: string;
  itemId: string;
  sectionId: string;
  description: string;
  severity: BpmFindingSeverity;
  responsibleId: string | null;
  dueDate: string | null;
  status: BpmFindingStatus;
  recurrenceCount: number;
  correctionNote: string | null;
  correctionPhotoKey: string | null;
  correctedAt: string | null;
  correctedByUserId: string | null;
  verifiedAt: string | null;
  verifiedByUserId: string | null;
  rejectionNote: string | null;
  rejectedAt: string | null;
  rejectedByUserId: string | null;
  criticalNotifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IQueryBpmFindings extends IPaginationQuery {
  status?: BpmFindingStatus;
  severity?: BpmFindingSeverity;
  siteId?: string;
}

export const bpmFindingsApi = {
  list: (query: IQueryBpmFindings = {}) =>
    unwrap<IPaginatedResult<IBpmFinding>>(http.get('/bpm-findings', { params: query })),

  listMine: (query: IQueryBpmFindings = {}) =>
    unwrap<IPaginatedResult<IBpmFinding>>(http.get('/bpm-findings/mine', { params: query })),

  get: (id: string) => unwrap<IBpmFinding>(http.get(`/bpm-findings/${id}`)),

  startCorrection: (id: string) => unwrap<IBpmFinding>(http.patch(`/bpm-findings/${id}/start-correction`)),

  correct: (id: string, payload: { note: string; photoKey?: string }) =>
    unwrap<IBpmFinding>(http.patch(`/bpm-findings/${id}/correct`, payload)),

  verify: (id: string) => unwrap<IBpmFinding>(http.patch(`/bpm-findings/${id}/verify`)),

  reject: (id: string, note: string) => unwrap<IBpmFinding>(http.patch(`/bpm-findings/${id}/reject`, { note })),

  assign: (id: string, responsibleId: string) =>
    unwrap<IBpmFinding>(http.patch(`/bpm-findings/${id}/assign`, { responsibleId })),

  uploadPhoto: (findingId: string, file: Blob, filename: string) => {
    const formData = new FormData();
    formData.append('file', file, filename);
    return unwrap<{ key: string }>(
      http.post(`/bpm-findings/${findingId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },
};

// ---- Kitchen orders (#19/#20) ----

/** #177: payload del 409 al enviar un pedido que necesita confirmación del cocinero. */
export interface IKitchenOrderConfirmationRequired {
  code: 'CONFIRMATION_REQUIRED';
  atypicalLines: { lineId: string; articleDescription: string; suggestedQuantity: string; orderedQuantity: string; deviationPercent: number }[];
  outOfWindow: boolean;
  deadlineHour: string;
}

/** #178: urgencia de un pedido de reposición. */
export type KitchenOrderUrgency = 'normal' | 'urgent';

export type KitchenOrderOrigin = 'automatic' | 'manual' | 'restock' | 'exceptional' | 'supervisor';

/** #257: motivo/categoría de un pedido adicional. */
export type KitchenOrderAdditionalReason = 'visit' | 'extra_diners' | 'forgot' | 'restock';

export const KITCHEN_ORDER_ORIGIN_LABELS: Record<KitchenOrderOrigin, string> = {
  automatic: 'Automático',
  manual: 'Manual',
  restock: 'Reposición',
  exceptional: 'Excepcional',
  supervisor: 'Supervisor',
};
/** "Secciones libres" del pedido de cocina (ISSUE-142) — artículos sueltos sin ficha de plato. */
export type FreeKitchenOrderLineSection = 'salad_bar' | 'entradas' | 'postres' | 'otros';
/** 'transversales': limpieza/descartables cargados a mano; se muestran en el bloque Transversales, no como sección libre. */
export type KitchenOrderLineSection = FreeKitchenOrderLineSection | 'transversales';
export const KITCHEN_ORDER_SECTIONS: { value: FreeKitchenOrderLineSection; label: string }[] = [
  { value: 'salad_bar', label: 'Salad Bar' },
  { value: 'entradas', label: 'Entradas' },
  { value: 'postres', label: 'Postres' },
  { value: 'otros', label: 'Otros' },
];

export interface IKitchenOrder {
  id: string;
  voucherNumber: string;
  siteId: string;
  orderDate: string;
  deliveryDate: string;
  /** ISSUE-199: identidad del pedido pasa a ser (siteId, deliveryDate, service). */
  service: string;
  origin: KitchenOrderOrigin;
  approvedByUserId: string | null;
  /** ISSUE-146: cuándo aprobó Compras / cuándo envió Cocina. Null hasta entonces y en pedidos históricos. */
  approvedAt: string | null;
  sentAt: string | null;
  sentByUserId: string | null;
  status: KitchenOrderStatus;
  notes: string | null;
  /** #181: observación con la que Compras devolvió el pedido; null si no fue devuelto (o ya se reenvió). */
  returnObservation: string | null;
  /** #259: Cocina modificó el pedido después de enviarlo a Compras (y antes de que lo aprobara). Null si no lo hizo. */
  editedAfterSendAt: string | null;
  /** #233: pedido del que se dividió este por rubro (null si no es un pedido hijo) y motivo de la división. */
  splitFromKitchenOrderId: string | null;
  splitReason: string | null;
  createdByUserId: string | null;
  /** Solo para origin 'exceptional': comensales adicionales estimados que motivan el pedido. */
  extraDinersEstimate: number | null;
  /** #257: motivo/categoría del pedido adicional (null en pedidos no adicionales y en adicionales previos a #257). */
  additionalReason: KitchenOrderAdditionalReason | null;
  createdAt: string;
  updatedAt: string;
}

export interface IKitchenOrderLineView {
  id: string;
  articleId: string;
  articleDescription: string;
  /** #242: presentación de compra (ej. "Cajón (15 kg)"); null si el artículo no la tiene. */
  articlePresentation: string | null;
  suggestedQuantity: string | null;
  orderedQuantity: string;
  unitOfMeasureId: string;
  unitOfMeasureCode: string;
  modificationReason: string | null;
  status: KitchenOrderLineStatus;
  isCrossCutting: boolean;
  section: KitchenOrderLineSection | null;
  dishId: string | null;
  orderedPortions: number | null;
}

/** ISSUE-142: card por plato — ingredientes ya cargados + metadata para el stepper de porciones. */
export interface IKitchenOrderDishGroup {
  dishId: string;
  dishName: string;
  basePortions: number;
  comensales: number;
  /** #298: cuántos comensales eligieron este plato en el portal (no cambia aunque el cocinero ajuste porciones); `null` si no hay elecciones. */
  chosenCount?: number | null;
  hasIngredients: boolean;
  lines: IKitchenOrderLineView[];
  /** Porciones vigentes del plato — `comensales` si el stepper nunca se aplicó, o el valor explícito de la última vez que se aplicó (ver `KitchenOrdersService.attachLines`). */
  currentPortions: number;
}

export interface IKitchenOrderDetail {
  order: IKitchenOrder;
  dishLines: IKitchenOrderLineView[];
  transversalLines: IKitchenOrderLineView[];
  dishGroups: IKitchenOrderDishGroup[];
  freeSections: Record<FreeKitchenOrderLineSection, IKitchenOrderLineView[]>;
  deviationThresholdPercent: number;
  /** #259: Cocina puede agregar/editar líneas (pedido generado, o enviado y todavía no aprobado por Compras). */
  editable: boolean;
  /** #259: por qué el pedido está bloqueado para Cocina; null si es editable. */
  lockedReason: string | null;
  /** #306: ingredientes que no se pudieron convertir a la unidad de pedido; no se calcularon ni se inventó su cantidad. */
  conversionWarnings?: string[];
}

/** #228: seguimiento del pedido para el cocinero — estado derivado + semáforo + ítems. */
export interface IKitchenOrderTracking extends IKitchenOrder {
  trackingStatus: 'pending' | 'in_process' | 'in_transit' | 'received' | 'closed' | 'expired';
  trafficLight: 'green' | 'yellow' | 'red' | null;
  items: { id: string; articleDescription: string; articlePresentation: string | null; orderedQuantity: string; unitOfMeasureCode: string }[];
}

export const kitchenOrdersApi = {
  tracking: (filters: { siteId?: string; from?: string; to?: string } = {}) =>
    unwrap<IKitchenOrderTracking[]>(http.get('/kitchen-orders/tracking', { params: filters })),

  list: (filters: { siteId?: string; status?: KitchenOrderStatus; origin?: KitchenOrderOrigin } = {}) =>
    unwrap<IKitchenOrder[]>(http.get('/kitchen-orders', { params: filters })),

  get: (id: string) => unwrap<IKitchenOrderDetail>(http.get(`/kitchen-orders/${id}`)),

  generate: (payload: { siteId: string; deliveryDate: string }) =>
    unwrap<IKitchenOrderDetail>(http.post('/kitchen-orders/generate', payload)),

  createManual: (payload: {
    siteId: string;
    deliveryDate: string;
    service: string;
    origin: 'manual' | 'restock' | 'exceptional';
    lines?: { articleId: string; quantity: number; unitOfMeasureId?: string }[];
    notes?: string;
    /** Solo para origin 'exceptional'. */
    extraDinersEstimate?: number;
    /** Solo para origin 'restock' (#178). Default 'normal'. */
    urgency?: KitchenOrderUrgency;
    /** #257: obligatorio para 'exceptional' (visit | extra_diners | forgot); 'restock' lo implica. */
    additionalReason?: KitchenOrderAdditionalReason;
  }) => unwrap<IKitchenOrderDetail>(http.post('/kitchen-orders', payload)),

  updateLine: (
    orderId: string,
    lineId: string,
    payload: { orderedQuantity: number; modificationReason?: string; orderedPortions?: number; expectedOrderedQuantity?: number },
  ) =>
    unwrap<IKitchenOrderLineView>(http.patch(`/kitchen-orders/${orderId}/lines/${lineId}`, payload)),

  /** ISSUE-142: agregar una línea suelta a una sección libre o a un plato puntual (exactamente uno de section/dishId) de un pedido ya generado, sin sugerencia. */
  addLine: (
    orderId: string,
    payload: { articleId: string; quantity: number; section?: KitchenOrderLineSection; dishId?: string; unitOfMeasureId?: string },
  ) => unwrap<IKitchenOrderDetail>(http.post(`/kitchen-orders/${orderId}/lines`, payload)),

  /** #177: sin confirmaciones explícitas, un envío atípico/fuera de ventana responde 409 `CONFIRMATION_REQUIRED` (ver `IKitchenOrderConfirmationRequired`). */
  confirm: (id: string, acknowledgements: { acknowledgeAtypical?: boolean; acknowledgeOutOfWindow?: boolean } = {}) =>
    unwrap<IKitchenOrderDetail>(http.post(`/kitchen-orders/${id}/confirm`, acknowledgements)),
};

/** ISSUE-179: pedido de insumos no alimentarios de Supervisión — un pedido por site, enviado directo a Compras. */
export const supervisorOrdersApi = {
  create: (payload: { deliveryDate: string; siteIds: string[]; lines: { articleId: string; quantity: number; unitOfMeasureId?: string }[]; notes?: string }) =>
    unwrap<IKitchenOrder[]>(http.post('/kitchen-orders/supervisor', payload)),

  list: () => kitchenOrdersApi.list({ origin: 'supervisor' }),
};

/** ISSUE-146: bandeja de Compras. */
export type PurchasingInboxStatus = 'to_approve' | 'approved' | 'returned';
export type PurchasingLineOrigin = 'suggested' | 'added' | 'free';

export interface IPurchasingInboxOrder {
  id: string;
  voucherNumber: string;
  service: string;
  origin: KitchenOrderOrigin;
  status: KitchenOrderStatus;
  displayStatus: PurchasingInboxStatus;
  sentAt: string | null;
  sentByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  totalValue: string;
  unpricedLines: number;
  dishCount: number;
  portions: number;
  articleCount: number;
  /** #177: el pedido se envió fuera de la ventana horaria ("fuera de término"). */
  sentOutOfWindow: boolean;
  /** #178: urgencia de la reposición ('normal' en el resto de los orígenes). */
  urgency: KitchenOrderUrgency;
  /** #257: categoría del pedido adicional; null en el resto de los orígenes. */
  additionalReason?: KitchenOrderAdditionalReason | null;
  /** #180: líneas modificadas del pedido (alerta de modificación). */
  modifiedLineCount: number;
}

export interface IPurchasingInboxDay {
  date: string;
  sites: { siteId: string; siteName: string; orders: IPurchasingInboxOrder[] }[];
}

export interface IPurchasingLineView {
  id: string;
  articleId: string;
  articleDescription: string;
  articlePresentation: string | null;
  /** #233: rubro del artículo. */
  categoryId: string | null;
  unitOfMeasureCode: string;
  suggestedQuantity: string | null;
  orderedQuantity: string;
  origin: PurchasingLineOrigin;
  section: KitchenOrderLineSection | null;
  dishId: string | null;
  status: KitchenOrderLineStatus;
  modificationReason: string | null;
  /** #234: cantidad antes del primer ajuste post-aprobación; null si nunca se ajustó. */
  originalQuantity: string | null;
  replacedArticleId: string | null;
  /** Precio de última compra (vigente antes de aprobar, congelado después). Null si el artículo no tiene. */
  unitPrice: string | null;
  lineValue: string | null;
}

export interface IPurchasingOrderDetail {
  order: IKitchenOrder;
  lines: IPurchasingLineView[];
  totalValue: string;
  unpricedLines: number;
  editable: boolean;
  /** #234: pedido aprobado/consolidado — se puede ajustar o anular con motivo. */
  adjustable: boolean;
  /** #301: se puede anular con motivo (aprobado/consolidado o devuelto a Cocina). */
  cancellable: boolean;
  /** Pedido en una consolidación confirmada: hay que reabrirla antes de anularlo o ajustarlo. */
  consolidationLocked: boolean;
  /** Artículos del pedido que ya entraron a una consolidación. */
  consolidatedArticleIds: string[];
}

export interface IServiceCostEntry {
  service: string;
  orderCount: number;
  excludedOrders: number;
  unpricedLines: number;
  totalValue: string;
  servedPortions: number;
  costPerService: string | null;
  reason: string | null;
}

export interface ICategoryCentralization {
  categoryId: string;
  categoryName: string;
  quantities: { unitCode: string; quantity: string }[];
  suppliers: string[];
}

export interface IPurchasingCentralization {
  approvedOrders: number;
  groups: ICategoryCentralization[];
}

export const purchasingOrdersApi = {
  inbox: (filters: { from?: string; to?: string; siteId?: string; status?: PurchasingInboxStatus; origin?: KitchenOrderOrigin } = {}) =>
    unwrap<IPurchasingInboxDay[]>(http.get('/kitchen-orders/purchasing-inbox', { params: filters })),

  detail: (id: string) => unwrap<IPurchasingOrderDetail>(http.get(`/kitchen-orders/${id}/purchasing-detail`)),

  centralization: (date: string) =>
    unwrap<IPurchasingCentralization>(http.get('/kitchen-orders/purchasing-centralization', { params: { date } })),

  serviceCost: (params: { siteId: string; date: string }) =>
    unwrap<IServiceCostEntry[]>(http.get('/kitchen-orders/service-cost', { params })),

  approve: (id: string) => unwrap<IPurchasingOrderDetail>(http.post(`/kitchen-orders/${id}/approve`)),

  /** #181: devuelve el pedido a Cocina con una observación obligatoria. */
  returnOrder: (id: string, observation: string) =>
    unwrap<IPurchasingOrderDetail>(http.post(`/kitchen-orders/${id}/return`, { observation })),

  /** #233: las líneas de los rubros elegidos pasan a un pedido hijo con otra fecha de entrega. */
  splitByCategory: (id: string, payload: { newDeliveryDate: string; categoryIds: string[]; reason: string }) =>
    unwrap<{ id: string; childId: string }>(http.post(`/kitchen-orders/${id}/split`, payload)),

  /** #253: reprograma la fecha de entrega del pedido completo. */
  reschedule: (id: string, payload: { newDeliveryDate: string; reason: string }) =>
    unwrap<{ id: string }>(http.post(`/kitchen-orders/${id}/reschedule`, payload)),

  addLine: (id: string, payload: { articleId: string; quantity: number; unitOfMeasureId?: string }) =>
    unwrap<IPurchasingOrderDetail>(http.post(`/kitchen-orders/${id}/purchasing/lines`, payload)),

  updateLine: (id: string, lineId: string, payload: { orderedQuantity: number; modificationReason?: string; expectedOrderedQuantity?: number }) =>
    unwrap<IPurchasingOrderDetail>(http.patch(`/kitchen-orders/${id}/purchasing/lines/${lineId}`, payload)),

  removeLine: (id: string, lineId: string) =>
    unwrap<IPurchasingOrderDetail>(http.delete(`/kitchen-orders/${id}/purchasing/lines/${lineId}`)),

  /** #234: anula el pedido (motivo obligatorio). 409 si hay OC emitida. */
  cancelOrder: (id: string, reason: string) => unwrap<IPurchasingOrderDetail>(http.post(`/kitchen-orders/${id}/cancel`, { reason })),

  /** #234: ajusta la cantidad de una línea de un pedido aprobado (conserva la original). */
  adjustApprovedLine: (id: string, lineId: string, payload: { orderedQuantity: number; reason: string }) =>
    unwrap<IPurchasingOrderDetail>(http.patch(`/kitchen-orders/${id}/approved-lines/${lineId}`, payload)),

  /** #234: quita una línea de un pedido aprobado con motivo obligatorio. */
  removeApprovedLine: (id: string, lineId: string, reason: string) =>
    unwrap<IPurchasingOrderDetail>(http.delete(`/kitchen-orders/${id}/approved-lines/${lineId}`, { data: { reason } })),

  replaceLine: (id: string, lineId: string, payload: { newArticleId: string; reason: string }) =>
    unwrap<IPurchasingOrderDetail>(http.post(`/kitchen-orders/${id}/lines/${lineId}/replace`, payload)),
};


export interface IPurchaseConsolidation {
  id: string;
  consolidationDate: string;
  /** ISSUE-146: rubro de una consolidación generada por aprobación; null en las manuales. */
  categoryId?: string | null;
  status: PurchaseConsolidationStatus;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface IConsolidationSourceBreakdownEntry {
  /** null = ítem agregado a mano para un site (#219). */
  kitchenOrderId: string | null;
  siteId: string;
  quantity: string;
  siteName: string;
  kitchenOrderVoucherNumber: string;
  kitchenOrderOrigin: KitchenOrderOrigin | null;
  /** #227: el pedido de origen se envió fuera de término. */
  sentOutOfWindow: boolean;
}

export interface IPurchaseConsolidationLine {
  id: string;
  consolidationId: string;
  articleId: string;
  articleDescription: string;
  /** #219: la bandeja agrupa por rubro. */
  categoryId: string | null;
  categoryName: string;
  totalRequestedQty: string;
  netQty: string | null;
  /** ISSUE-198: snapshot de `netQty` calculado por el sistema al consolidar, preservado aunque `netQty` se edite manualmente después. */
  theoreticalNetQty: string | null;
  /** ISSUE-198: null cuando el artículo no tiene presentación de compra estándar definida. */
  purchasePresentation: {
    name: string;
    content: string;
    fullBundles: number;
    remainder: string;
  } | null;
  sourceBreakdown: IConsolidationSourceBreakdownEntry[];
  /** ISSUE-206: al menos una fuente de esta línea es un pedido de cocina excepcional. */
  hasExceptionalSource: boolean;
  /** #227: al menos una fuente de esta línea es un pedido enviado fuera de término. */
  hasOutOfWindowSource: boolean;
  /** #232: proveedor con el que saldría la línea y su origen (override manual / principal del artículo / menor precio). */
  supplier: { id: string; name: string; email?: string | null; source: 'override' | 'primary' | 'cheapest' | 'usual' } | null;
  primarySupplierName: string | null;
  /** #232: el proveedor asignado a mano no tiene precio vigente — la OC sale con precio estimado. */
  supplierWithoutCurrentPrice: boolean;
  assignedSupplierId: string | null;
  supplierAssignedByUserId: string | null;
  supplierAssignedAt: string | null;
  /** #75: fecha en la que Compras va a comprar esta línea (reasignable por subconjunto). */
  purchaseDate: string;
  /** #75: fecha con la que pidió el cocinero — referencia inmutable. */
  originalRequestedDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface IConsolidationDetail {
  consolidation: IPurchaseConsolidation;
  lines: IPurchaseConsolidationLine[];
}

/** #201: filtro compartido por `generate` y `preview` — ausente/vacío = comportamiento actual (todos los sites, solo `approved`). */
export interface IConsolidationFilters {
  consolidationDate: string;
  /** Fin del rango (inclusive); ausente = solo `consolidationDate`. */
  consolidationDateTo?: string;
  siteIds?: string[];
  statuses?: KitchenOrderStatus[];
}

export interface IConsolidationSitePreview {
  siteId: string;
  siteName: string;
  orderCount: number;
  /** #227: cuántos de esos pedidos se enviaron fuera de término. */
  outOfWindowOrderCount: number;
  /** #302: cuántos de esos pedidos ya fueron volcados a una consolidación (el generar no los vuelve a tomar). */
  consolidatedOrderCount: number;
  articleCount: number;
  totalUnits: string;
}

/** #219: pedido de un site con sus líneas, para revisarlo antes de generar. */
export interface IConsolidationSiteOrderPreview {
  kitchenOrderId: string;
  voucherNumber: string;
  deliveryDate: string;
  status: string;
  sentOutOfWindow: boolean;
  lines: { articleId: string; articleDescription: string; categoryName: string; quantity: string }[];
}

export const purchaseConsolidationsApi = {
  list: () => unwrap<IPurchaseConsolidation[]>(http.get('/purchase-consolidations')),

  get: (id: string) => unwrap<IConsolidationDetail>(http.get(`/purchase-consolidations/${id}`)),

  generate: (filters: IConsolidationFilters) =>
    unwrap<IConsolidationDetail>(http.post('/purchase-consolidations/generate', filters)),

  /**
   * #201: aporte por site para los mismos filtros de `generate`, sin persistir nada.
   * `siteIds: []` (array vacío explícito, no ausente) se manda como `siteIds=''` — no se omite
   * el param — para que el backend (`toCsvArray` + `resolveEligibleOrders`) lo distinga de
   * "sin filtro de site" y no lo trate como "todos los sites".
   */
  preview: (filters: IConsolidationFilters) =>
    unwrap<IConsolidationSitePreview[]>(
      http.get('/purchase-consolidations/preview', {
        params: {
          consolidationDate: filters.consolidationDate,
          consolidationDateTo: filters.consolidationDateTo,
          siteIds: filters.siteIds !== undefined ? filters.siteIds.join(',') : undefined,
          statuses: filters.statuses?.length ? filters.statuses.join(',') : undefined,
        },
      }),
    ),

  /** #219: pedidos de un site (mismos filtros que `preview`) con sus líneas, sin persistir nada. */
  previewSite: (siteId: string, filters: IConsolidationFilters) =>
    unwrap<IConsolidationSiteOrderPreview[]>(
      http.get(`/purchase-consolidations/preview/site/${siteId}`, {
        params: {
          consolidationDate: filters.consolidationDate,
          consolidationDateTo: filters.consolidationDateTo,
          statuses: filters.statuses?.length ? filters.statuses.join(',') : undefined,
        },
      }),
    ),

  addLine: (consolidationId: string, payload: { articleId: string; quantity: number; siteId?: string }) =>
    unwrap<IPurchaseConsolidationLine>(http.post(`/purchase-consolidations/${consolidationId}/lines`, payload)),

  /** #219: quita un ítem agregado a mano de un borrador. */
  removeLine: (consolidationId: string, lineId: string) =>
    unwrap<void>(http.delete(`/purchase-consolidations/${consolidationId}/lines/${lineId}`)),

  /** #220: "Actualizar consolidación" — rehace el borrador con los pedidos vigentes de sus sites. */
  regenerate: (id: string, statuses?: KitchenOrderStatus[]) =>
    unwrap<IConsolidationDetail>(http.post(`/purchase-consolidations/${id}/regenerate`, { statuses })),

  updateLine: (consolidationId: string, lineId: string, netQty: number) =>
    unwrap<IPurchaseConsolidationLine>(
      http.patch(`/purchase-consolidations/${consolidationId}/lines/${lineId}`, { netQty }),
    ),

  /** #75: reasigna la fecha de compra de un subconjunto de líneas (por proveedor o selección manual de líneas) sin correr la del resto. */
  reassignPurchaseDate: (
    consolidationId: string,
    payload: { newPurchaseDate: string; supplierId?: string; lineItemIds?: string[] },
  ) => unwrap<IConsolidationDetail>(http.patch(`/purchase-consolidations/${consolidationId}/purchase-date`, payload)),

  /** #232: asigna (o quita con supplierId null) el proveedor de una o varias líneas en borrador. */
  assignLineSupplier: (consolidationId: string, payload: { lineItemIds: string[]; supplierId: string | null }) =>
    unwrap<IConsolidationDetail>(http.patch(`/purchase-consolidations/${consolidationId}/lines-supplier`, payload)),

  confirm: (id: string) => unwrap<IPurchaseConsolidation>(http.post(`/purchase-consolidations/${id}/confirm`)),

  /** #180: reabre una consolidación confirmada (vuelve a borrador) si no generó OC vigentes. */
  reopen: (id: string) => unwrap<IPurchaseConsolidation>(http.post(`/purchase-consolidations/${id}/reopen`)),
};

export type PurchaseOrderDeliveryLocation = 'warehouse' | 'site';

export interface IPurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  issueDate: string;
  deliveryDate: string;
  deliveryLocation: PurchaseOrderDeliveryLocation;
  paymentTerms: string;
  issuedByUserId: string;
  status: PurchaseOrderStatus;
  version: number;
  approvedByUserId: string | null;
  approvedAt: string | null;
  lastMovementAt: string | null;
  staleAlertSentAt: string | null;
  delayReason: string | null;
  delayReasonRecordedAt: string | null;
  /** #75: si la OC fue fusionada dentro de otra, id de la OC destino. */
  mergedIntoPurchaseOrderId: string | null;
  /** Fecha comprometida y confirmación del proveedor; si `confirmedOnBehalf`, la confirmó Compras (`confirmedByUserId`) por su cuenta (#280). */
  committedDeliveryDate: string | null;
  supplierConfirmedAt: string | null;
  confirmedByUserId: string | null;
  confirmedOnBehalf: boolean;
  confirmationChannel: string | null;
  confirmationNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IPurchaseOrderLine {
  id: string;
  purchaseOrderId: string;
  articleId: string;
  articleDescription: string;
  quantity: string;
  /** #74: cantidad neta consolidada original, previa al redondeo al bulto del proveedor. Null si la línea no viene de una consolidación. */
  theoreticalQuantity: string | null;
  /** #221: precio esperado interno; null = sin precio conocido (se define al recibir). */
  unitPrice: string | null;
  /** #221: el precio no es un precio vigente de proveedor (referencia, último precio o sin precio): la OC requiere aprobación gerencial. */
  priceEstimated: boolean;
  discount: string | null;
  vatRate: string;
  status: 'pending' | 'received' | 'partially_received' | 'cancelled';
}

export interface IPurchaseOrderDetail {
  order: IPurchaseOrder;
  lines: IPurchaseOrderLine[];
  total: number;
  /** #261: proveedor de la OC (nombre y email de envío). */
  supplier: { id: string; name: string; email: string | null } | null;
  confirmedByName: string | null;
  /** #310: fecha (YYYY-MM-DD) de la última recepción confirmada de la OC, o null. */
  lastReceptionDate?: string | null;
}

/** ISSUE-222: el listado de OC trae el nombre del proveedor (quien recibe identifica por proveedor). */
export interface IPurchaseOrderListItem extends IPurchaseOrder {
  supplierName: string;
}

export const purchaseOrdersApi = {
  list: (filters: { supplierId?: string; status?: PurchaseOrderStatus; statuses?: PurchaseOrderStatus[]; invoiceable?: boolean } = {}) => {
    const { statuses, invoiceable, ...rest } = filters;
    return unwrap<IPurchaseOrderListItem[]>(
      http.get('/purchase-orders', {
        params: { ...rest, statuses: statuses?.length ? statuses.join(',') : undefined, invoiceable: invoiceable || undefined },
      }),
    );
  },

  get: (id: string) => unwrap<IPurchaseOrderDetail>(http.get(`/purchase-orders/${id}`)),

  generateFromConsolidation: (payload: { consolidationId: string; overrideMinOrderAmount?: boolean }) =>
    unwrap<IPurchaseOrderDetail[]>(http.post('/purchase-orders/generate-from-consolidation', payload)),

  /** #75: fusiona 2+ OC emitidas del mismo proveedor en una sola (la más antigua queda como destino). */
  merge: (sourcePurchaseOrderIds: string[]) =>
    unwrap<IPurchaseOrderDetail>(http.post('/purchase-orders/merge', { sourcePurchaseOrderIds })),

  approve: (id: string) => unwrap<IPurchaseOrder>(http.post(`/purchase-orders/${id}/approve`)),

  /** #74: edita manualmente la cantidad de una línea (ej. corregir el redondeo automático al bulto). Sin validación de múltiplo. */
  updateLineQuantity: (lineId: string, quantity: number) =>
    unwrap<IPurchaseOrderLine>(http.patch(`/purchase-orders/lines/${lineId}/quantity`, { quantity })),

  cancel: (id: string) => unwrap<IPurchaseOrder>(http.post(`/purchase-orders/${id}/cancel`)),
  /** #280: Compras confirma la OC en nombre del proveedor. */
  confirm: (id: string, payload: { committedDeliveryDate?: string; channel?: string; note?: string }) =>
    unwrap<IPurchaseOrder>(http.post(`/purchase-orders/${id}/confirm`, payload)),

  registerDelayReason: (id: string, reason: string) =>
    unwrap<IPurchaseOrder>(http.patch(`/purchase-orders/${id}/delay-reason`, { reason })),

  /** Descarga el PDF (sin precios) de una OC aprobada. NO avisa al proveedor: se puede bajar las veces que haga falta. */
  downloadPdf: (id: string) => http.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' }),

  /** Avisa al proveedor por mail/WhatsApp (best-effort). Cada llamada vuelve a notificar; devuelve qué canales salieron. */
  send: (id: string) =>
    unwrap<{ order: IPurchaseOrder; emailSent: boolean; emailError: string | null; whatsappSent: boolean }>(http.post(`/purchase-orders/${id}/send`)),
};

// ---- Recepción Depósito Central (#26) ----


export interface IReception {
  id: string;
  receptionNumber: string;
  purchaseOrderId: string | null;
  supplierId: string;
  invoiceOrDeliveryNote: string;
  receptionDate: string;
  receivedByUserId: string;
  destinationWarehouseId: string;
  status: ReceptionStatus;
}

export interface IReceptionLine {
  id: string;
  receptionId: string;
  purchaseOrderLineId: string | null;
  articleId: string;
  orderedQty: string | null;
  receivedQty: string;
  batch: string | null;
  expirationDate: string | null;
  unitCost: string;
  /** #221: variación % del precio recibido vs. el esperado de la OC (null si no había esperado). */
  priceVariationPct: string | null;
  priceAlert: boolean;
  differenceNote: string | null;
}

export interface IReceptionDetail {
  reception: IReception;
  lines: IReceptionLine[];
  priceAlertThresholdPct: number;
}

export interface IPendingPurchaseOrderLine {
  id: string;
  articleId: string;
  quantity: string;
  receivedQty: string;
  pendingQty: string;
  unitPrice: string | null;
}

export interface ICreateWarehouseReceptionLinePayload {
  articleId: string;
  purchaseOrderLineId?: string;
  orderedQty?: number;
  receivedQty: number;
  batch?: string;
  expirationDate?: string;
  unitCost: number;
  differenceNote?: string;
}

export interface ICreateWarehouseReceptionPayload {
  purchaseOrderId?: string;
  supplierId: string;
  invoiceOrDeliveryNote: string;
  receptionDate?: string;
  destinationWarehouseId: string;
  lines: ICreateWarehouseReceptionLinePayload[];
}

export const warehouseReceptionsApi = {
  list: () => unwrap<IReception[]>(http.get('/warehouse-receptions')),

  get: (id: string) => unwrap<IReceptionDetail>(http.get(`/warehouse-receptions/${id}`)),
  priceAlertThreshold: () => unwrap<{ thresholdPct: number }>(http.get('/warehouse-receptions/price-alert-threshold')),

  /** ISSUE-222: lo que TODAVÍA falta recibir de la OC (excluye líneas completas/canceladas; `pendingQty` ya descuenta lo recibido). */
  prefillFromPurchaseOrder: (purchaseOrderId: string) =>
    unwrap<IPendingPurchaseOrderLine[]>(http.get(`/warehouse-receptions/purchase-order/${purchaseOrderId}/lines`)),

  create: (payload: ICreateWarehouseReceptionPayload) =>
    unwrap<IReceptionDetail>(http.post('/warehouse-receptions', payload)),

  confirm: (id: string) => unwrap<IReception>(http.post(`/warehouse-receptions/${id}/confirm`)),
};

// ---- Picking y Hoja de Ruta (#27) ----


export interface IPickingRoute {
  id: string;
  routeNumber: string;
  routeDate: string;
  vehicleName: string | null;
  driverName: string | null;
  warehouseId: string;
  status: PickingRouteStatus;
}

export type DeliveryStatus = 'pending' | 'delivered' | 'partial' | 'not_delivered';

export interface IPickingRouteLine {
  id: string;
  pickingRouteId: string;
  siteId: string;
  articleId: string;
  quantity: string;
  pickedQty: string | null;
  suggestedBatch: string | null;
  suggestedExpirationDate: string | null;
  checklistConfirmed: boolean;
  kitchenOrderId: string | null;
  deliveryStatus: DeliveryStatus;
  deliveredQty: string | null;
  deliveryNote: string | null;
}

/** #235 — resultado de la entrega de una línea (pedido vs. entregado + nota). */
export interface IDeliverySheetLine {
  id: string;
  routeNumber: string;
  articleId: string;
  articleDescription: string;
  orderedQty: number;
  deliveredQty: number | null;
  status: DeliveryStatus;
  note: string | null;
}

export interface IDeliverySheet {
  siteId: string;
  siteName: string;
  routeDate: string;
  lines: IDeliverySheetLine[];
  totals: Record<DeliveryStatus, number>;
}

export interface IPickingRouteDetail {
  route: IPickingRoute;
  lines: IPickingRouteLine[];
}

export const pickingRoutesApi = {
  list: () => unwrap<IPickingRoute[]>(http.get('/picking-routes')),

  get: (id: string) => unwrap<IPickingRouteDetail>(http.get(`/picking-routes/${id}`)),

  generate: (payload: {
    routeDate: string;
    siteIds: string[];
    warehouseId: string;
    vehicleName?: string;
    driverName?: string;
  }) => unwrap<IPickingRouteDetail>(http.post('/picking-routes/generate', payload)),

  pickLine: (lineId: string, pickedQty: number) =>
    unwrap<IPickingRouteLine>(http.patch(`/picking-routes/lines/${lineId}/pick`, { pickedQty })),

  dispatch: (id: string) => unwrap<IPickingRoute>(http.post(`/picking-routes/${id}/dispatch`)),

  deliverLine: (lineId: string, payload: { status: Exclude<DeliveryStatus, 'pending'>; deliveredQty?: number; note?: string }) =>
    unwrap<IPickingRouteLine>(http.patch(`/picking-routes/lines/${lineId}/deliver`, payload)),

  deliveries: (date: string, siteId?: string) => unwrap<IDeliverySheet[]>(http.get('/picking-routes/deliveries', { params: { date, siteId } })),

  deliveriesPdf: (date: string, siteId: string) =>
    http.get('/picking-routes/deliveries/pdf', { params: { date, siteId }, responseType: 'blob' }),
};

// ---- Recepción en Cocina (#28) ----


export interface IKitchenReception {
  id: string;
  siteId: string;
  pickingRouteId: string | null;
  supplierId: string | null;
  receptionDate: string;
  receivedByUserId: string;
  status: KitchenReceptionStatus;
}

export interface IKitchenReceptionLine {
  id: string;
  kitchenReceptionId: string;
  pickingRouteLineId: string | null;
  articleId: string;
  expectedQty: string;
  receivedQty: string;
  missingQty: string;
  urgencyLevel: 'normal' | 'urgent' | null;
  photoUrl: string | null;
  batch: string | null;
  expirationDate: string | null;
  unitCost: string | null;
  /** ISSUE-223: mercadería en mal estado, declarada por quien recibe. */
  badCondition: boolean;
  conditionNote: string | null;
}

export interface IKitchenReceptionDetail {
  reception: IKitchenReception;
  lines: IKitchenReceptionLine[];
}

export interface ICreateKitchenReceptionLinePayload {
  articleId: string;
  pickingRouteLineId?: string;
  expectedQty: number;
  receivedQty: number;
  urgencyLevel?: 'normal' | 'urgent';
  photoUrl?: string;
  batch?: string;
  expirationDate?: string;
  unitCost?: number;
  badCondition?: boolean;
  conditionNote?: string;
}

export interface ICreateKitchenReceptionPayload {
  siteId: string;
  pickingRouteId?: string;
  supplierId?: string;
  receptionDate?: string;
  lines: ICreateKitchenReceptionLinePayload[];
}

export const kitchenReceptionsApi = {
  list: () => unwrap<IKitchenReception[]>(http.get('/kitchen-receptions')),

  get: (id: string) => unwrap<IKitchenReceptionDetail>(http.get(`/kitchen-receptions/${id}`)),

  create: (payload: ICreateKitchenReceptionPayload) =>
    unwrap<IKitchenReceptionDetail>(http.post('/kitchen-receptions', payload)),

  confirm: (id: string) => unwrap<IKitchenReception>(http.post(`/kitchen-receptions/${id}/confirm`)),
};

// ---- Facturas de compra / Validación 3 puntas (ISSUE-501, #34) ----

export type PurchaseVoucherType = 'A' | 'B' | 'C' | 'NC';

export interface IPurchaseInvoice {
  id: string;
  supplierId: string;
  voucherType: PurchaseVoucherType;
  salesPoint: string;
  number: string;
  issueDate: string;
  dueDate: string;
  netAmount: string;
  totalVat: string;
  /** #312: percepciones del proveedor, ya incluidas en `totalAmount`. */
  totalPerceptions: string;
  totalAmount: string;
  expenseCategory: string;
  purchaseOrderIds: string[];
  /** #265: compra fuera de circuito (proveedor genérico «varios») — pasible de auditoría. */
  auditFlag: boolean;
  genericSupplierName: string | null;
  genericSupplierTaxId: string | null;
  genericSupplierReason: string | null;
  /** Archivo del comprobante adjunto (se descarga con `purchaseInvoicesApi.voucherFile`). */
  voucherFileName: string | null;
  createdByUserId: string | null;
  status: PurchaseInvoiceStatus;
  validatedByUserId: string | null;
  validatedAt: string | null;
  /** Nombre legible del proveedor (#275). */
  supplierName: string;
  /** Orden de Pago vigente (no anulada) que ya incluye la factura (#276); la propuesta/orden se identifica por la fecha de su propuesta. */
  paymentOrder: { id: string; status: PaymentOrderStatus; proposalId: string; proposalDate: string } | null;
  /** Propuesta de pago en borrador que incluye la factura (#276); `null` si ya tiene Orden de Pago. */
  paymentProposal: { id: string; proposalDate: string } | null;
}

export interface IPurchaseInvoiceLine {
  id: string;
  purchaseInvoiceId: string;
  purchaseOrderLineId: string | null;
  /** `null` = ítem no stockeable (#264): envío, servicio o cargo extra. */
  articleId: string | null;
  description: string | null;
  articleDescription: string;
  invoicedQty: string;
  unitPrice: string;
  vatRate: string;
  siteAllocations: Record<string, number>;
  matchStatus: PurchaseInvoiceLineMatchStatus;
}

export interface IPurchaseInvoiceVatBreakdownEntry {
  rate: number;
  taxableBase: number;
  vatAmount: number;
}

/** #312: tipos de impuesto de las percepciones del proveedor. */
export type PerceptionTaxType = 'iibb' | 'iva' | 'ganancias' | 'other';

export const PERCEPTION_TAX_TYPE_LABELS: Record<PerceptionTaxType, string> = {
  iibb: 'IIBB',
  iva: 'IVA',
  ganancias: 'Ganancias',
  other: 'Otra',
};

export interface IPurchaseInvoicePerception {
  id: string;
  purchaseInvoiceId: string;
  taxType: PerceptionTaxType;
  label: string | null;
  rate: string;
  baseAmount: string;
  amount: string;
}

export interface IPurchaseInvoiceDetail {
  invoice: IPurchaseInvoice;
  lines: IPurchaseInvoiceLine[];
  vatBreakdown: IPurchaseInvoiceVatBreakdownEntry[];
  /** #312: percepciones incluidas en `invoice.totalAmount`. */
  perceptions: IPurchaseInvoicePerception[];
}

export interface ICreatePurchaseInvoicePerceptionPayload {
  taxType: PerceptionTaxType;
  label?: string;
  /** Alícuota en % sobre el neto. */
  rate: number;
}

export interface ICreatePurchaseInvoiceLinePayload {
  purchaseOrderLineId?: string;
  /** Vacío = ítem no stockeable (#264), requiere `description`. */
  articleId?: string;
  description?: string;
  invoicedQty: number;
  unitPrice: number;
  vatRate: PurchaseInvoiceVatRate;
}

export interface ICreatePurchaseInvoicePayload {
  supplierId: string;
  voucherType: PurchaseVoucherType;
  salesPoint: string;
  number: string;
  issueDate: string;
  dueDate: string;
  expenseCategory: string;
  /** #265: datos mínimos del vendedor real, obligatorios si el proveedor es el genérico «varios». */
  genericSupplierName?: string;
  genericSupplierTaxId?: string;
  genericSupplierReason?: string;
  lines: ICreatePurchaseInvoiceLinePayload[];
  /** #312: percepciones (alícuota sobre el neto) que suman al total. */
  perceptions?: ICreatePurchaseInvoicePerceptionPayload[];
}

export const purchaseInvoicesApi = {
  list: (filters: { supplierId?: string; status?: PurchaseInvoiceStatus; number?: string; auditFlag?: boolean } = {}) =>
    unwrap<IPurchaseInvoice[]>(http.get('/purchase-invoices', { params: filters })),

  get: (id: string) => unwrap<IPurchaseInvoiceDetail>(http.get(`/purchase-invoices/${id}`)),

  create: (payload: ICreatePurchaseInvoicePayload) =>
    unwrap<IPurchaseInvoiceDetail>(http.post('/purchase-invoices', payload)),

  validate: (id: string) => unwrap<IPurchaseInvoice>(http.patch(`/purchase-invoices/${id}/validate`)),

  /** #265: adjunta el archivo del comprobante (obligatorio para validar una compra fuera de circuito). */
  attachVoucherFile: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return unwrap<IPurchaseInvoice>(http.post(`/purchase-invoices/${id}/voucher-file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
  },

  voucherFile: (id: string) => http.get(`/purchase-invoices/${id}/voucher-file`, { responseType: 'blob' }).then((r) => r.data as Blob),
};

// ---- Cuentas Corrientes (ISSUE-502, #35) ----

export type AccountLedgerPartyType = 'client' | 'supplier';
export type AccountLedgerEntryType = 'invoice' | 'payment' | 'manual_adjustment';
export type AccountLedgerSourceDocumentType = 'purchase_invoice' | 'sales_invoice' | 'supplier_payment' | 'client_receipt' | 'manual';

export interface IAccountLedgerEntry {
  id: string;
  partyType: AccountLedgerPartyType;
  partyId: string;
  entryType: AccountLedgerEntryType;
  amount: string;
  sourceDocumentType: AccountLedgerSourceDocumentType;
  sourceDocumentId: string;
  balanceAfter: string;
  createdByUserId: string;
  createdAt: string;
  /** ISSUE-107 — fecha de vencimiento del comprobante origen (`null` salvo comprobantes de venta con vencimiento cargado). */
  dueDate: string | null;
}

export interface IAccountLedgerEntriesPage {
  items: IAccountLedgerEntry[];
  total: number;
  page: number;
  limit: number;
}

/** ISSUE-107 — desglose del saldo del cliente entre vencido y a vencer a una fecha de corte. */
export interface IClientOverdue {
  asOf: string;
  balance: number;
  overdueBalance: number;
  notDueBalance: number;
}

export interface ISupplierBalance {
  supplierId: string;
  supplierName: string;
  taxId: string | null;
  balance: number;
}

export interface ISupplierBalancesPage {
  items: ISupplierBalance[];
  total: number;
  page: number;
  limit: number;
  /** Suma de los saldos de todas las cuentas del listado, no solo de la página. */
  totalBalance: number;
}

export const accountLedgerApi = {
  listSupplierBalances: (params: { supplierId?: string; sort?: 'name' | 'balance'; page?: number; limit?: number } = {}) =>
    unwrap<ISupplierBalancesPage>(http.get('/account-ledger/suppliers/balances', { params })),

  getEntries: (
    partyType: AccountLedgerPartyType,
    partyId: string,
    filters: { page?: number; limit?: number; from?: string; to?: string; onlyOverdue?: boolean } = {},
  ) => unwrap<IAccountLedgerEntriesPage>(http.get(`/account-ledger/${partyType}/${partyId}`, { params: filters })),

  getBalance: (partyType: AccountLedgerPartyType, partyId: string) =>
    unwrap<{ balance: number }>(http.get(`/account-ledger/${partyType}/${partyId}/balance`)),

  getClientOverdue: (clientId: string, asOf?: string) =>
    unwrap<IClientOverdue>(http.get(`/account-ledger/client/${clientId}/overdue`, { params: asOf ? { asOf } : {} })),
};

export type PaymentMethod = 'transferencia' | 'efectivo' | 'cheque' | 'echeque' | 'tarjeta' | 'otro';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  transferencia: 'Transferencia',
  efectivo: 'Efectivo',
  cheque: 'Cheque',
  echeque: 'eCheque',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

export interface ISupplierPayment {
  id: string;
  supplierId: string;
  paymentOrderId: string;
  bankAccountId: string;
  paymentDate: string;
  amount: string;
  paymentMethod: PaymentMethod;
  appliedInvoiceIds: string[];
  referenceNumber: string | null;
  /** #267: datos del cheque / eCheque (completables después del pago). */
  checkNumber: string | null;
  checkIssueDate: string | null;
  checkDueDate: string | null;
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
}

export interface ICreateSupplierPaymentPayload {
  supplierId: string;
  paymentOrderId: string;
  bankAccountId: string;
  paymentDate: string;
  amount: number;
  paymentMethod: PaymentMethod;
  appliedInvoiceIds: string[];
  referenceNumber?: string;
  checkNumber?: string;
  checkIssueDate?: string;
  checkDueDate?: string;
  notes?: string;
}

export interface IUpdateSupplierPaymentDetailsPayload {
  referenceNumber?: string;
  checkNumber?: string;
  checkIssueDate?: string;
  checkDueDate?: string;
}

export const supplierPaymentsApi = {
  list: (filters: { supplierId?: string; paymentOrderId?: string } = {}) => unwrap<ISupplierPayment[]>(http.get('/supplier-payments', { params: filters })),

  /** #267: completa con posterioridad los datos del medio de pago (auditado). */
  completeDetails: (id: string, payload: IUpdateSupplierPaymentDetailsPayload) =>
    unwrap<ISupplierPayment>(http.patch('/supplier-payments/' + id + '/details', payload)),

  create: (payload: ICreateSupplierPaymentPayload) => unwrap<ISupplierPayment>(http.post('/supplier-payments', payload)),
};

// ---- Cuentas bancarias/caja (ISSUE-504, #37) ----

export type BankAccountType = 'bank' | 'cash';

export interface IBankAccount {
  id: string;
  name: string;
  type: BankAccountType;
  currentBalance: string;
  active: boolean;
  createdAt: string;
}

export interface ICreateBankAccountPayload {
  name: string;
  type: BankAccountType;
  initialBalance?: number;
}

/** #269: cheque emitido con fecha de cobro futura, todavía no debitado de la cuenta. */
export interface ICashFlowCheck {
  paymentId: string;
  paymentOrderId: string;
  supplierName: string;
  checkNumber: string | null;
  amount: string;
}

export interface ICashFlowAccount {
  accountId: string;
  name: string;
  type: BankAccountType;
  /** Saldo contable: ya descuenta los cheques emitidos que el banco todavía no debitó. */
  currentBalance: string;
  pendingChecksTotal: string;
  /** Saldo bancario estimado hoy (`currentBalance` + cheques pendientes de cobro). */
  balanceToday: string;
  /** Cheques/eCheques sin fecha de cobro cargada (no entran en la proyección). */
  checksWithoutDueDate: number;
  dueDates: { date: string; total: string; projectedBalance: string; checks: ICashFlowCheck[] }[];
}

export const bankAccountsApi = {
  list: () => unwrap<IBankAccount[]>(http.get('/bank-accounts')),

  /** #269: vencimientos de cheques y saldo proyectado por cuenta. */
  cashFlow: () => unwrap<ICashFlowAccount[]>(http.get('/bank-accounts/cash-flow')),

  create: (payload: ICreateBankAccountPayload) => unwrap<IBankAccount>(http.post('/bank-accounts', payload)),
};

/**
 * Tipos de comprobante de venta. Incluye la Factura de Crédito Electrónica
 * MiPyMEs (`FCE_A | FCE_B | FCE_C`, ISSUE-105). Separado de
 * `PurchaseVoucherType` a propósito: la FCE es de emisión propia, no aplica
 * a `purchase-invoices`.
 */
export type SalesVoucherType = PurchaseVoucherType | 'FCE_A' | 'FCE_B' | 'FCE_C';

export interface ISalesInvoice {
  id: string;
  clientId: string;
  voucherType: SalesVoucherType;
  salesPoint: string;
  number: string;
  issueDate: string;
  /** ISSUE-107 — plazo de pago en días desde `issueDate` (atajo de carga). */
  paymentTermDays: number | null;
  /** ISSUE-107 — fecha de vencimiento efectiva; `null` ⇒ el comprobante no vence. */
  dueDate: string | null;
  totalAmount: string;
  description: string | null;
  createdByUserId: string;
  createdAt: string;
}

export interface ICreateSalesInvoicePayload {
  clientId: string;
  voucherType: SalesVoucherType;
  salesPoint: string;
  number: string;
  issueDate: string;
  totalAmount: number;
  description?: string;
  /** ISSUE-107 — plazo en días desde emisión; si va sin `dueDate`, el backend deriva la fecha. Ignorado para `NC`. */
  paymentTermDays?: number;
  /** ISSUE-107 — fecha de vencimiento explícita (gana sobre el plazo). Ignorada para `NC`. */
  dueDate?: string;
}

/** ISSUE-107 — sólo se puede corregir el vencimiento de un comprobante ya cargado. */
export interface IUpdateSalesInvoicePayload {
  paymentTermDays?: number;
  dueDate?: string;
}

export interface ISalesInvoiceImportRow {
  row: number;
  clientId: string | null;
  voucherType: SalesVoucherType | null;
  salesPoint: string | null;
  number: string | null;
  issueDate: string | null;
  totalAmount: string | null;
  errors: { field?: string; message: string }[];
}

export interface ISalesInvoiceImportPreview {
  batchId: string;
  fileName: string;
  expiresAt: string;
  summary: { valid: number; errors: number };
  rows: ISalesInvoiceImportRow[];
}

export interface ISalesInvoiceImportConfirmResult {
  batchId: string;
  applied: number;
  failed: number;
  rows: { row: number; status: 'applied' | 'failed' | 'skipped'; error?: string }[];
}

export const salesInvoicesApi = {
  list: (filters: { clientId?: string } = {}) => unwrap<ISalesInvoice[]>(http.get('/sales-invoices', { params: filters })),

  create: (payload: ICreateSalesInvoicePayload) => unwrap<ISalesInvoice>(http.post('/sales-invoices', payload)),

  update: (id: string, payload: IUpdateSalesInvoicePayload) =>
    unwrap<ISalesInvoice>(http.patch(`/sales-invoices/${id}`, payload)),

  previewImport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return unwrap<ISalesInvoiceImportPreview>(
      http.post('/sales-invoices/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  confirmImport: (batchId: string) =>
    unwrap<ISalesInvoiceImportConfirmResult>(http.post(`/sales-invoices/import/${batchId}/confirm`, {})),

  downloadTemplate: () =>
    http.get('/sales-invoices/import/template', { responseType: 'blob' }).then((r) => r.data as Blob),
};

export interface IClientReceipt {
  id: string;
  clientId: string;
  receiptDate: string;
  amount: string;
  paymentMethod: PaymentMethod;
  appliedInvoiceIds: string[];
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
}

export interface IClientReceiptDetail extends IClientReceipt {
  createdByName: string | null;
  appliedInvoices: { id: string; voucherType: SalesVoucherType; salesPoint: string; number: string; totalAmount: string; issueDate: string; dueDate: string | null }[];
}

export interface ICreateClientReceiptPayload {
  clientId: string;
  receiptDate: string;
  amount: number;
  paymentMethod: PaymentMethod;
  appliedInvoiceIds: string[];
  notes?: string;
}

export const clientReceiptsApi = {
  list: (filters: { clientId?: string } = {}) => unwrap<IClientReceipt[]>(http.get('/client-receipts', { params: filters })),

  create: (payload: ICreateClientReceiptPayload) => unwrap<IClientReceipt>(http.post('/client-receipts', payload)),

  /** Fila 84: detalle del cobro con los comprobantes que cancela. */
  get: (id: string) => unwrap<IClientReceiptDetail>(http.get(`/client-receipts/${id}`)),
};

// ---- Propuesta Automática de Pagos (ISSUE-503, #36) ----


export interface IPaymentProposal {
  id: string;
  proposalDate: string;
  status: PaymentProposalStatus;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  /** Sólo en el listado: proveedores de las facturas incluidas y Orden de Pago vigente generada, si la hay. */
  supplierNames?: string[];
  orderId?: string | null;
  /** Sólo en el listado: cantidad de ítems (una propuesta en borrador con 0 se puede eliminar). */
  itemsCount?: number;
}

export interface IWithholdingLine {
  id: string;
  withholdingRuleId: string;
  amount: string;
}

/** Respaldo de un pago sin factura (#266). */
export type PaymentSupportType = 'remito' | 'otro';
export const PAYMENT_SUPPORT_TYPE_LABELS: Record<PaymentSupportType, string> = { remito: 'Remito', otro: 'Otro respaldo' };

export interface IPaymentProposalItem {
  id: string;
  proposalId: string;
  /** `null` = pago sin factura (#266). */
  invoiceId: string | null;
  supplierId: string | null;
  supportType: PaymentSupportType | null;
  supportReference: string | null;
  /** Nombre del archivo de respaldo adjunto (se descarga con `paymentProposalsApi.supportFile`/`orderSupportFile`). */
  supportFileName: string | null;
  amount: string;
  withholdingAmount: string;
  withholdingRuleId: string | null;
  /** Presente cuando el ítem viene de `get`/`getOrder`; los endpoints de alta/edición devuelven el ítem sin este desglose (refetch para verlo). */
  withholdingLines?: IWithholdingLine[];
  /** Sólo en `get`: etiqueta de la factura y nombre del proveedor. */
  invoiceLabel?: string;
  supplierName?: string;
}

export interface IPaymentProposalDetail {
  proposal: IPaymentProposal;
  items: IPaymentProposalItem[];
  /** Sólo en `get`: Orden de Pago vigente generada por la propuesta. */
  orderId?: string | null;
}

export interface IPaymentOrder {
  id: string;
  proposalId: string;
  status: PaymentOrderStatus;
  grossAmount: string;
  withheldAmount: string;
  netAmount: string;
  confirmedByUserId: string;
  authorizedByUserId: string | null;
  authorizedAt: string | null;
  /** #268: último pedido de cambios de Gerencia sobre esta OP. */
  changesRequestedByUserId: string | null;
  changesRequestedAt: string | null;
  changesRequestComment: string | null;
  /** #268: OP que esta reemplaza tras corregirse la propuesta. */
  previousOrderId: string | null;
  createdAt: string;
  /** Sólo en el listado: proveedores de los ítems y fecha de la propuesta de origen. */
  supplierNames?: string[];
  proposalDate?: string | null;
}

export interface IPaymentOrderItem {
  id: string;
  orderId: string;
  /** `null` = pago sin factura (#266). */
  invoiceId: string | null;
  supplierId: string;
  supportType: PaymentSupportType | null;
  supportReference: string | null;
  /** Nombre del archivo de respaldo adjunto (se descarga con `paymentProposalsApi.supportFile`/`orderSupportFile`). */
  supportFileName: string | null;
  amount: string;
  withholdingAmount: string;
  withholdingRuleId: string | null;
  /** Presente cuando el ítem viene de `get`/`getOrder`; los endpoints de alta/edición devuelven el ítem sin este desglose (refetch para verlo). */
  withholdingLines?: IWithholdingLine[];
  invoiceLabel?: string;
  supplierName?: string;
}

export interface IPaymentOrderDetail {
  order: IPaymentOrder;
  /** #268: OPs anteriores encadenadas (la más reciente primero), con el pedido de cambios de cada una. */
  history?: IPaymentOrder[];
  items: IPaymentOrderItem[];
  proposalDate?: string | null;
}

export type WithholdingRuleTaxType = 'iibb' | 'ganancias' | 'iva' | 'suss' | 'other';

export const WITHHOLDING_RULE_TAX_TYPE_LABELS: Record<WithholdingRuleTaxType, string> = {
  iibb: 'IIBB',
  ganancias: 'Ganancias',
  iva: 'IVA',
  suss: 'SUSS',
  other: 'Otra',
};

export interface IWithholdingRule {
  id: string;
  taxCondition: string;
  taxType: WithholdingRuleTaxType;
  rate: string;
  minAmount: string | null;
  maxAmount: string | null;
  active: boolean;
}

export interface ICreateWithholdingRulePayload {
  taxCondition: string;
  taxType?: WithholdingRuleTaxType;
  rate: number;
  minAmount?: number;
  maxAmount?: number;
  active?: boolean;
}

export const paymentProposalsApi = {
  list: () => unwrap<IPaymentProposal[]>(http.get('/payment-proposals')),

  get: (id: string) => unwrap<IPaymentProposalDetail>(http.get(`/payment-proposals/${id}`)),

  generate: (proposalDate: string) => unwrap<IPaymentProposalDetail>(http.post('/payment-proposals', { proposalDate })),

  /** #274: solo propuestas en borrador y sin ítems. */
  remove: (proposalId: string) => unwrap<void>(http.delete(`/payment-proposals/${proposalId}`)),

  /** #277: facturas que se pueden agregar (validadas, sin OP activa, no incluidas), con búsqueda server-side. */

  addItem: (proposalId: string, invoiceId: string, amount?: number) =>
    unwrap<IPaymentProposalItem>(http.post(`/payment-proposals/${proposalId}/items`, { invoiceId, amount })),

  /** #266: ítem sin factura — solo proveedores con «pago sin factura» habilitado, con su respaldo. */
  addItemWithoutInvoice: (
    proposalId: string,
    payload: { supplierId: string; amount: number; supportType: PaymentSupportType; supportReference: string; supportFile: File },
  ) => {
    const fd = new FormData();
    fd.append('supplierId', payload.supplierId);
    fd.append('amount', String(payload.amount));
    fd.append('supportType', payload.supportType);
    fd.append('supportReference', payload.supportReference);
    fd.append('supportFile', payload.supportFile, payload.supportFile.name);
    return unwrap<IPaymentProposalItem>(
      http.post(`/payment-proposals/${proposalId}/items`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
    );
  },

  /** #266: archivo de respaldo de un ítem sin factura de la propuesta. */
  supportFile: (proposalId: string, itemId: string) =>
    http.get(`/payment-proposals/${proposalId}/items/${itemId}/support-file`, { responseType: 'blob' }).then((r) => r.data as Blob),

  /** #266: ídem para un ítem de la Orden de Pago. */
  orderSupportFile: (orderId: string, itemId: string) =>
    http.get(`/payment-proposals/orders/${orderId}/items/${itemId}/support-file`, { responseType: 'blob' }).then((r) => r.data as Blob),

  updateItem: (proposalId: string, itemId: string, amount: number) =>
    unwrap<IPaymentProposalItem>(http.post(`/payment-proposals/${proposalId}/items/${itemId}`, { amount })),

  removeItem: (proposalId: string, itemId: string) => unwrap<void>(http.delete(`/payment-proposals/${proposalId}/items/${itemId}`)),

  confirm: (proposalId: string) => unwrap<IPaymentOrderDetail>(http.post(`/payment-proposals/${proposalId}/confirm`)),

  listOrders: () => unwrap<IPaymentOrder[]>(http.get('/payment-proposals/orders')),

  getOrder: (id: string) => unwrap<IPaymentOrderDetail>(http.get(`/payment-proposals/orders/${id}`)),

  authorizeOrder: (id: string) => unwrap<IPaymentOrder>(http.post(`/payment-proposals/orders/${id}/authorize`)),

  /** #268: Gerencia pide cambios sobre una OP pendiente. */
  requestOrderChanges: (id: string, comment: string) =>
    unwrap<IPaymentOrder>(http.post(`/payment-proposals/orders/${id}/request-changes`, { comment })),

  /** #268: Administración reabre la propuesta de la OP con cambios pedidos; devuelve la propuesta a corregir. */
  reopenOrder: (id: string) => unwrap<{ proposalId: string }>(http.post(`/payment-proposals/orders/${id}/reopen`)),

  cancelOrder: (id: string) => unwrap<IPaymentOrder>(http.post(`/payment-proposals/orders/${id}/cancel`)),

  listWithholdingRules: () => unwrap<IWithholdingRule[]>(http.get('/payment-proposals/withholding-rules')),

  createWithholdingRule: (payload: ICreateWithholdingRulePayload) =>
    unwrap<IWithholdingRule>(http.post('/payment-proposals/withholding-rules', payload)),
};

// ---- Preliquidación de Facturación a Clientes (ISSUE-505) ----


export interface IPreliquidacion {
  id: string;
  siteId: string;
  serviceType: string;
  periodStart: string;
  periodEnd: string;
  status: PreliquidacionStatus;
  /** Moneda de los acuerdos usados en el cálculo (nunca mezcla ARS y USD). */
  currency: 'ARS' | 'USD';
  calculatedAmount: string;
  salesInvoiceId: string | null;
  invoiceAmountDifference: string | null;
  excludedDates: string[];
  createdByUserId: string;
  closedByUserId: string | null;
  closedAt: string | null;
  createdAt: string;
}

export interface IPreliquidacionItem {
  id: string;
  preliquidacionId: string;
  serviceDate: string;
  servedPortions: number;
  unitValue: string;
  amount: string;
  commercialAgreementId: string | null;
}

export interface IPreliquidacionDetail {
  preliquidacion: IPreliquidacion;
  items: IPreliquidacionItem[];
  /** Cliente al que se le factura (derivado del site) — acota el selector de facturas a asociar. */
  clientId: string;
}

export interface IGeneratePreliquidacionPayload {
  siteId: string;
  serviceType: string;
  periodStart: string;
  periodEnd: string;
}

export const preliquidacionesApi = {
  list: (query: IPaginationQuery = {}) =>
    unwrap<IPaginatedResult<IPreliquidacion>>(http.get('/preliquidaciones', { params: query })),

  get: (id: string) => unwrap<IPreliquidacionDetail>(http.get(`/preliquidaciones/${id}`)),

  generate: (payload: IGeneratePreliquidacionPayload) =>
    unwrap<{ preliquidacion: IPreliquidacion; items: IPreliquidacionItem[]; excludedDates: string[] }>(http.post('/preliquidaciones', payload)),

  /** `reason` es obligatorio (queda auditado); un ajuste de más del 50% sobre lo calculado exige `confirmLargeAdjustment`. */
  updateItem: (preliquidacionId: string, itemId: string, payload: { amount: number; reason: string; confirmLargeAdjustment?: boolean }) =>
    unwrap<IPreliquidacionItem>(http.post(`/preliquidaciones/${preliquidacionId}/items/${itemId}`, payload)),

  associateSalesInvoice: (id: string, salesInvoiceId: string, confirmDifference?: boolean) =>
    unwrap<IPreliquidacion>(http.post(`/preliquidaciones/${id}/sales-invoice`, { salesInvoiceId, confirmDifference })),

  /** `confirmExcludedDates`: reconoce que las fechas excluidas del cálculo quedan sin facturar en esta preliquidación. */
  close: (id: string, confirmExcludedDates?: boolean) =>
    unwrap<IPreliquidacion>(http.post(`/preliquidaciones/${id}/close`, { confirmExcludedDates })),

  downloadPdf: (id: string) => http.get(`/preliquidaciones/${id}/pdf`, { responseType: 'blob' }),
};

// ---- Retención por cliente + Mano de obra + Estado de resultado (ISSUE-72) ----

export type ClientWithholdingMode = 'percentage' | 'fixed_amount';

export interface IClientWithholdingConfig {
  id: string;
  clientId: string;
  mode: ClientWithholdingMode;
  rate: string | null;
  fixedAmount: string | null;
  validFrom: string;
  validTo: string | null;
  status: ClientWithholdingConfigStatus;
  previousVersionId: string | null;
  supersededAt: string | null;
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
}

export interface ICreateClientWithholdingConfigPayload {
  mode: ClientWithholdingMode;
  rate?: number;
  fixedAmount?: number;
  validFrom: string;
  validTo?: string;
  notes?: string;
}

export interface IClientLaborCost {
  id: string;
  clientId: string;
  siteId: string | null;
  periodStart: string;
  periodEnd: string;
  amount: string;
  notes: string | null;
  createdByUserId: string;
  updatedByUserId: string | null;
  createdAt: string;
}

export interface IUpsertClientLaborCostPayload {
  siteId?: string | null;
  periodStart: string;
  periodEnd: string;
  amount: number;
  notes?: string;
}

export interface IClientResultStatement {
  clientId: string;
  clientName: string;
  periodStart: string;
  periodEnd: string;
  ingresos: string;
  materiaPrima: string;
  manoDeObra: string;
  retencionesEstimadas: string;
  resultadoEstimado: string;
  breakdown: {
    ingresosPorPreliquidacion: { preliquidacionId: string; siteId: string; siteName: string; periodStart: string; periodEnd: string; status: string; amount: string }[];
    materiaPrimaPorSite: { siteId: string; siteName: string; amount: string; cierresSinCosteoStock: number }[];
    manoDeObra: { id: string; siteId: string | null; siteName: string | null; periodStart: string; periodEnd: string; amount: string }[];
    retencion: { mode: string; rate: string | null; fixedAmount: string | null; validFrom: string; validTo: string | null; aplicado: string }[];
  };
  assumptions: string[];
  exclusions: string[];
}

export const clientWithholdingApi = {
  getActive: (clientId: string) =>
    unwrap<IClientWithholdingConfig | null>(http.get(`/clients/${clientId}/withholding-config`)),
  getHistory: (clientId: string) =>
    unwrap<IClientWithholdingConfig[]>(http.get(`/clients/${clientId}/withholding-config/history`)),
  createVersion: (clientId: string, payload: ICreateClientWithholdingConfigPayload) =>
    unwrap<IClientWithholdingConfig>(http.post(`/clients/${clientId}/withholding-config`, payload)),
};

export const clientLaborCostApi = {
  list: (clientId: string, params: { siteId?: string; from?: string; to?: string } = {}) =>
    unwrap<IClientLaborCost[]>(http.get(`/clients/${clientId}/labor-costs`, { params })),
  upsert: (clientId: string, payload: IUpsertClientLaborCostPayload) =>
    unwrap<IClientLaborCost>(http.post(`/clients/${clientId}/labor-costs`, payload)),
  remove: (clientId: string, id: string) => http.delete(`/clients/${clientId}/labor-costs/${id}`),
};

// ───────── Presupuesto mensual de comedor (#288 / #289 / #290) ─────────

export type ComedorBudgetStatus = 'draft' | 'approved' | 'superseded';
export type ComedorBudgetOtherCostType = 'fixed' | 'percent_of_labor_and_supplies';
export type ComedorBudgetPriceSource = 'manual' | 'recipe' | 'article';

export interface IComedorBudgetLaborLinePayload {
  position: string;
  quantity: number;
  grossUnit: number;
  nonRemunerativeUnit: number;
  /** Fracción decimal: 26,66% = 0.2666. */
  socialChargesRate: number;
  unionRate: number;
  notes?: string;
}

export interface IComedorBudgetSupplyLinePayload {
  description: string;
  category: string;
  articleId?: string;
  quantity: number;
  unit: string;
  /** null / omitido = precio faltante; 0 = cero confirmado. */
  unitCost?: number | null;
  priceUnit?: string;
  conversionFactor?: number;
  priceSource?: ComedorBudgetPriceSource;
  priceDate?: string;
}

export interface IComedorBudgetOtherCostPayload {
  description: string;
  type: ComedorBudgetOtherCostType;
  /** Monto (ARS) si es fijo; fracción decimal si es porcentaje sobre RRHH + insumos. */
  value: number;
}

export type ComedorBudgetPriceBaseType = 'fixed' | 'percent_of_direct_cost' | 'percent_of_net_price' | 'percent_of_profitability';
export type ComedorBudgetPriceLineRole = 'standard' | 'profitability';

export interface IComedorBudgetPriceLinePayload {
  /** Identificador estable de la línea (sobrevive a renombrar y reordenar). */
  lineKey?: string;
  /** La rentabilidad se identifica por el rol, nunca por el texto del concepto. */
  role?: ComedorBudgetPriceLineRole;
  concept: string;
  baseType: ComedorBudgetPriceBaseType;
  /** Monto (ARS) si es fijo; tasa fraccional (0.03 = 3%) en los demás tipos. */
  value: number;
  notes?: string;
}

export interface IComedorBudgetPriceError {
  code: string;
  message: string;
  lineKey?: string;
}

export type PerViandUnavailableReason = 'NO_VOLUME' | 'NO_VAT_RATE';

export interface IComedorBudgetPricing {
  errors: IComedorBudgetPriceError[];
  lines: { key: string; amount: string; base: string | null }[];
  aux: { markupRate: string; fixedTotal: string; profitability: string; profitabilityDerived: string; grossUpRate: string } | null;
  /** Precio neto del servicio: antes de IVA. */
  netPrice: string | null;
  netPriceExact: string | null;
  chargesTotal: string | null;
  reconciliationDiff: string | null;
  vat: { rate: string; amount: string; finalPrice: string; finalPriceExact: string } | null;
  perViand: { directCost: string; netPrice: string; vat: string; finalPrice: string; finalPriceRounded: string; roundingDifference: string } | null;
  perViandUnavailable: PerViandUnavailableReason | null;
}

export interface IComedorBudgetBodyPayload {
  dinersPerDay: number;
  serviceDays: number;
  laborLines: IComedorBudgetLaborLinePayload[];
  supplyLines: IComedorBudgetSupplyLinePayload[];
  otherCosts: IComedorBudgetOtherCostPayload[];
  /** Cargas sobre el precio (#291). */
  priceLines?: IComedorBudgetPriceLinePayload[];
  /** Alícuota de IVA (fracción). null / omitida = no definida: no se asume 21%. */
  vatRate?: number | null;
}

export interface ICreateComedorBudgetPayload extends IComedorBudgetBodyPayload {
  clientId: string;
  siteId: string;
  serviceType: string;
  periodStart: string;
  periodEnd: string;
  name: string;
  notes?: string;
}

export interface IUpdateComedorBudgetPayload extends IComedorBudgetBodyPayload {
  expectedRevision: number;
  name: string;
  periodStart: string;
  periodEnd: string;
  notes?: string;
}

export interface IComedorBudget {
  id: string;
  clientId: string;
  siteId: string;
  serviceType: string;
  currency: string;
  periodStart: string;
  periodEnd: string;
  name: string;
  version: number;
  previousBudgetId: string | null;
  dinersPerDay: number;
  serviceDays: number;
  notes: string | null;
  /** Alícuota de IVA (fracción, string decimal) o null si no se definió. */
  vatRate: string | null;
  status: ComedorBudgetStatus;
  revision: number;
  createdByUserId: string;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface IComedorBudgetTotals {
  laborTotal: string;
  suppliesTotal: string;
  otherCostsTotal: string;
  directCost: string;
  directCostExact: string;
  suppliesWithoutPrice: number;
}

export interface IComedorBudgetWarning {
  code: 'FOOD_SUPPLIES_ZERO' | 'SUPPLIES_WITHOUT_PRICE' | 'RECIPE_ESTIMATE_OVERLAP' | 'DUPLICATE_ARTICLE';
  message: string;
}

export interface IComedorBudgetPreview {
  volume: number;
  totals: IComedorBudgetTotals;
  laborLines: { socialCharges: string; union: string; total: string }[];
  supplyLines: { total: string | null }[];
  otherCosts: { amount: string }[];
  warnings: IComedorBudgetWarning[];
  /** null si no hay cargas ni alícuota cargadas. */
  pricing: IComedorBudgetPricing | null;
}

export interface IComedorBudgetDetail {
  budget: IComedorBudget;
  volume: number;
  laborLines: (IComedorBudgetLaborLinePayload & { id: string; socialCharges: string; union: string; total: string })[];
  supplyLines: (IComedorBudgetSupplyLinePayload & { id: string; total: string | null })[];
  otherCosts: (IComedorBudgetOtherCostPayload & { id: string; amount: string })[];
  priceLines: {
    id: string;
    lineKey: string;
    role: ComedorBudgetPriceLineRole;
    concept: string;
    baseType: ComedorBudgetPriceBaseType;
    value: string;
    notes: string | null;
    amount: string | null;
    base: string | null;
  }[];
  totals: IComedorBudgetTotals;
  warnings: IComedorBudgetWarning[];
  pricing: IComedorBudgetPricing | null;
}

export type AgreementTransferState = 'NEW_VERSION' | 'NO_AGREEMENT' | 'ALREADY_TRANSFERRED';
export type AgreementBlockedReason = 'PRICING_NOT_CONFIGURED' | 'PRICING_INVALID' | 'NO_VAT_RATE' | 'NO_VOLUME' | 'ZERO_PRICE';

export interface IAgreementSummary {
  id: string;
  version: number;
  status: string;
  mealValue: string;
  targetCostPerDiner: string | null;
  validFrom: string;
  validTo: string;
}

export interface IAgreementTransferPreview {
  state: AgreementTransferState;
  blockedReason: AgreementBlockedReason | null;
  /** Precio FINAL por vianda (con IVA incluido), redondeado a centavos: pasa a `mealValue` del acuerdo. */
  mealValue: string | null;
  vatRate: string | null;
  perViand: { directCost: string; netPrice: string; vat: string; finalPrice: string; roundingDifference: string } | null;
  monthlyFinalPrice: string | null;
  validFrom: string;
  validTo: string;
  currentAgreement: IAgreementSummary | null;
  transferredAgreement: IAgreementSummary | null;
  /** El objetivo de costo por comensal mide solo ingredientes de receta: no se pisa salvo pedido explícito. */
  targetCost: { current: string | null; foodCostPerViand: string | null };
}

export interface ICreateAgreementFromBudgetPayload {
  validFrom?: string;
  validTo?: string;
  applyTargetCost?: boolean;
}

export type LaborCostTransferState = 'NEW' | 'ALREADY_TRANSFERRED' | 'REPLACES_PREVIOUS_VERSION' | 'REPLACES_OTHER';

export interface ILaborCostTransferPreview {
  amount: string;
  siteId: string;
  periodStart: string;
  periodEnd: string;
  state: LaborCostTransferState;
  existing: { id: string; amount: string; notes: string | null; sourceBudgetId: string | null; sourceBudgetVersion: number | null } | null;
}

export interface IRecipeEstimatePayload {
  dishes: { dishId: string; portions: number }[];
}

export interface IComedorBudgetListParams {
  clientId?: string;
  siteId?: string;
  status?: ComedorBudgetStatus;
  from?: string;
  to?: string;
}

export const comedorBudgetsApi = {
  list: (params: IComedorBudgetListParams = {}) => unwrap<IComedorBudget[]>(http.get('/comedor-budgets', { params })),
  get: (id: string) => unwrap<IComedorBudgetDetail>(http.get(`/comedor-budgets/${id}`)),
  preview: (payload: IComedorBudgetBodyPayload) => unwrap<IComedorBudgetPreview>(http.post('/comedor-budgets/preview', payload)),
  create: (payload: ICreateComedorBudgetPayload) => unwrap<IComedorBudgetDetail>(http.post('/comedor-budgets', payload)),
  update: (id: string, payload: IUpdateComedorBudgetPayload) => unwrap<IComedorBudgetDetail>(http.put(`/comedor-budgets/${id}`, payload)),
  remove: (id: string) => http.delete(`/comedor-budgets/${id}`),
  duplicate: (id: string) => unwrap<IComedorBudgetDetail>(http.post(`/comedor-budgets/${id}/duplicate`)),
  createRevision: (id: string) => unwrap<IComedorBudgetDetail>(http.post(`/comedor-budgets/${id}/revision`)),
  /** Excel (.xlsx) con el estilo y las fórmulas de la plantilla de costeo. */
  exportXlsx: (id: string) => http.get(`/comedor-budgets/${id}/export`, { responseType: 'blob' }).then((r) => r.data as Blob),
  agreementTransferPreview: (id: string, range: { validFrom?: string; validTo?: string } = {}) =>
    unwrap<IAgreementTransferPreview>(http.get(`/comedor-budgets/${id}/agreement-transfer`, { params: range })),
  createAgreementVersion: (id: string, payload: ICreateAgreementFromBudgetPayload = {}) =>
    unwrap<{ transferred: boolean; agreement: IAgreementSummary | null; preview: IAgreementTransferPreview }>(http.post(`/comedor-budgets/${id}/agreement-version`, payload)),
  recipeEstimate: (payload: IRecipeEstimatePayload) => unwrap<IComedorBudgetSupplyLinePayload[]>(http.post('/comedor-budgets/recipe-estimate', payload)),
  laborCostTransferPreview: (id: string) => unwrap<ILaborCostTransferPreview>(http.get(`/comedor-budgets/${id}/labor-cost-transfer`)),
  transferLaborCost: (id: string, payload: { replaceExisting?: boolean } = {}) =>
    unwrap<{ transferred: boolean; preview: ILaborCostTransferPreview }>(http.post(`/comedor-budgets/${id}/labor-cost-transfer`, payload)),
  approve: (id: string, payload: { expectedRevision: number; acknowledgeWarnings?: boolean }) =>
    unwrap<IComedorBudgetDetail>(http.post(`/comedor-budgets/${id}/approve`, payload)),
  /** #293: comparativo del presupuesto aprobado contra la ejecución real de la sede. */
  comparison: (id: string, range: { from?: string; to?: string } = {}) =>
    unwrap<IBudgetComparison>(http.get(`/comedor-budgets/${id}/comparison`, { params: range })),
  /** #295: cotización PDF para el cliente (sin salarios ni costos internos). */
  exportQuotePdf: (id: string) =>
    http
      .get(`/comedor-budgets/${id}/quote`, { responseType: 'blob' })
      .then((r) => r.data as Blob)
      .catch(async (error) => {
        // Con responseType blob el cuerpo del error llega como Blob: se decodifica para mostrar el mensaje real del servidor.
        const data = error?.response?.data;
        if (data instanceof Blob) {
          try {
            const parsed = JSON.parse(await data.text());
            error.response.data = parsed;
          } catch {
            /* cuerpo no JSON: se deja el error original */
          }
        }
        throw error;
      }),
  /** #295: vista previa de la importación de la plantilla Rev04; no escribe nada. */
  importPreview: (file: File, target: IBudgetImportTarget) =>
    unwrap<IBudgetImportPreview>(
      http.post('/comedor-budgets/import/preview', importForm(file, target), {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    ),
  /** #295: confirma la importación (crea un borrador; reintentar el mismo archivo no duplica). */
  importConfirm: (file: File, target: IBudgetImportTarget) =>
    unwrap<{ created: boolean; budget: IComedorBudgetDetail }>(
      http.post('/comedor-budgets/import/confirm', importForm(file, target), {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    ),
};

export interface ISiteExpense {
  id: string;
  siteId: string;
  expenseDate: string;
  category: string;
  description: string;
  amount: string;
  sourceDocumentType: string | null;
  sourceDocumentId: string | null;
}

/** #293: gastos imputados a una sede (fuente de «Otros gastos» reales del comparativo). */
export const siteExpensesApi = {
  list: (siteId: string, from: string, to: string) => unwrap<ISiteExpense[]>(http.get('/site-expenses', { params: { siteId, from, to } })),
  create: (payload: { siteId: string; expenseDate: string; category: string; description: string; amount: number }) =>
    unwrap<ISiteExpense>(http.post('/site-expenses', payload)),
  remove: (id: string) => http.delete(`/site-expenses/${id}`),
};

export type BudgetComparisonRowStatus = 'ok' | 'incomplete' | 'missing';

export interface IBudgetComparisonRow {
  key: 'volume' | 'income' | 'supplies' | 'labor' | 'otherExpenses' | 'result';
  label: string;
  budget: string;
  actual: string | null;
  deviation: string | null;
  deviationPct: string | null;
  deviationPctUnavailable: 'BUDGET_ZERO' | 'NO_ACTUAL' | null;
  status: BudgetComparisonRowStatus;
  note: string | null;
}

export interface IBudgetComparison {
  budgetId: string;
  siteId: string;
  budgetVersion: number;
  periodStart: string;
  periodEnd: string;
  budgetPeriodStart: string;
  budgetPeriodEnd: string;
  periodState: 'SAME_PERIOD' | 'DIFFERENT_PERIOD';
  rows: IBudgetComparisonRow[];
  complete: boolean;
  missingSources: string[];
  effects: { supplies: { volumeEffect: string; unitCostEffect: string } | null; labor: { volumeEffect: string; unitCostEffect: string } | null };
  withholdingsEstimated: string;
  detail: { assumptions: string[]; exclusions: string[] };
}

export interface IBudgetImportTarget {
  clientId?: string;
  siteId?: string;
  serviceType?: string;
  name?: string;
  /** Concepto elegido como rentabilidad ("__none__" = ninguno) cuando la plantilla no marca el rol. */
  profitabilityConcept?: string;
}

export interface IBudgetImportIssue {
  severity: 'error' | 'warning';
  sheet: string;
  cell: string | null;
  message: string;
}

export interface IBudgetImportPreview {
  fileName: string;
  period: { periodStart: string; periodEnd: string } | null;
  valid: boolean;
  issues: IBudgetImportIssue[];
  alreadyImportedBudgetId: string | null;
  priceConcepts: { concept: string; baseType: string; eligible: boolean; suggested: boolean }[];
  profitability: 'explicit' | 'chosen' | 'none' | 'unresolved';
  calculation: IComedorBudgetPreview | null;
}

function importForm(file: File, target: IBudgetImportTarget): FormData {
  const form = new FormData();
  form.append('file', file);
  (Object.entries(target) as [string, string | undefined][]).forEach(([k, v]) => v && form.append(k, v));
  return form;
}

export const clientResultStatementApi = {
  get: (clientId: string, periodStart: string, periodEnd: string) =>
    unwrap<IClientResultStatement>(
      http.get(`/clients/${clientId}/result-statement`, { params: { periodStart, periodEnd } }),
    ),
};

// ---- Control de Caja Chica (ISSUE-506, #39) ----

export type PettyCashBoxType = 'administracion' | 'site' | 'deposito' | 'buffet' | 'supervisor';
export type PettyCashMedium = 'efectivo' | 'cuenta_bancaria' | 'billetera_virtual';
export type PettyCashMovementType = 'ingreso' | 'egreso' | 'reposicion' | 'ajuste_arqueo';
export type PettyCashCostCenterType = 'site' | 'general';

export interface IPettyCashCategory {
  id: string;
  code: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IPettyCashAccount {
  id: string;
  boxId: string;
  medium: PettyCashMedium;
  label: string;
  bankAccountId: string | null;
  currentBalance: string;
  active: boolean;
  lowBalanceNotifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IPettyCashMovement {
  id: string;
  pettyCashAccountId: string;
  type: PettyCashMovementType;
  status: PettyCashMovementStatus;
  amount: string;
  balanceAfter: string | null;
  paymentMethodDetail: string | null;
  description: string;
  justification: string | null;
  comprobanteKey: string | null;
  categoryId: string | null;
  costCenterType: PettyCashCostCenterType | null;
  siteId: string | null;
  costCenter: string | null;
  occurredAt: string;
  createdByUserId: string;
  approvedByUserId: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  countId: string | null;
  reposicionGroupId: string | null;
  bankMovementId: string | null;
  /** #263: datos de entrega del sobre en un ingreso de efectivo. */
  deliveredBy: string | null;
  deliveryShift: PettyCashShift | null;
  deliveryDate: string | null;
  envelopeId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** #263: turno de entrega del sobre de un buffet. */
export type PettyCashShift = 'manana' | 'tarde' | 'noche';
export const PETTY_CASH_SHIFT_LABELS: Record<PettyCashShift, string> = { manana: 'Mañana', tarde: 'Tarde', noche: 'Noche' };
export type PettyCashEnvelopeStatus = 'pending' | 'matched' | 'difference' | 'cancelled';
export const PETTY_CASH_ENVELOPE_STATUS_LABELS: Record<PettyCashEnvelopeStatus, string> = {
  pending: 'Pendiente de recibir',
  matched: 'Conciliado',
  difference: 'Con diferencia',
  cancelled: 'Anulado',
};

export interface IPettyCashEnvelope {
  id: string;
  pettyCashBoxId: string;
  deliveredBy: string;
  shift: PettyCashShift;
  envelopeDate: string;
  declaredAmount: string;
  status: PettyCashEnvelopeStatus;
  movementId: string | null;
  receivedAmount: string | null;
  /** Recibido − declarado: negativo es faltante, positivo sobrante. */
  difference: string | null;
  note: string | null;
  createdByUserId: string;
  receivedByUserId: string | null;
  receivedAt: string | null;
  createdAt: string;
}

export interface IPettyCashEnvelopesQuery {
  boxId?: string;
  status?: string;
  from?: string;
  to?: string;
}

export interface IPettyCashEnvelopeReconciliation {
  envelopes: IPettyCashEnvelope[];
  /** Ingresos de efectivo de cajas buffet sin sobre asociado. */
  unlinkedIngresos: IPettyCashMovement[];
  totals: {
    declared: string;
    received: string;
    difference: string;
    pendingCount: number;
    differenceCount: number;
    unlinkedCount: number;
    unlinkedAmount: string;
  };
}

export interface IPettyCashBox {
  id: string;
  code: string;
  name: string;
  type: PettyCashBoxType;
  siteId: string | null;
  responsibleUserId: string;
  status: PettyCashBoxStatus;
  maxExpenseAmount: string | null;
  maxBalance: string | null;
  lowBalanceThreshold: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IPettyCashBoxWithAccounts extends IPettyCashBox {
  accounts: (IPettyCashAccount & { lastMovement?: IPettyCashMovement | null })[];
  totalBalance: string;
}

export interface IPettyCashCount {
  id: string;
  pettyCashAccountId: string;
  countedByUserId: string;
  countedAt: string;
  systemAmount: string;
  countedAmount: string;
  difference: string;
  note: string | null;
  status: PettyCashCountStatus;
  authorizedByUserId: string | null;
  authorizedAt: string | null;
  adjustmentMovementId: string | null;
}

export interface IPettyCashMovementsQuery {
  page?: number;
  limit?: number;
  boxId?: string;
  pettyCashAccountId?: string;
  type?: string;
  status?: string;
  categoryId?: string;
  costCenter?: string;
  siteId?: string;
  from?: string;
  to?: string;
  search?: string;
}

export const pettyCashApi = {
  categories: {
    list: (params: { search?: string; active?: boolean } = {}) =>
      unwrap<IPettyCashCategory[]>(http.get('/petty-cash/categories', { params })),
    create: (payload: { code: string; name: string }) =>
      unwrap<IPettyCashCategory>(http.post('/petty-cash/categories', payload)),
    update: (id: string, payload: { name?: string; active?: boolean }) =>
      unwrap<IPettyCashCategory>(http.patch(`/petty-cash/categories/${id}`, payload)),
  },

  boxes: {
    list: (params: { search?: string } = {}) =>
      unwrap<IPettyCashBoxWithAccounts[]>(http.get('/petty-cash/boxes', { params })),
    get: (id: string) => unwrap<IPettyCashBoxWithAccounts>(http.get(`/petty-cash/boxes/${id}`)),
    create: (payload: {
      name: string;
      type: PettyCashBoxType;
      responsibleUserId: string;
      siteId?: string;
      maxExpenseAmount?: number;
      maxBalance?: number;
      lowBalanceThreshold?: number;
    }) => unwrap<IPettyCashBox>(http.post('/petty-cash/boxes', payload)),
    update: (id: string, payload: Record<string, unknown>) =>
      unwrap<IPettyCashBox>(http.patch(`/petty-cash/boxes/${id}`, payload)),
    addAccount: (
      id: string,
      payload: { medium: PettyCashMedium; label: string; bankAccountId?: string; initialBalance?: number },
    ) => unwrap<IPettyCashAccount>(http.post(`/petty-cash/boxes/${id}/accounts`, payload)),
    deactivate: (id: string) => unwrap<IPettyCashBox>(http.post(`/petty-cash/boxes/${id}/deactivate`)),
    reactivate: (id: string) => unwrap<IPettyCashBox>(http.post(`/petty-cash/boxes/${id}/reactivate`)),
    rendicion: (id: string) =>
      unwrap<{ sinceReposicionAt: string | null; egresos: IPettyCashMovement[]; total: number }>(
        http.get(`/petty-cash/boxes/${id}/rendicion`),
      ),
  },

  movements: {
    list: (query: IPettyCashMovementsQuery = {}) =>
      unwrap<IPaginatedResult<IPettyCashMovement>>(http.get('/petty-cash/movements', { params: query })),
    createIngreso: (payload: {
      pettyCashAccountId: string;
      amount: number;
      description: string;
      paymentMethodDetail?: string;
      occurredAt?: string;
      /** #263: obligatorios en un ingreso a un medio efectivo (salvo que reciba un sobre). */
      deliveredBy?: string;
      deliveryShift?: PettyCashShift;
      deliveryDate?: string;
      /** #263: sobre de buffet que este ingreso recibe y concilia. */
      envelopeId?: string;
      differenceNote?: string;
    }) => unwrap<IPettyCashMovement>(http.post('/petty-cash/movements/ingresos', payload)),
    createEgreso: (payload: {
      pettyCashAccountId: string;
      amount: number;
      description: string;
      categoryId: string;
      costCenterType: PettyCashCostCenterType;
      siteId?: string;
      justification?: string;
      paymentMethodDetail?: string;
      occurredAt?: string;
      comprobante?: Blob | null;
      comprobanteName?: string;
    }) => {
      const fd = new FormData();
      fd.append('pettyCashAccountId', payload.pettyCashAccountId);
      fd.append('amount', String(payload.amount));
      fd.append('description', payload.description);
      fd.append('categoryId', payload.categoryId);
      fd.append('costCenterType', payload.costCenterType);
      if (payload.siteId) fd.append('siteId', payload.siteId);
      if (payload.justification) fd.append('justification', payload.justification);
      if (payload.paymentMethodDetail) fd.append('paymentMethodDetail', payload.paymentMethodDetail);
      if (payload.occurredAt) fd.append('occurredAt', payload.occurredAt);
      if (payload.comprobante) fd.append('comprobante', payload.comprobante, payload.comprobanteName ?? 'comprobante');
      return unwrap<IPettyCashMovement>(
        http.post('/petty-cash/movements/egresos', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
      );
    },
    createReposicion: (payload: {
      destinationAccountId: string;
      originType: 'bank_account' | 'petty_cash_account';
      originId: string;
      amount: number;
      description?: string;
      paymentMethodDetail?: string;
    }) => unwrap<IPettyCashMovement>(http.post('/petty-cash/movements/reposiciones', payload)),
    approve: (id: string) => unwrap<IPettyCashMovement>(http.post(`/petty-cash/movements/${id}/approve`)),
    reject: (id: string, reason?: string) =>
      unwrap<IPettyCashMovement>(http.post(`/petty-cash/movements/${id}/reject`, { reason })),
    comprobante: (id: string) =>
      http.get(`/petty-cash/movements/${id}/comprobante`, { responseType: 'blob' }).then((r) => r.data as Blob),
    exportCsv: (query: IPettyCashMovementsQuery = {}) =>
      http.get('/petty-cash/movements/export', { params: query, responseType: 'blob' }).then((r) => r.data as Blob),
  },

  envelopes: {
    list: (query: IPettyCashEnvelopesQuery = {}) => unwrap<IPettyCashEnvelope[]>(http.get('/petty-cash/envelopes', { params: query })),
    reconciliation: (query: IPettyCashEnvelopesQuery = {}) =>
      unwrap<IPettyCashEnvelopeReconciliation>(http.get('/petty-cash/envelopes/reconciliation', { params: query })),
    declare: (payload: { pettyCashBoxId: string; deliveredBy: string; shift: PettyCashShift; envelopeDate: string; declaredAmount: number }) =>
      unwrap<IPettyCashEnvelope>(http.post('/petty-cash/envelopes', payload)),
    cancel: (id: string) => unwrap<IPettyCashEnvelope>(http.post(`/petty-cash/envelopes/${id}/cancel`)),
  },

  counts: {
    listForAccount: (pettyCashAccountId: string) =>
      unwrap<IPettyCashCount[]>(http.get('/petty-cash/counts', { params: { pettyCashAccountId } })),
    listForBox: (boxId: string) =>
      unwrap<IPettyCashCount[]>(http.get('/petty-cash/counts', { params: { boxId } })),
    create: (payload: { pettyCashAccountId: string; countedAmount: number; note?: string }) =>
      unwrap<IPettyCashCount>(http.post('/petty-cash/counts', payload)),
    authorize: (id: string, note?: string) =>
      unwrap<IPettyCashCount>(http.post(`/petty-cash/counts/${id}/authorize`, { note })),
  },
};
/** #236: exportaciones a Excel de Compras y Logística — mismos filtros que la pantalla desde la que se piden. */
async function getXlsxBlob(url: string, params?: Record<string, string | undefined>): Promise<Blob> {
  try {
    const response = await http.get(url, { params, responseType: 'blob' });
    return response.data as Blob;
  } catch (error) {
    const data = (error as { response?: { data?: unknown } }).response?.data;
    if (data instanceof Blob) {
      let message = 'No se pudo generar el archivo';
      try {
        const body = JSON.parse(await data.text()) as { message?: string | string[] };
        message = Array.isArray(body.message) ? body.message.join(', ') : (body.message ?? message);
      } catch {
        /* el cuerpo no era JSON: queda el mensaje genérico */
      }
      throw new Error(message);
    }
    throw error;
  }
}

export const logisticsExportsApi = {
  purchasingInbox: (params: { date?: string; siteId?: string; origin?: string; status?: string }) => getXlsxBlob('/logistics-exports/purchasing-inbox', params),
  consolidation: (id: string, params: { siteId?: string } = {}) => getXlsxBlob(`/logistics-exports/purchase-consolidations/${id}`, params),
  purchaseOrders: (params: { supplierId?: string; statuses?: string } = {}) => getXlsxBlob('/logistics-exports/purchase-orders', params),
};

// ---------------------------------------------------------------- Portal del proveedor

export type SupplierPortalOrderView = 'pending' | 'confirmed' | 'history';

export interface ISupplierPortalOrder {
  id: string;
  poNumber: string;
  issueDate: string;
  deliveryDate: string;
  deliveryLocation: string;
  paymentTerms: string;
  status: string;
  committedDeliveryDate: string | null;
  supplierConfirmedAt: string | null;
  /** Confirmada por Compras en nombre del proveedor (#280). */
  confirmedOnBehalf?: boolean;
}

export interface ISupplierPortalOrderLine {
  id: string;
  articleCode: string | null;
  articleDescription: string;
  quantity: string;
  unitPrice: string | null;
  discount: string | null;
  status: string;
}

export interface ISupplierPortalPrice {
  articleId: string;
  articleCode: string | null;
  articleDescription: string;
  price: string;
  validFrom: string;
}

export interface ISupplierPriceProposal {
  id: string;
  supplierId: string;
  articleId: string;
  articleCode: string | null;
  articleDescription: string;
  proposedPrice: string;
  validFrom: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  note: string | null;
  createdAt: string;
  reviewedAt: string | null;
  /** Sólo en el listado interno de Compras. */
  supplierName?: string;
  currentPrice?: string | null;
  /** Mejor precio vigente entre proveedores y cómo se compara la propuesta (higher = más cara). */
  bestPrice?: string | null;
  comparison?: SupplierProposalComparison;
}

export type SupplierProposalComparison = 'higher' | 'ok' | 'no_reference';

export interface ISupplierCatalogRow {
  articleId: string;
  articleCode: string;
  articleDescription: string;
  categoryId: string;
  categoryName: string;
  ownPrice: string | null;
  /** #281: el proveedor ya tiene precio vigente para este artículo. */
  offered: boolean;
  pendingProposal: { price: string; note: string | null; validFrom: string } | null;
}

export interface ISupplierImportPreviewRow {
  row: number;
  articleCode: string;
  articleId: string | null;
  articleDescription: string | null;
  price: number | null;
  note: string | null;
  /** Vigente desde (AAAA-MM-DD); null = hoy. */
  validFrom: string | null;
  error: string | null;
}

export interface ISupplierProposalItem {
  articleId: string;
  price: number;
  note?: string;
  validFrom?: string;
}

export type SupplierDocumentStatus = 'pending' | 'approved' | 'rejected';

export interface ISupplierPortalDocument {
  id: string;
  fileName: string;
  attachmentType: string;
  expirationDate: string | null;
  expired: boolean;
  status: SupplierDocumentStatus;
  rejectionReason: string | null;
  /** Subido desde el portal: se puede eliminar mientras no esté aprobado. */
  uploadedByMe: boolean;
  downloadable: boolean;
}

/** Documento subido por un proveedor, en el listado de revisión de Compras. */
export interface ISupplierDocumentReview {
  id: string;
  supplierId: string;
  supplierName: string;
  fileName: string;
  attachmentType: string;
  expirationDate: string | null;
  status: SupplierDocumentStatus;
  rejectionReason: string | null;
  createdAt: string;
}

export interface IUploadSupplierDocumentPayload {
  attachmentType: 'contract' | 'certification' | 'other';
  expirationDate?: string;
}

export interface ISupplierPortalAccount {
  balance: number;
  entries: {
    total: number;
    page: number;
    limit: number;
    items: Array<{ id: string; createdAt: string; entryType: string; sourceDocumentType: string; amount: string; balanceAfter: string }>;
  };
  payments: Array<{ id: string; paymentDate: string; amount: string; paymentMethod: string; referenceNumber: string | null }>;
  documents: ISupplierPortalDocument[];
}

export const supplierPortalApi = {
  getSupplierInfo: () => unwrap<{ id: string; name: string }>(http.get('/supplier-portal/me')),
  listOrders: (params: { view?: SupplierPortalOrderView; page?: number; limit?: number; search?: string }) =>
    unwrap<{ items: ISupplierPortalOrder[]; total: number; page: number; limit: number }>(http.get('/supplier-portal/purchase-orders', { params })),
  getOrder: (id: string) =>
    unwrap<ISupplierPortalOrder & { lines: ISupplierPortalOrderLine[] }>(http.get(`/supplier-portal/purchase-orders/${id}`)),
  confirmOrder: (id: string, committedDeliveryDate: string) =>
    unwrap<ISupplierPortalOrder>(http.post(`/supplier-portal/purchase-orders/${id}/confirm`, { committedDeliveryDate })),
  listPrices: () => unwrap<ISupplierPortalPrice[]>(http.get('/supplier-portal/prices')),
  listProposals: () => unwrap<ISupplierPriceProposal[]>(http.get('/supplier-portal/price-proposals')),
  listCatalog: (params: { search?: string; categoryId?: string; onlyMine?: boolean; onlyOthers?: boolean; page?: number; limit?: number }) =>
    unwrap<{ items: ISupplierCatalogRow[]; total: number; page: number; limit: number }>(http.get('/supplier-portal/catalog', { params })),
  listCatalogCategories: () => unwrap<{ id: string; name: string }[]>(http.get('/supplier-portal/catalog/categories')),
  exportCatalog: (params: { search?: string; categoryId?: string; onlyMine?: boolean; onlyOthers?: boolean }) =>
    getXlsxBlob('/supplier-portal/catalog/export', { ...params, onlyMine: params.onlyMine ? 'true' : undefined, onlyOthers: params.onlyOthers ? 'true' : undefined }),
  downloadImportTemplate: () => getXlsxBlob('/supplier-portal/catalog/template'),
  offerArticle: (payload: { articleId: string; price: number }) =>
    unwrap<{ articleId: string; price: string }>(http.post('/supplier-portal/catalog/offer', payload)),
  previewCatalogImport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return unwrap<{ rows: ISupplierImportPreviewRow[] }>(http.post('/supplier-portal/catalog/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }));
  },
  submitProposals: (items: ISupplierProposalItem[]) =>
    unwrap<{ created: number; updated: number }>(http.post('/supplier-portal/price-proposals/bulk', { items })),
  createProposal: (payload: { articleId: string; price: number; note?: string; validFrom?: string }) =>
    unwrap<ISupplierPriceProposal>(http.post('/supplier-portal/price-proposals', payload)),
  getAccount: (params: { page?: number; limit?: number } = {}) =>
    unwrap<ISupplierPortalAccount>(http.get('/supplier-portal/account', { params })),
  uploadDocument: (file: File, payload: IUploadSupplierDocumentPayload) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('attachmentType', payload.attachmentType);
    if (payload.expirationDate) formData.append('expirationDate', payload.expirationDate);
    return unwrap<ISupplierDocumentReview>(http.post('/supplier-portal/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }));
  },
  removeDocument: (id: string) => http.delete(`/supplier-portal/documents/${id}`).then(() => undefined),
  getDocumentFile: (id: string) => http.get(`/supplier-portal/documents/${id}/file`, { responseType: 'blob' }).then((r) => r.data as Blob),
};

/** Revisión interna (Compras) de la documentación que suben los proveedores. */
export const supplierDocumentsApi = {
  list: (status: SupplierDocumentStatus = 'pending') =>
    unwrap<ISupplierDocumentReview[]>(http.get('/supplier-documents', { params: { status } })),
  getFile: (id: string) => http.get(`/supplier-documents/${id}/file`, { responseType: 'blob' }).then((r) => r.data as Blob),
  approve: (id: string) => unwrap<ISupplierDocumentReview>(http.post(`/supplier-documents/${id}/approve`)),
  reject: (id: string, reason: string) => unwrap<ISupplierDocumentReview>(http.post(`/supplier-documents/${id}/reject`, { reason })),
};

/** Gestión interna (Compras) de las propuestas de precio que llegan desde el portal del proveedor. */
export const supplierPriceProposalsApi = {
  list: (status: 'pending' | 'approved' | 'rejected' = 'pending') =>
    unwrap<ISupplierPriceProposal[]>(http.get('/supplier-price-proposals', { params: { status } })),
  approve: (id: string) => unwrap<ISupplierPriceProposal>(http.post(`/supplier-price-proposals/${id}/approve`)),
  reject: (id: string, reason: string) => unwrap<ISupplierPriceProposal>(http.post(`/supplier-price-proposals/${id}/reject`, { reason })),
  /** #282: resolución en lote; las que ya no estaban pendientes vuelven en `failed` sin frenar el resto. */
  approveMany: (ids: string[]) =>
    unwrap<IResolveManyResult>(http.post('/supplier-price-proposals/approve-many', { ids })),
  rejectMany: (ids: string[], reason: string) =>
    unwrap<IResolveManyResult>(http.post('/supplier-price-proposals/reject-many', { ids, reason })),
};

export interface IResolveManyResult {
  resolved: number;
  failed: { id: string; error: string }[];
}

// ---------------------------------------------------------------- Perfil propio

export const profileApi = {
  update: (payload: { firstName?: string; lastName?: string; email?: string }) => unwrap<IUser>(http.patch('/profile', payload)),
  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    http.post('/profile/password', payload).then(() => undefined),
  uploadAvatar: (file: Blob, filename: string) => {
    const formData = new FormData();
    formData.append('file', file, filename);
    return unwrap<IUser>(http.post('/profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } }));
  },
  removeAvatar: () => unwrap<IUser>(http.delete('/profile/avatar')),
  /** `IUser.avatarKey` es la key de storage (bucket privado): la foto la sirve el backend por esta ruta. 404 si no tiene. */
  getAvatar: () => http.get('/profile/avatar', { responseType: 'blob' }).then((r) => r.data as Blob),
};
